import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { api } from '../../services/api';
import toast from 'react-hot-toast';
import {
  SparklesIcon,
  PlusIcon,
  PlayIcon,
  StopIcon,
  ArrowPathIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  KeyIcon,
  EyeIcon,
  EyeSlashIcon,
  ComputerDesktopIcon,
  Cog6ToothIcon,
  ClipboardDocumentListIcon,
  XMarkIcon,
  PencilSquareIcon,
  ChartBarIcon,
  BoltIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowUpTrayIcon,
  PauseIcon,
  BeakerIcon,
  ClockIcon,
  ShieldExclamationIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';
import {
  AgentDesktopSetup,
  DesktopSetupPanel
} from '../../components/admin/AgentDesktopSetup';

function isLinkedScrapeSessionType(type) {
  return type === 'scrape_session' || type === 'scrape_v2_session';
}

function isScrapeV2PoolType(type) {
  return type === 'scrape_v2_pool';
}

function agentBrowserIsOrbex(agent) {
  return String(agent?.config?.browserEngine || '').toLowerCase() === 'orbex';
}

function gologinTokenBlocksStart(agent) {
  return !agentBrowserIsOrbex(agent) && !agent?.hasGologinApiToken;
}

function needsScrapeSessionPick(type) {
  return isLinkedScrapeSessionType(type);
}

/** Pool / scrape source stats (available · followed · claimed · total). */
function SourceAvailabilityStats({
  available,
  stored,
  followed,
  claimed,
  unclaimed,
  loading,
  loadingAvailable,
  loadingFollowed,
  loadingStored,
  loadingClaimed,
  showClaimed = false,
  reserveHint,
  followedTitle = 'Successfully followed by this model (all agents, all time)',
  poolMode = false,
  categoryFilter = null,
  claimedTitle = 'Reserved for dispatch by this model (claimed pool rows)',
  size = 'md',
}) {
  const numClass = size === 'xl' ? 'text-2xl' : (size === 'lg' ? 'text-xl' : 'text-lg');
  const fmt = (value, fieldLoading) => {
    const isLoading = fieldLoading ?? loading;
    if (isLoading && value == null) return '…';
    if (value == null || value < 0) return '—';
    return value.toLocaleString();
  };
  const availableDisplay = (() => {
    if (typeof available === 'number' && available >= 0) return available.toLocaleString();
    if (loadingAvailable) return '…';
    return '—';
  })();
  const categoryLabel = categoryFilter ? String(categoryFilter) : null;
  const followedLabel = categoryLabel
    ? 'followed in category'
    : (poolMode ? 'followed by model' : 'successfully followed');
  const followedHint = categoryLabel
    ? `Handles in “${categoryLabel}” already on this model’s ledger (dedup stays model-wide; follow-backs still scan all followed usernames)`
    : (poolMode
      ? 'Lifetime follows recorded for this model (all agents) — after pool migration, available + followed in pool ≤ total'
      : followedTitle);
  const totalLabel = categoryLabel
    ? 'total in category'
    : (poolMode ? 'total in database' : 'total in source');
  const gridClass = showClaimed
    ? 'grid grid-cols-2 sm:grid-cols-4 gap-2'
    : 'grid grid-cols-3 gap-2';
  return (
    <div>
      <div className={gridClass}>
        <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2.5 text-center">
          <div className={`${numClass} font-bold text-emerald-700 dark:text-emerald-300 tabular-nums leading-none`}>
            {availableDisplay}
          </div>
          <div className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400 mt-1">available to follow</div>
          {loadingAvailable && typeof unclaimed === 'number' && unclaimed > 0 && typeof available !== 'number' && (
            <div className="text-[9px] text-emerald-600/80 dark:text-emerald-400/80 mt-0.5 leading-tight">
              scanning dedup · {unclaimed.toLocaleString()} unclaimed in pool
            </div>
          )}
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-3 py-2.5 text-center">
          <div className={`${numClass} font-bold text-gray-600 dark:text-gray-300 tabular-nums leading-none`}>
            {fmt(followed, loadingFollowed)}
          </div>
          <div className="text-[10px] font-medium text-gray-500 mt-1" title={followedHint}>{followedLabel}</div>
        </div>
        {showClaimed && (
          <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-3 py-2.5 text-center">
            <div className={`${numClass} font-bold text-amber-700 dark:text-amber-300 tabular-nums leading-none`}>
              {fmt(claimed, loadingClaimed)}
            </div>
            <div className="text-[10px] font-medium text-amber-600 dark:text-amber-400 mt-1" title={claimedTitle}>reserved / claimed</div>
          </div>
        )}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-3 py-2.5 text-center">
          <div className={`${numClass} font-bold text-gray-600 dark:text-gray-300 tabular-nums leading-none`}>
            {fmt(stored, loadingStored)}
          </div>
          <div className="text-[10px] font-medium text-gray-500 mt-1">{totalLabel}</div>
        </div>
      </div>
      {reserveHint && (
        <p className="text-[10px] text-gray-500 mt-1.5 text-center">{reserveHint}</p>
      )}
      {categoryLabel && (
        <p className="text-[10px] text-indigo-600 dark:text-indigo-400 mt-1.5 text-center">
          Pool filtered to <strong>{categoryLabel}</strong> · follow-backs still use the full model ledger
        </p>
      )}
    </div>
  );
}

const SOURCE_AVAIL_POLL_MS = 2500;
const SOURCE_AVAIL_MAX_POLLS = 6;

function sourceAvailabilityBreakdownReady(data) {
  return data?.success && typeof data.available === 'number';
}

function formatPoolReserveHint(data) {
  const parts = [];
  const blocked = data?.blockedInSource;
  if (typeof blocked === 'number' && blocked > 0) {
    parts.push(`${blocked.toLocaleString()} already touched in pool (ledger)`);
  }
  if (data?.partial && typeof data?.available === 'number') {
    parts.push('partial scan — count still refining');
  }
  return parts.length ? parts.join(' · ') : null;
}

/** Staged fetch: quick headline stats immediately; availability loads in background. */
async function fetchTargetSourceAvailability(agentId, params, { signal, onProgress, blocking = false } = {}) {
  const isV2Pool = params.sourceType === 'scrape_v2_pool' && params.followModelId;
  let lastGood = null;
  let quickAvailableSet = false;

  const mergeProgress = (data, { fromQuick = false } = {}) => {
    if (!data?.success) return;
    if (
      isV2Pool
      && quickAvailableSet
      && !fromQuick
      && typeof lastGood?.available === 'number'
      && typeof data.available === 'number'
    ) {
      // Full path may return a stale cache entry. Keep quick available when the
      // full payload is partial/stale; allow a complete ledger scan to refine it.
      if (data.partial === true) {
        const { available: _ignored, ...rest } = data;
        lastGood = { ...lastGood, ...rest, available: lastGood.available };
        onProgress?.(lastGood);
        return;
      }
    }
    lastGood = { ...(lastGood || {}), ...data };
    if (fromQuick && typeof data.available === 'number') quickAvailableSet = true;
    onProgress?.(lastGood);
  };

  if (isV2Pool && !blocking) {
    try {
      const quick = await api.get(`/admin/va/ai-agents/${agentId}/target-source/available`, {
        params: { ...params, quick: '1' },
        signal,
      });
      mergeProgress(quick.data, { fromQuick: true });
      if (typeof quick.data?.storedInSource === 'number') {
        api.get(`/admin/va/ai-agents/${agentId}/target-source/available`, { params, signal })
          .then((res) => mergeProgress(res.data))
          .catch(() => { /* availability may time out — headline stats already shown */ });
        return lastGood;
      }
    } catch (err) {
      if (err.response?.status === 422 && err.response?.data?.needsScrapeOwner) {
        throw err;
      }
      /* fall through to blocking fetch */
    }
  }

  const maxPolls = blocking ? 24 : SOURCE_AVAIL_MAX_POLLS;

  for (let attempt = 0; attempt <= maxPolls; attempt += 1) {
    try {
      const res = await api.get(`/admin/va/ai-agents/${agentId}/target-source/available`, {
        params,
        signal,
      });
      if (res.data?.success) mergeProgress(res.data);
      if (sourceAvailabilityBreakdownReady(res.data)) return res.data;
      if (attempt < maxPolls) {
        await new Promise((resolve) => setTimeout(resolve, SOURCE_AVAIL_POLL_MS));
      }
    } catch (_) {
      if (attempt >= maxPolls) break;
      await new Promise((resolve) => setTimeout(resolve, SOURCE_AVAIL_POLL_MS));
    }
  }
  if (lastGood?.success && (
    typeof lastGood.storedInSource === 'number'
    || typeof lastGood.alreadyTouchedInSource === 'number'
    || typeof lastGood.available === 'number'
  )) {
    return lastGood;
  }
  return null;
}

const ADVANCED_SOURCE_TYPES = new Set(['scrape_v2_session', 'scrape_session', 'scrape_tag']);
function scrapeSessionOptionLabel(s) {
  const stored = typeof s.progress?.totalStored === 'number'
    ? ` · ${s.progress.totalStored.toLocaleString()} stored`
    : '';
  return `${s.name || s.sessionId}${s.status ? ` · ${s.status}` : ''}${s.tag ? ` · tag:${s.tag}` : ''}${stored}`;
}

// ── Test Mode Log Panel ─────────────────────────────────────────────────────
function TestModeLogPanel({ logs }) {
  const [tab, setTab] = useState('all');
  const scrollRef = React.useRef(null);
  const filtered = tab === 'all' ? logs : logs.filter((e) => e.cat === tab);

  // Auto-scroll to bottom whenever new entries arrive
  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [filtered.length]);
  const levelColor = (level) => {
    if (level === 'success') return 'text-emerald-400';
    if (level === 'warn') return 'text-amber-400';
    if (level === 'error') return 'text-red-400';
    return 'text-gray-300';
  };
  const catColor = (cat) => {
    if (cat === 'follow') return 'text-violet-400';
    if (cat === 'scanner') return 'text-sky-400';
    if (cat === 'chatbot') return 'text-emerald-400';
    return 'text-gray-400';
  };
  const tabs = [
    { id: 'all', label: 'All' },
    { id: 'follow', label: 'Follow' },
    { id: 'scanner', label: 'Scanner' },
    { id: 'chatbot', label: 'Chatbot' }
  ];
  return (
    <div className="mt-2 rounded-lg bg-gray-950/90 dark:bg-gray-950 border border-gray-800 overflow-hidden">
      <div className="flex items-center gap-0 border-b border-gray-800 px-2 pt-1">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-2.5 py-1 text-[10px] font-medium transition-colors border-b-2 -mb-px ${
              tab === t.id
                ? 'border-violet-500 text-violet-300'
                : 'border-transparent text-gray-500 hover:text-gray-300'
            }`}
          >
            {t.label}
            {t.id !== 'all' && (
              <span className="ml-1 text-gray-600">({logs.filter((e) => e.cat === t.id).length})</span>
            )}
          </button>
        ))}
      </div>
      <div ref={scrollRef} className="h-52 overflow-y-auto p-2 space-y-0.5 font-mono">
        {filtered.length === 0 ? (
          <p className="text-[10px] text-gray-600 italic py-4 text-center">
            No logs yet — send a follow to see activity
          </p>
        ) : (
          filtered.map((entry) => (
            <div key={entry.id} className="flex items-start gap-1.5 text-[10px] leading-relaxed">
              <span className="text-gray-600 flex-shrink-0 tabular-nums">
                {new Date(entry.ts).toLocaleTimeString('en-GB', { hour12: false })}
              </span>
              <span className={`flex-shrink-0 font-semibold uppercase ${catColor(entry.cat)}`}>
                [{entry.cat}]
              </span>
              <span className={levelColor(entry.level)}>{entry.msg}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const DEFAULT_CONFIG = {
  maxConcurrentBrowsers: 5,
  followsPerAccountPerBatch: 4, // legacy single value, kept so older agents still validate
  followsPerAccountMin: 4,
  followsPerAccountMax: 6,
  // Per-job per-profile total — runner loops fresh rounds (each round
  // does followsPerAccountMin..Max follows in a new browser launch)
  // until a randomly-assigned total in this range is reached.
  totalFollowsPerProfileMin: 40,
  totalFollowsPerProfileMax: 60,
  pageLoadDwellMinMs: 4000,
  pageLoadDwellMaxMs: 6000,
  delayBetweenFollowsMinMs: 9000,
  delayBetweenFollowsMaxMs: 12000,
  delayBetweenAccountsMinMs: 3000,
  delayBetweenAccountsMaxMs: 8000,
  followTimeoutMs: 30000,
  headed: true,
  warmupEnabled: true,
  warmupProfileMinMs: 15000,
  warmupProfileMaxMs: 20000,
  warmupHomeMinMs: 30000,
  warmupHomeMaxMs: 35000,
  warmupVisitNotifications: true,
  warmupVisitSettings: true,
  autoRemoveGologinOnSuspend: false
};

const DEFAULT_BOOTSTRAP = {
  pollIntervalMs: 5000,
  heartbeatIntervalMs: 10000
};

/** Matches backend computeJobTargetCapacity — accounts × totalFollowsPerProfileMax. */
function computeJobTargetCapacity(config, enabledAccountCount) {
  const accounts = Math.max(0, Number(enabledAccountCount) || 0);
  if (!accounts) return 1;
  const perRoundMid = (
    (Number(config?.followsPerAccountMin) || Number(config?.followsPerAccountPerBatch) || 4)
    + (Number(config?.followsPerAccountMax) || Number(config?.followsPerAccountPerBatch) || 6)
  ) / 2;
  const perProfileMax = Math.max(
    1,
    Number(config?.totalFollowsPerProfileMax)
      || Number(config?.totalFollowsPerProfileMin)
      || perRoundMid
  );
  return Math.max(1, accounts * perProfileMax);
}

/**
 * Parse a free-form username list (newline-, comma-, or space-separated)
 * into a deduplicated list of lowercase X handles. Strips leading @ and any
 * non-handle characters so operators can paste from spreadsheets/screenshots.
 */
function parseUsernameList(text) {
  if (!text) return [];
  const seen = new Set();
  const out = [];
  for (const raw of String(text).split(/[\s,;\n\r\t]+/)) {
    const cleaned = raw.trim().replace(/^@/, '').toLowerCase();
    // X usernames: 1-15 chars, alphanumeric + underscore.
    if (!/^[a-z0-9_]{1,15}$/.test(cleaned)) continue;
    if (seen.has(cleaned)) continue;
    seen.add(cleaned);
    out.push(cleaned);
  }
  return out;
}

function StatusPill({ online }) {
  return online ? (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
      Desktop online
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
      <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
      Desktop offline
    </span>
  );
}

function RunStatusPill({ status }) {
  const map = {
    idle: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    running: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200',
    pausing: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200 border border-amber-200 dark:border-amber-800',
    paused: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
    error: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
  };
  const label = status === 'pausing' ? 'pausing…' : status;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || map.idle}`}>
      {label}
    </span>
  );
}

const VAAgentsSection = ({ vaUsers = [], initialAgentId = null }) => {
  const [agents, setAgents] = useState([]);
  /**
   * `initialLoading` only flips for the very first fetch, so background
   * polling never re-renders the empty-state spinner (which would unmount
   * any open modal mid-edit).
   */
  const [initialLoading, setInitialLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  /** After creating (or duplicating) an agent, show the desktop pairing wizard. */
  const [postCreateSetup, setPostCreateSetup] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [openWarmupStartFor, setOpenWarmupStartFor] = useState(null);
  const [agentDetail, setAgentDetail] = useState(null);

  const fetchAgents = useCallback(async ({ silent = false } = {}) => {
    try {
      const res = await api.get('/admin/va/ai-agents');
      if (res.data.success) setAgents(res.data.agents || []);
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.error || 'Failed to load AI agents');
    }
  }, []);

  const fetchDetail = useCallback(async (agentId, { silent = false } = {}) => {
    if (!agentId) return;
    try {
      const res = await api.get(`/admin/va/ai-agents/${agentId}`, {
        params: silent ? { light: 1 } : undefined
      });
      if (!res.data.success) return;
      const next = res.data.agent;
      setAgentDetail((prev) => {
        // Light polls omit accounts/events — keep the hydrated fields from
        // the full fetch so the Setup tab doesn't flash empty.
        if (
          silent
          && res.data.light
          && prev
          && String(prev._id) === String(next._id)
        ) {
          return {
            ...prev,
            ...next,
            accounts: prev.accounts,
            recentEvents: prev.recentEvents,
            accountCount: next.accountCount ?? prev.accountCount
          };
        }
        return next;
      });
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.error || 'Failed to load agent detail');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await fetchAgents();
      if (!cancelled) setInitialLoading(false);
    })();
    return () => { cancelled = true; };
  }, [fetchAgents]);

  useEffect(() => {
    if (initialAgentId) setSelectedAgentId(String(initialAgentId));
  }, [initialAgentId]);

  useEffect(() => {
    if (!selectedAgentId) {
      setAgentDetail(null);
      return undefined;
    }
    fetchDetail(selectedAgentId);
    // Pause detail polling while the create modal is open so the form state
    // never competes with re-renders triggered by the poll.
    if (showCreate) return undefined;
    const id = window.setInterval(() => fetchDetail(selectedAgentId, { silent: true }), 5000);
    return () => window.clearInterval(id);
  }, [selectedAgentId, fetchDetail, showCreate]);

  // Light refresh of list status while UI is open. Skipped while the create
  // modal is open so opening the form doesn't get re-rendered out from under
  // the operator if no agents exist yet.
  useEffect(() => {
    if (showCreate) return undefined;
    const id = window.setInterval(() => {
      fetchAgents({ silent: true });
    }, 15000);
    return () => window.clearInterval(id);
  }, [fetchAgents, showCreate]);

  const handleCreate = async (payload) => {
    try {
      const res = await api.post('/admin/va/ai-agents', payload);
      if (res.data.success) {
        const agent = res.data.agent;
        setShowCreate(false);
        setPostCreateSetup({
          agent,
          token: agent.desktopAgentToken,
          pairingId: agent.desktopPairings?.[0]?._id,
          headline: 'VA Agent created — pair a desktop'
        });
        await fetchAgents();
        setSelectedAgentId(String(agent._id));
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create agent');
    }
  };

  const handleDuplicate = async (agent) => {
    if (!window.confirm(`Duplicate "${agent.name}"? The copy starts with the same config and no accounts attached.`)) return;
    try {
      const res = await api.post(`/admin/va/ai-agents/${agent._id}/duplicate`);
      if (res.data.success) {
        const copy = res.data.agent;
        setPostCreateSetup({
          agent: copy,
          token: copy.desktopAgentToken,
          pairingId: copy.desktopPairings?.[0]?._id,
          headline: 'Agent duplicated — pair another desktop'
        });
        await fetchAgents();
        setSelectedAgentId(String(copy._id));
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to duplicate');
    }
  };

  const handleDelete = async (agent) => {
    if (!window.confirm(`Delete agent "${agent.name}"? Pending jobs will be cancelled.`)) return;
    try {
      await api.delete(`/admin/va/ai-agents/${agent._id}`);
      toast.success('Agent deleted');
      if (String(selectedAgentId) === String(agent._id)) setSelectedAgentId(null);
      await fetchAgents();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete');
    }
  };

  const handleStart = async (agent) => {
    if (agent.automationType === 'warmup') {
      setSelectedAgentId(String(agent._id));
      setOpenWarmupStartFor(String(agent._id));
      await fetchDetail(agent._id);
      return;
    }
    try {
      const res = await api.post(`/admin/va/ai-agents/${agent._id}/start`, {
        useTargetPool: true
      });
      if (res.data.success) {
        toast.success('Job dispatched to desktop agent');
        await fetchAgents();
        await fetchDetail(agent._id);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start');
    }
  };

  const handleStop = async (agent) => {
    try {
      const endpoint = agent.automationType === 'warmup'
        ? `/admin/va/ai-agents/${agent._id}/warmup/stop`
        : `/admin/va/ai-agents/${agent._id}/stop`;
      await api.post(endpoint, {});
      toast.success(agent.automationType === 'warmup' ? 'Warmup stopped' : 'Stop signal sent');
      await fetchAgents();
      await fetchDetail(agent._id);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to stop');
    }
  };

  if (initialLoading && !agents.length) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading VA Agents…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
              <SparklesIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">VA Agents</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-2xl">
                Create AI Agents that run on a paired desktop running GoLogin. Each agent connects to one
                or more accounts and runs follow jobs with the delays and concurrency you set here.
                Duplicate an agent to spin up another one for a different desktop or account batch.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={fetchAgents}
              className="inline-flex items-center gap-2 btn-secondary text-sm"
            >
              <ArrowPathIcon className="w-4 h-4" />
              Refresh
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 btn-primary text-sm"
            >
              <PlusIcon className="w-4 h-4" />
              New VA Agent
            </button>
          </div>
        </div>
      </div>

      {/* Empty state */}
      {!agents.length && !initialLoading && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-gray-300 dark:border-gray-600 p-10 text-center">
          <SparklesIcon className="w-12 h-12 mx-auto text-indigo-400" />
          <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">No VA Agents yet</h3>
          <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
            Create your first VA Agent, copy the pairing token to your desktop running GoLogin, start the
            <code className="mx-1 px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-xs">wingr-agent</code>
            worker, then attach accounts and press Start.
          </p>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="mt-6 inline-flex items-center gap-2 btn-primary"
          >
            <PlusIcon className="w-4 h-4" /> Create VA Agent
          </button>
        </div>
      )}

      {/* Agents list */}
      {agents.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
          {agents.map((agent, idx) => {
            const selected = String(selectedAgentId) === String(agent._id);
            const isWarmup = agent.automationType === 'warmup';
            return (
              <div
                key={agent._id}
                className={`flex flex-wrap items-center gap-3 px-5 py-3.5 cursor-pointer transition-colors
                  ${idx < agents.length - 1 ? 'border-b border-gray-100 dark:border-gray-700' : ''}
                  ${selected ? 'bg-indigo-50/60 dark:bg-indigo-900/20 border-l-2 border-l-indigo-500' : 'hover:bg-gray-50 dark:hover:bg-gray-900/30'}
                `}
                onClick={() => setSelectedAgentId(String(agent._id))}
              >
                {/* Name + pills */}
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${agent.online ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'}`} title={agent.online ? 'Desktop online' : 'Desktop offline'} />
                  <span className="font-semibold text-gray-900 dark:text-white truncate">{agent.name}</span>
                  {isWarmup ? (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 flex-shrink-0">Warmup</span>
                  ) : (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 flex-shrink-0">Follow</span>
                  )}
                  <AgentModuleChips agent={agent} />
                  <RunStatusPill status={agent.status} />
                  <StatusPill online={agent.online} />
                  {gologinTokenBlocksStart(agent) && (
                    <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 flex-shrink-0">
                      Needs GoLogin token
                    </span>
                  )}
                </div>

                {/* Quick stats */}
                <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 tabular-nums flex-shrink-0">
                  <span><span className="font-semibold text-gray-900 dark:text-white">{agent.accountCount || 0}</span> accts</span>
                  <span><span className="font-semibold text-gray-900 dark:text-white">{agent.config?.maxConcurrentBrowsers || 0}</span> concurr</span>
                  {(agent.pendingJobs || 0) > 0 && (
                    <span><span className="font-semibold text-indigo-600 dark:text-indigo-400">{agent.pendingJobs}</span> pending</span>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    onClick={() => handleStart(agent)}
                    disabled={
                      !agent.accountCount
                      || (agent.automationType === 'warmup'
                        ? agent.status === 'running'
                        : (agent.status === 'running' || agent.status === 'pausing' || agent.status === 'paused'))
                      || gologinTokenBlocksStart(agent)
                    }
                    className="inline-flex items-center gap-1 btn-primary !py-1.5 !px-2.5 text-xs disabled:opacity-40"
                    title={
                      agent.status === 'paused'
                        ? 'Open the agent and click Resume to continue the paused run'
                        : agent.status === 'pausing'
                          ? 'Pause in progress — wait for browsers to close'
                          : (gologinTokenBlocksStart(agent) ? 'Set GoLogin API token first' : '')
                    }
                  >
                    <PlayIcon className="w-3 h-3" /> Start
                  </button>
                  <button
                    type="button"
                    onClick={() => handleStop(agent)}
                    disabled={agent.status !== 'running' && agent.status !== 'pausing' && agent.status !== 'paused' && !agent.pendingJobs}
                    className="inline-flex items-center gap-1 btn-secondary !py-1.5 !px-2.5 text-xs disabled:opacity-40"
                  >
                    <StopIcon className="w-3 h-3" /> Stop
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDuplicate(agent)}
                    className="inline-flex items-center gap-1 btn-secondary !py-1.5 !px-2.5 text-xs"
                  >
                    <DocumentDuplicateIcon className="w-3 h-3" /> Dupe
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(agent)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 dark:text-red-400 px-2 py-1.5"
                  >
                    <TrashIcon className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail panel */}
      {selectedAgentId && (
        <AgentDetailPanel
          agent={agentDetail && String(agentDetail._id) === String(selectedAgentId) ? agentDetail : null}
          vaUsers={vaUsers}
          onClose={() => setSelectedAgentId(null)}
          openWarmupStartFor={openWarmupStartFor}
          onWarmupStartHandled={() => setOpenWarmupStartFor(null)}
          onChanged={async () => {
            await Promise.all([fetchAgents({ silent: true }), fetchDetail(selectedAgentId)]);
          }}
        />
      )}

      {showCreate && (
        <CreateAgentModal
          onClose={() => setShowCreate(false)}
          onCreate={handleCreate}
        />
      )}

      {postCreateSetup && (
        <DesktopSetupModal
          agent={postCreateSetup.agent}
          token={postCreateSetup.token}
          pairingId={postCreateSetup.pairingId}
          headline={postCreateSetup.headline}
          onClose={() => setPostCreateSetup(null)}
        />
      )}
    </div>
  );
};

export default VAAgentsSection;

// DesktopSetupPanel + buildInstallCommands live in
// frontend/src/components/admin/AgentDesktopSetup.js

function DesktopSetupModal({ agent, token, pairingId, headline, onClose }) {
  const copy = (text) => {
    if (!text) return;
    navigator.clipboard?.writeText(text).then(
      () => toast.success('Copied'),
      () => toast.error('Copy failed')
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl my-8 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-950/40 dark:to-violet-950/30">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">{headline || 'Pair a desktop'}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Agent <span className="font-medium">{agent?.name}</span> — new token added; all existing tokens still work
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5">
          <DesktopSetupPanel
            agent={agent}
            initialPairingId={pairingId}
            initialToken={token}
            onCopy={copy}
          />
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
          <button type="button" onClick={onClose} className="btn-primary text-sm">
            Done — open agent Setup anytime to add more desktops
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Create modal
// ────────────────────────────────────────────────────────────────────

function CreateAgentModal({ onClose, onCreate }) {
  const [name, setName] = useState('VA Agent 1');
  const [description, setDescription] = useState('');
  const [gologinApiToken, setGologinApiToken] = useState('');
  const [showGologinToken, setShowGologinToken] = useState(false);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [bootstrap, setBootstrap] = useState(DEFAULT_BOOTSTRAP);
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      await onCreate({
        name: name.trim(),
        description: description.trim(),
        gologinApiToken: gologinApiToken.trim(),
        config,
        bootstrap
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
      <form onSubmit={submit} className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white">New VA Agent</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="form-input w-full"
              placeholder="VA Agent 1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description (optional)</label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="form-textarea w-full text-sm"
              placeholder="e.g. Runs on Mark's Mac mini"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1.5">
              <KeyIcon className="w-4 h-4" /> GoLogin API token
            </label>
            <div className="flex gap-2">
              <input
                type={showGologinToken ? 'text' : 'password'}
                autoComplete="off"
                value={gologinApiToken}
                onChange={(e) => setGologinApiToken(e.target.value)}
                className="form-input flex-1 font-mono text-xs"
                placeholder="eyJ… (Personal Area → API Token in GoLogin)"
              />
              <button
                type="button"
                onClick={() => setShowGologinToken((v) => !v)}
                className="btn-secondary !px-3"
                title={showGologinToken ? 'Hide' : 'Show'}
              >
                {showGologinToken ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Stored encrypted on the server and pushed to the desktop worker per job — never written to disk on the Mac.
            </p>
          </div>

          <ConfigEditor value={config} onChange={setConfig} />

          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="text-xs font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-300"
          >
            {showAdvanced ? 'Hide' : 'Show'} desktop worker timings
          </button>
          {showAdvanced && (
            <BootstrapEditor value={bootstrap} onChange={setBootstrap} />
          )}
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-gray-200 dark:border-gray-700">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? 'Creating…' : 'Create agent'}
          </button>
        </div>
      </form>
    </div>
  );
}

function BootstrapEditor({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Desktop worker timings</h4>
      <p className="text-xs text-gray-500 -mt-1">
        Live-pushed to the paired desktop on every heartbeat. No restart required.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <NumberField label="Poll interval (ms)" value={value.pollIntervalMs} min={1000} max={600000}
          onChange={(v) => set('pollIntervalMs', v)} hint="How often the desktop asks for new jobs" />
        <NumberField label="Heartbeat interval (ms)" value={value.heartbeatIntervalMs} min={2000} max={600000}
          onChange={(v) => set('heartbeatIntervalMs', v)} hint="How often the desktop pings to stay online" />
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Config editor (reused in create + detail)
// ────────────────────────────────────────────────────────────────────

function ConfigEditor({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
        <Cog6ToothIcon className="w-4 h-4" /> Follow configuration
      </h4>
      <div className="grid grid-cols-2 gap-3">
        <NumberField label="Concurrent browsers" value={value.maxConcurrentBrowsers} min={1} max={1000}
          onChange={(v) => set('maxConcurrentBrowsers', v)} hint="GoLogin profiles open at once — no hard cap, limited by your VPS CPU/RAM" />
        <NumberField label="Follows per profile per round (min)" value={value.followsPerAccountMin ?? value.followsPerAccountPerBatch} min={1} max={40}
          onChange={(v) => set('followsPerAccountMin', v)} hint="One round = open browser, follow N, close. Random per round." />
        <NumberField label="Follows per profile per round (max)" value={value.followsPerAccountMax ?? value.followsPerAccountPerBatch} min={1} max={40}
          onChange={(v) => set('followsPerAccountMax', v)} />
        <NumberField label="Total follows per profile (min)" value={value.totalFollowsPerProfileMin ?? value.followsPerAccountMin} min={1} max={5000}
          onChange={(v) => set('totalFollowsPerProfileMin', v)} hint="Runner loops rounds on each profile until this random total is hit." />
        <NumberField label="Total follows per profile (max)" value={value.totalFollowsPerProfileMax ?? value.followsPerAccountMax} min={1} max={5000}
          onChange={(v) => set('totalFollowsPerProfileMax', v)} />
        <NumberField label="Page-load dwell min (ms)" value={value.pageLoadDwellMinMs} min={0} max={60000}
          onChange={(v) => set('pageLoadDwellMinMs', v)} hint="Read profile before clicking Follow" />
        <NumberField label="Page-load dwell max (ms)" value={value.pageLoadDwellMaxMs} min={0} max={60000}
          onChange={(v) => set('pageLoadDwellMaxMs', v)} />
        <NumberField label="Follow timeout (ms)" value={value.followTimeoutMs} min={5000} max={300000}
          onChange={(v) => set('followTimeoutMs', v)} />
        <NumberField label="Delay follows min (ms)" value={value.delayBetweenFollowsMinMs} min={500} max={600000}
          onChange={(v) => set('delayBetweenFollowsMinMs', v)} />
        <NumberField label="Delay follows max (ms)" value={value.delayBetweenFollowsMaxMs} min={500} max={600000}
          onChange={(v) => set('delayBetweenFollowsMaxMs', v)} />
        <NumberField label="Delay accounts min (ms)" value={value.delayBetweenAccountsMinMs} min={0} max={600000}
          onChange={(v) => set('delayBetweenAccountsMinMs', v)} />
        <NumberField label="Delay accounts max (ms)" value={value.delayBetweenAccountsMaxMs} min={0} max={600000}
          onChange={(v) => set('delayBetweenAccountsMaxMs', v)} />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!value.headed} onChange={(e) => set('headed', e.target.checked)} className="rounded" />
        <span>Show browser window on desktop (recommended)</span>
      </label>

      <div className="pt-2 mt-2 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">Human warm-up</h5>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={value.warmupEnabled !== false}
              onChange={(e) => set('warmupEnabled', e.target.checked)}
              className="rounded"
            />
            <span>Enabled</span>
          </label>
        </div>
        <p className="text-[11px] text-gray-500 mb-2">
          Before any follows, the worker views the account&apos;s own profile, then scrolls Home for a while —
          dwelling on each post like a person reading. Reduces shadow-ban risk vs. mass-follow patterns.
        </p>
        <div className={`grid grid-cols-2 gap-3 ${value.warmupEnabled === false ? 'opacity-50 pointer-events-none' : ''}`}>
          <label className="flex items-center gap-2 text-xs col-span-2 sm:col-span-1">
            <input
              type="checkbox"
              checked={value.warmupVisitNotifications !== false}
              onChange={(e) => set('warmupVisitNotifications', e.target.checked)}
              className="rounded"
            />
            <span>Visit /notifications</span>
          </label>
          <label className="flex items-center gap-2 text-xs col-span-2 sm:col-span-1">
            <input
              type="checkbox"
              checked={value.warmupVisitSettings !== false}
              onChange={(e) => set('warmupVisitSettings', e.target.checked)}
              className="rounded"
            />
            <span>Visit /settings/account</span>
          </label>
          <NumberField label="Profile dwell min (ms)" value={value.warmupProfileMinMs} min={0} max={600000}
            onChange={(v) => set('warmupProfileMinMs', v)} hint="Time on own profile" />
          <NumberField label="Profile dwell max (ms)" value={value.warmupProfileMaxMs} min={0} max={600000}
            onChange={(v) => set('warmupProfileMaxMs', v)} />
          <NumberField label="Home scroll min (ms)" value={value.warmupHomeMinMs} min={0} max={600000}
            onChange={(v) => set('warmupHomeMinMs', v)} hint="Time on Home feed" />
          <NumberField label="Home scroll max (ms)" value={value.warmupHomeMaxMs} min={0} max={600000}
            onChange={(v) => set('warmupHomeMaxMs', v)} />
        </div>
      </div>
    </div>
  );
}

function NumberField({ label, value, min, max, onChange, hint }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{label}</label>
      <input
        type="number"
        min={min}
        max={max}
        value={value ?? ''}
        onChange={(e) => onChange(Number(e.target.value))}
        className="form-input w-full text-sm"
      />
      {hint && <p className="mt-0.5 text-[11px] text-gray-400">{hint}</p>}
    </div>
  );
}

/**
 * Editable per-job config fields — still used by the Edit / continuation
 * modal where one-off overrides make sense without touching agent Setup.
 */
function RunJobConfigFields({ config, onChange }) {
  if (!config) return null;
  const set = (key, value) => onChange({ ...config, [key]: value });
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      <NumberField label="Concurrent browsers" value={config.maxConcurrentBrowsers} min={1} max={1000}
        onChange={(v) => set('maxConcurrentBrowsers', v)} />
      <NumberField label="Follows / round (min)" value={config.followsPerAccountMin} min={1} max={40}
        onChange={(v) => set('followsPerAccountMin', v)} />
      <NumberField label="Follows / round (max)" value={config.followsPerAccountMax} min={1} max={40}
        onChange={(v) => set('followsPerAccountMax', v)} />
      <NumberField label="Total / profile (min)" value={config.totalFollowsPerProfileMin} min={1} max={5000}
        onChange={(v) => set('totalFollowsPerProfileMin', v)} />
      <NumberField label="Total / profile (max)" value={config.totalFollowsPerProfileMax} min={1} max={5000}
        onChange={(v) => set('totalFollowsPerProfileMax', v)} />
      <NumberField label="Delay follows min (ms)" value={config.delayBetweenFollowsMinMs} min={500} max={600000}
        onChange={(v) => set('delayBetweenFollowsMinMs', v)} />
      <NumberField label="Delay follows max (ms)" value={config.delayBetweenFollowsMaxMs} min={500} max={600000}
        onChange={(v) => set('delayBetweenFollowsMaxMs', v)} />
      <NumberField label="Delay accounts min (ms)" value={config.delayBetweenAccountsMinMs} min={0} max={600000}
        onChange={(v) => set('delayBetweenAccountsMinMs', v)} />
      <NumberField label="Delay accounts max (ms)" value={config.delayBetweenAccountsMaxMs} min={0} max={600000}
        onChange={(v) => set('delayBetweenAccountsMaxMs', v)} />
      <div className="col-span-2 sm:col-span-3 pt-1 border-t border-gray-200 dark:border-gray-700">
        <label className="inline-flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={Boolean(config.autoRemoveGologinOnSuspend)}
            onChange={(e) => set('autoRemoveGologinOnSuspend', e.target.checked)}
          />
          <span>
            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
              Auto-remove from GoLogin when suspended
            </span>
            <span className="block text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
              Deletes the GoLogin browser profile when X flags an attached account as suspended.
              The Wingr account row stays (soft) so follower stats history is preserved.
            </span>
          </span>
        </label>
      </div>
    </div>
  );
}

/**
 * Read-only summary of the agent's saved follow config. Operators tweak
 * the underlying numbers in the Setup tab; this card just lets them see
 * what the next /start will dispatch without having to flip tabs.
 */
function RunJobConfigSummary({ config, onEdit }) {
  if (!config) return null;
  const rows = [
    {
      label: 'Concurrent browsers',
      value: config.maxConcurrentBrowsers ?? '—',
      hint: 'parallel GoLogin profiles'
    },
    {
      label: 'Follows per round',
      value: `${config.followsPerAccountMin ?? '—'} – ${config.followsPerAccountMax ?? '—'}`,
      hint: 'one round = one browser launch per account'
    },
    {
      label: 'Total per profile',
      value: `${config.totalFollowsPerProfileMin ?? '—'} – ${config.totalFollowsPerProfileMax ?? '—'}`,
      hint: 'daily ceiling per account — random target between min/max each job'
    },
    {
      label: 'Delay between follows',
      value: `${Math.round((config.delayBetweenFollowsMinMs || 0) / 1000)}–${Math.round((config.delayBetweenFollowsMaxMs || 0) / 1000)}s`,
      hint: 'inside one round'
    },
    {
      label: 'Delay between accounts',
      value: `${Math.round((config.delayBetweenAccountsMinMs || 0) / 1000)}–${Math.round((config.delayBetweenAccountsMaxMs || 0) / 1000)}s`,
      hint: 'staggers account launches'
    },
    {
      label: 'GoLogin on suspend',
      value: config.autoRemoveGologinOnSuspend ? 'Remove profile' : 'Keep profile',
      hint: 'auto-delete GoLogin profile when X suspends an account'
    }
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {rows.map((row) => (
        <div key={row.label} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400">{row.label}</div>
          <div className="mt-0.5 text-sm font-semibold text-gray-900 dark:text-white tabular-nums">{row.value}</div>
          {row.hint && <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{row.hint}</div>}
        </div>
      ))}
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="rounded-lg border border-dashed border-indigo-300 dark:border-indigo-700 bg-indigo-50/40 dark:bg-indigo-950/20 px-3 py-2 text-left hover:bg-indigo-100/60 dark:hover:bg-indigo-950/40 transition-colors group"
        >
          <div className="text-[10px] uppercase tracking-wide font-medium text-indigo-600 dark:text-indigo-300 flex items-center gap-1">
            <Cog6ToothIcon className="w-3 h-3" /> Edit in Setup
          </div>
          <div className="mt-0.5 text-sm font-semibold text-indigo-700 dark:text-indigo-200 group-hover:underline">
            Change config →
          </div>
          <div className="text-[10px] text-indigo-500/80 dark:text-indigo-400/80 mt-0.5">
            applies to all future runs
          </div>
        </button>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Modules — live add/remove without stopping the agent
// ────────────────────────────────────────────────────────────────────

const MODULE_UI = [
  {
    id: 'follow',
    label: 'Follow',
    blurb: 'Send follows from your pool',
    ready: true,
    color: 'emerald'
  },
  {
    id: 'warmup',
    label: 'Warmup',
    blurb: 'Browse / scroll warm-up (or pre-follow session)',
    ready: true,
    color: 'amber',
    extra: 'warmupLikes'
  },
  {
    id: 'post',
    label: 'Post',
    blurb: 'Auto Posts from the follow model via GoLogin',
    ready: true,
    color: 'sky',
    extra: 'postsPerDay'
  },
  {
    id: 'unfollow',
    label: 'Unfollow',
    blurb: 'Drop non-followbacks after X days (GoLogin)',
    ready: true,
    color: 'rose',
    extra: 'unfollow'
  },
  {
    id: 'probe',
    label: 'Probe',
    blurb: 'Mutual / notification integrity check',
    ready: false,
    color: 'violet'
  }
];

function resolveModulesLocal(agent) {
  const m = agent?.modules || {};
  const isWarmup = agent?.automationType === 'warmup';
  return {
    follow: { enabled: m.follow?.enabled ?? !isWarmup, ...m.follow },
    warmup: {
      enabled: m.warmup?.enabled ?? true,
      likesEnabled: m.warmup?.likesEnabled ?? false,
      likesPerDayMin: m.warmup?.likesPerDayMin ?? 1,
      likesPerDayMax: m.warmup?.likesPerDayMax ?? 3,
      ...m.warmup
    },
    post: { enabled: m.post?.enabled ?? false, postsPerDay: m.post?.postsPerDay ?? 1, ...m.post },
    unfollow: {
      enabled: m.unfollow?.enabled ?? false,
      days: m.unfollow?.days ?? 7,
      unfollowsPerDayMin: m.unfollow?.unfollowsPerDayMin ?? 5,
      unfollowsPerDayMax: m.unfollow?.unfollowsPerDayMax ?? 15,
      ...m.unfollow
    },
    probe: { enabled: m.probe?.enabled ?? false, ...m.probe }
  };
}

function AgentModuleChips({ agent }) {
  const mods = resolveModulesLocal(agent);
  const on = MODULE_UI.filter((x) => mods[x.id]?.enabled);
  if (!on.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 flex-shrink-0">
      {on.map((x) => (
        <span
          key={x.id}
          className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-700 dark:bg-slate-700/60 dark:text-slate-200"
          title={x.blurb}
        >
          {x.label}
        </span>
      ))}
    </div>
  );
}

function AgentModulesPanel({ agent, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [local, setLocal] = useState(() => resolveModulesLocal(agent));
  const busyRef = useRef(null);
  const localRef = useRef(local);
  localRef.current = local;
  const isRunning = agent?.status === 'running' || agent?.status === 'pausing' || agent?.status === 'paused';

  useEffect(() => {
    // Don't stomp optimistic toggles with a stale poll while a PATCH is in flight.
    if (busyRef.current) return;
    setLocal(resolveModulesLocal(agent));
  }, [agent?._id, agent?.modules, agent?.automationType, agent?.updatedAt]);

  const patchModule = async (id, patch) => {
    setBusy(id);
    busyRef.current = id;
    const prev = localRef.current;
    const next = {
      ...prev,
      [id]: { ...prev[id], ...patch }
    };
    setLocal(next);
    try {
      const res = await api.patch(`/admin/va/ai-agents/${agent._id}/modules`, {
        modules: next
      });
      if (res.data?.success) {
        if (res.data.modules) {
          setLocal(resolveModulesLocal({ ...agent, modules: res.data.modules }));
        } else if (res.data.agent?.modules) {
          setLocal(resolveModulesLocal(res.data.agent));
        }
        toast.success(
          isRunning
            ? 'Modules updated live — next account turn uses the new set'
            : 'Modules saved'
        );
        // Refresh parent after a tick so our local state isn't overwritten by a
        // response that raced ahead of the write.
        window.setTimeout(() => onChanged?.(), 0);
      } else {
        setLocal(prev);
        toast.error(res.data?.error || 'Could not update modules');
      }
    } catch (err) {
      setLocal(prev);
      toast.error(err.response?.data?.error || err.message || 'Could not update modules');
    } finally {
      busyRef.current = null;
      setBusy(null);
    }
  };

  const saveUnfollowSettings = () => {
    const u = localRef.current.unfollow || {};
    const unfollowsPerDayMin = Math.max(1, Math.min(100, Number(u.unfollowsPerDayMin) || 5));
    const unfollowsPerDayMax = Math.max(
      unfollowsPerDayMin,
      Math.max(1, Math.min(100, Number(u.unfollowsPerDayMax) || 15))
    );
    const days = Math.max(1, Math.min(90, Number(u.days) || 7));
    return patchModule('unfollow', {
      enabled: true,
      days,
      unfollowsPerDayMin,
      unfollowsPerDayMax
    });
  };

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Squares2X2Icon className="w-4 h-4 text-indigo-500" />
            Automation modules
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 max-w-xl">
            Toggle modules anytime — no need to stop. One browser open per account (session lease).
            Changes apply on the next account turn. Post uses texts/images from the follow model&apos;s Auto Posts
            on a random daily schedule (not when follow browsers open).
          </p>
        </div>
        {isRunning && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Live edit OK
          </span>
        )}
      </div>
      <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {MODULE_UI.map((mod) => {
          const enabled = Boolean(local[mod.id]?.enabled);
          const saving = busy === mod.id;
          return (
            <div
              key={mod.id}
              className={`rounded-xl border px-3 py-2.5 transition-colors ${
                enabled
                  ? 'border-indigo-300 dark:border-indigo-600 bg-indigo-50/50 dark:bg-indigo-950/30'
                  : 'border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{mod.label}</span>
                    {!mod.ready && (
                      <span className="text-[9px] uppercase tracking-wide font-medium text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 rounded">
                        Soon
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">{mod.blurb}</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={enabled}
                  disabled={!!busy}
                  onClick={() => patchModule(mod.id, { enabled: !enabled })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 disabled:opacity-50 ${
                    enabled ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                  title={enabled ? `Disable ${mod.label}` : `Enable ${mod.label}`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                      enabled ? 'translate-x-5' : 'translate-x-0'
                    } ${saving ? 'opacity-70' : ''}`}
                  />
                </button>
              </div>
              {mod.extra === 'warmupLikes' && enabled && (
                <div className="mt-2 space-y-1.5">
                  <label className="flex items-start gap-2 text-[11px] text-gray-600 dark:text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                      checked={Boolean(local.warmup?.likesEnabled)}
                      disabled={!!busy}
                      onChange={(e) => patchModule('warmup', {
                        enabled: true,
                        likesEnabled: e.target.checked,
                        likesPerDayMin: local.warmup?.likesPerDayMin ?? 1,
                        likesPerDayMax: local.warmup?.likesPerDayMax ?? 3
                      })}
                    />
                    <span>
                      Like 2–3 posts / day on For You
                      <span className="block text-[10px] text-gray-400 mt-0.5">
                        Once per account per day during a pre-follow session; that session scrolls a bit longer.
                      </span>
                    </span>
                  </label>
                </div>
              )}
              {mod.extra === 'unfollow' && enabled && (
                <div className="mt-2 space-y-1.5">
                  <label className="flex items-center gap-2 text-[11px] text-gray-600 dark:text-gray-300">
                    After
                    <input
                      type="number"
                      min={1}
                      max={90}
                      value={local.unfollow?.days ?? 7}
                      disabled={!!busy}
                      onChange={(e) => {
                        const days = Math.max(1, Math.min(90, parseInt(e.target.value, 10) || 7));
                        setLocal((s) => ({ ...s, unfollow: { ...s.unfollow, days } }));
                      }}
                      onBlur={() => { saveUnfollowSettings(); }}
                      className="w-14 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-1.5 py-0.5 text-xs"
                    />
                    days no follow-back
                  </label>
                  <label className="flex items-center gap-2 text-[11px] text-gray-600 dark:text-gray-300 flex-wrap">
                    Unfollows / day
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={local.unfollow?.unfollowsPerDayMin ?? 5}
                      disabled={!!busy}
                      onChange={(e) => {
                        const unfollowsPerDayMin = Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 5));
                        setLocal((s) => ({ ...s, unfollow: { ...s.unfollow, unfollowsPerDayMin } }));
                      }}
                      onBlur={() => { saveUnfollowSettings(); }}
                      className="w-14 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-1.5 py-0.5 text-xs"
                    />
                    –
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={local.unfollow?.unfollowsPerDayMax ?? 15}
                      disabled={!!busy}
                      onChange={(e) => {
                        const unfollowsPerDayMax = Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 15));
                        setLocal((s) => ({ ...s, unfollow: { ...s.unfollow, unfollowsPerDayMax } }));
                      }}
                      onBlur={() => { saveUnfollowSettings(); }}
                      className="w-14 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-1.5 py-0.5 text-xs"
                    />
                  </label>
                  <p className="text-[10px] text-gray-400">
                    Warmup first · reclaim to Available for other accounts (max 3 follows / model)
                  </p>
                </div>
              )}
              {mod.extra === 'postsPerDay' && enabled && (
                <label className="mt-2 flex items-center gap-2 text-[11px] text-gray-600 dark:text-gray-300">
                  Posts / day
                  <input
                    type="number"
                    min={1}
                    max={5}
                    value={local.post?.postsPerDay ?? 1}
                    disabled={!!busy}
                    onChange={(e) => {
                      const postsPerDay = Math.max(1, Math.min(5, parseInt(e.target.value, 10) || 1));
                      setLocal((s) => ({ ...s, post: { ...s.post, postsPerDay } }));
                    }}
                    onBlur={() => {
                      const postsPerDay = Math.max(
                        1,
                        Math.min(5, Number(localRef.current.post?.postsPerDay) || 1)
                      );
                      patchModule('post', { enabled: true, postsPerDay });
                    }}
                    className="w-14 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-1.5 py-0.5 text-xs"
                  />
                </label>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AgentDailyPostsPanel({ agent }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all | due | done | missing
  const [postingIds, setPostingIds] = useState(() => new Set());

  const load = useCallback(async () => {
    if (!agent?._id) return;
    setLoading(true);
    try {
      const res = await api.get(`/admin/va/ai-agents/${agent._id}/posting-status`);
      if (res.data?.success) setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [agent?._id]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load, agent?.modules?.post?.enabled, agent?.status]);

  const rows = useMemo(() => {
    const list = data?.accounts || [];
    if (filter === 'due') return list.filter((a) => a.due);
    if (filter === 'done') return list.filter((a) => a.dailyDone);
    if (filter === 'missing') return list.filter((a) => !a.linkedAccountId || !data?.hasContent);
    return list;
  }, [data, filter]);

  const queuePostNow = async (row, { force = false } = {}) => {
    const id = row.profileAccountId;
    if (!id || !agent?._id) return;
    if (!agent.online) {
      toast.error('Desktop agent is offline — start it on the VPS first');
      return;
    }
    if (force) {
      const ok = window.confirm(
        `@${row.username} already posted today. Post again anyway?`
      );
      if (!ok) return;
    }
    setPostingIds((prev) => new Set(prev).add(id));
    try {
      const res = await api.post(`/admin/va/ai-agents/${agent._id}/post-now`, {
        accountId: id,
        force
      });
      toast.success(res.data?.message || `Post queued for @${row.username}`);
      // Refresh soon — status updates after GoLogin finishes
      setTimeout(load, 8000);
      setTimeout(load, 25000);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to queue post');
    } finally {
      setPostingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <BoltIcon className="w-4 h-4 text-sky-500" />
            Daily posts
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            From Models → Auto Posts · random schedule per account (Amsterdam / AMS)
            {data?.modelName ? (
              <>
                {' '}· model <span className="font-medium text-gray-700 dark:text-gray-200">{data.modelName}</span>
              </>
            ) : null}
            {data ? (
              <>
                {' '}· {data.doneCount}/{data.accountCount} done today · {data.dueCount} remaining
                {' '}· cap {data.postsPerDay}/day
              </>
            ) : null}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="form-input text-xs !py-1 !px-2"
          >
            <option value="all">All accounts</option>
            <option value="due">Due today</option>
            <option value="done">Posted today</option>
            <option value="missing">Needs setup</option>
          </select>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="btn-secondary !py-1 !px-2 text-xs inline-flex items-center gap-1"
          >
            <ArrowPathIcon className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {!data?.moduleEnabled && (
        <div className="px-4 py-3 text-xs text-amber-700 dark:text-amber-300 bg-amber-50/80 dark:bg-amber-950/30">
          Post module is off — enable it above to send Auto Posts via GoLogin during account sessions.
        </div>
      )}
      {data?.moduleEnabled && !agent?.online && (
        <div className="px-4 py-3 text-xs text-amber-700 dark:text-amber-300 bg-amber-50/80 dark:bg-amber-950/30">
          Desktop agent offline — Post now needs the VPS agent online.
        </div>
      )}
      {data?.moduleEnabled && !data?.hasContent && (
        <div className="px-4 py-3 text-xs text-amber-700 dark:text-amber-300 bg-amber-50/80 dark:bg-amber-950/30">
          Follow model has no Auto Posts texts/images yet — add them under Models → Edit → Auto Posts.
        </div>
      )}
      {data?.moduleEnabled && !data?.modelId && (
        <div className="px-4 py-3 text-xs text-amber-700 dark:text-amber-300 bg-amber-50/80 dark:bg-amber-950/30">
          Assign a follow model on this agent so Post knows which Auto Posts pack to use.
        </div>
      )}

      <div className="max-h-80 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
        {loading && !data ? (
          <div className="px-4 py-6 text-center text-sm text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-gray-500">No accounts match this filter.</div>
        ) : (
          rows.map((row) => {
            const busy = postingIds.has(row.profileAccountId);
            const canPost = Boolean(
              data?.moduleEnabled
              && data?.hasContent
              && row.linkedAccountId
              && agent?.online
            );
            return (
            <div
              key={row.profileAccountId}
              className="px-4 py-2.5 flex flex-wrap items-center gap-2 text-sm"
            >
              <a
                href={row.profileUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-indigo-600 dark:text-indigo-300 hover:underline min-w-[7rem]"
              >
                @{row.username || '—'}
              </a>
              {row.dailyDone ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
                  Posted today ✓
                </span>
              ) : row.imagesExhausted ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200">
                  Images used up
                </span>
              ) : row.scheduleDue ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                  Schedule due
                </span>
              ) : row.scheduledAt ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200">
                  Scheduled{' '}
                  {new Date(row.scheduledAt).toLocaleTimeString(undefined, {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              ) : row.due ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                  Due ({row.postedToday}/{row.postsPerDay})
                </span>
              ) : !row.linkedAccountId ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  No linked account
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  {row.postedToday}/{row.postsPerDay} today
                </span>
              )}
              <span className="text-[11px] text-gray-400 tabular-nums ml-auto">
                {row.latestPostedAt
                  ? new Date(row.latestPostedAt).toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })
                  : '—'}
              </span>
              <div className="flex items-center gap-1.5">
                {canPost && !row.dailyDone ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => queuePostNow(row, { force: false })}
                    className="btn-primary !py-1 !px-2 text-[11px] inline-flex items-center gap-1 disabled:opacity-50"
                    title="Open GoLogin on the VPS and post now"
                  >
                    {busy ? (
                      <ArrowPathIcon className="w-3 h-3 animate-spin" />
                    ) : (
                      <PlayIcon className="w-3 h-3" />
                    )}
                    Post now
                  </button>
                ) : null}
                {canPost && row.dailyDone ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => queuePostNow(row, { force: true })}
                    className="btn-secondary !py-1 !px-2 text-[11px] inline-flex items-center gap-1 disabled:opacity-50"
                    title="Force another post today (retest)"
                  >
                    {busy ? (
                      <ArrowPathIcon className="w-3 h-3 animate-spin" />
                    ) : (
                      <PlayIcon className="w-3 h-3" />
                    )}
                    Post again
                  </button>
                ) : null}
                {row.latestTweetUrl ? (
                  <a
                    href={row.latestTweetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary !py-1 !px-2 text-[11px]"
                    title={row.latestTweetText || 'Open latest tweet'}
                  >
                    Open post
                  </a>
                ) : null}
                {row.profileUrl ? (
                  <a
                    href={row.profileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary !py-1 !px-2 text-[11px]"
                  >
                    Visit profile
                  </a>
                ) : null}
              </div>
            </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Detail panel
// ────────────────────────────────────────────────────────────────────

function AgentDetailPanel({ agent, vaUsers, onClose, onChanged, openWarmupStartFor, onWarmupStartHandled }) {
  const [config, setConfig] = useState(null);
  const [bootstrap, setBootstrap] = useState(DEFAULT_BOOTSTRAP);
  const [addingLiveRunAccounts, setAddingLiveRunAccounts] = useState(false);
  const [showAttach, setShowAttach] = useState(false);
  // jobId we're editing for a continuation. null = modal closed.
  const [editingJobId, setEditingJobId] = useState(null);
  const [jobs, setJobs] = useState([]);
  // Optional explicit username list — pasted/typed by the operator. When
  // present, it's sent verbatim and (depending on the toggle) topped up from
  // the shared target pool.
  const [customTargetsText, setCustomTargetsText] = useState('');
  const [useTargetPool, setUseTargetPool] = useState(true);
  /**
   * Local working copy of the deselect list — kept in sync with the
   * server but lets the operator stage clicks before persisting. Set of
   * stringified ObjectIds. Defaults to whatever the server reports so a
   * fresh agent has every account selected.
   */
  const [disabledAccountIds, setDisabledAccountIds] = useState(() =>
    new Set((agent?.disabledAccountIds || []).map(String))
  );
  const [accountSelectionSaving, setAccountSelectionSaving] = useState(false);
  const [accountFilter, setAccountFilter] = useState('');
  /** Profiles that must stay deselected until status is cleared. */
  const UNSELECTABLE_STATUSES = useMemo(
    () => new Set(['captcha', 'suspended', 'auth_expired']),
    []
  );
  const isUnselectableAccount = useCallback(
    (acc) => UNSELECTABLE_STATUSES.has(String(acc?.status || '').toLowerCase()),
    [UNSELECTABLE_STATUSES]
  );
  const parsedCustomTargets = useMemo(
    () => parseUsernameList(customTargetsText),
    [customTargetsText]
  );
  const enabledAccountCount = useMemo(() => (
    typeof agent?.enabledAccountCount === 'number'
      ? agent.enabledAccountCount
      : Math.max(0, (agent?.accountCount || 0) - disabledAccountIds.size)
  ), [agent?.enabledAccountCount, agent?.accountCount, disabledAccountIds.size]);
  const jobTargetCapacity = useMemo(
    () => computeJobTargetCapacity(config || agent?.config || DEFAULT_CONFIG, enabledAccountCount),
    [config, agent?.config, enabledAccountCount]
  );
  /**
   * Target source configuration — surfaces what's stored on
   * `agent.targetSource` so the operator can pick between paste,
   * a live scrape session, or previously-scraped data. Kept in
   * local state until Save so we don't fire a PATCH for every
   * keystroke in the paste textarea.
   */
  const initialTs = agent?.targetSource || {};
  const [tsType, setTsType] = useState(initialTs.type || 'scrape_v2_pool');
  const [tsWorkspaceId, setTsWorkspaceId] = useState(initialTs.workspaceId || 'legacy');
  const [tsWorkspaces, setTsWorkspaces] = useState([]);
  const [tsScrapeSessionId, setTsScrapeSessionId] = useState(initialTs.scrapeSessionId || '');
  const [tsScrapeTag, setTsScrapeTag] = useState(initialTs.scrapeTag || '');
  const [tsBufferDays, setTsBufferDays] = useState(initialTs.bufferDays ?? 1);
  const [tsFollowModelId, setTsFollowModelId] = useState(agent?.followModelId || '');
  const initialFbf = initialTs.followBacksFirst || {};
  const [fbfEnabled, setFbfEnabled] = useState(Boolean(initialFbf.enabled));
  const [fbfSourceModelId, setFbfSourceModelId] = useState(
    initialFbf.sourceModelId ? String(initialFbf.sourceModelId) : ''
  );
  const [fbfMinAgeDays, setFbfMinAgeDays] = useState(Number(initialFbf.minAgeDays) || 7);
  const [followModels, setFollowModels] = useState([]);
  const [tsPastedText, setTsPastedText] = useState('');
  const [tsPastedLoaded, setTsPastedLoaded] = useState(false);
  const [tsSaving, setTsSaving] = useState(false);
  const [tsAvailable, setTsAvailable] = useState(-1);
  const [tsStoredInSource, setTsStoredInSource] = useState(null);
  const [tsAlreadyTouched, setTsAlreadyTouched] = useState(null);
  const [tsClaimedInSource, setTsClaimedInSource] = useState(null);
  const [tsUnclaimedInSource, setTsUnclaimedInSource] = useState(null);
  const [tsStatsLoading, setTsStatsLoading] = useState(false);
  const [tsLoadingAvailable, setTsLoadingAvailable] = useState(false);
  const [tsReserveHint, setTsReserveHint] = useState(null);
  const [tsDailyCap, setTsDailyCap] = useState(0);
  const [tsDesiredBuffer, setTsDesiredBuffer] = useState(0);
  // Cached lookups for dropdowns
  const [scrapeSessions, setScrapeSessions] = useState([]);
  const [scrapeV2Sessions, setScrapeV2Sessions] = useState([]);
  const [scrapeTags, setScrapeTags] = useState([]);
  const [scrapeV2Categories, setScrapeV2Categories] = useState([]);
  const tsParsedPaste = useMemo(() => parseUsernameList(tsPastedText), [tsPastedText]);
  /**
   * Configure panel (setup) is now a collapsible section — no longer a separate tab.
   * configureOpen controls visibility; default closed.
   */
  const [configureOpen, setConfigureOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [showWarmupStartModal, setShowWarmupStartModal] = useState(false);

  // Open warmup modal when triggered from the agents list "Start" button.
  useEffect(() => {
    if (openWarmupStartFor && agent && String(openWarmupStartFor) === String(agent._id)) {
      setShowWarmupStartModal(true);
      onWarmupStartHandled?.();
    }
  }, [openWarmupStartFor, agent?._id, onWarmupStartHandled]);
  // Keep detailTab for any residual refs that haven't been cleaned up.
  const detailTab = 'run';
  const setDetailTab = (tab) => { if (tab === 'setup') setConfigureOpen(true); };
  /** Live job doc (the most recent one, refreshed every few seconds). */
  const [liveJob, setLiveJob] = useState(null);

  // Full Test Interface — drawer (closed by default)
  const [testDrawerOpen, setTestDrawerOpen] = useState(false);
  const [testSelectedAccountIds, setTestSelectedAccountIds] = useState([]);
  const [testTargetUsername, setTestTargetUsername] = useState('');
  const [testSessionLoading, setTestSessionLoading] = useState(false);
  const [testModeStatus, setTestModeStatus] = useState(null);
  const [testLogs, setTestLogs] = useState([]);
  const [logsOpen, setLogsOpen] = useState(true);
  const testSessionActive = Boolean(testModeStatus?.testSession?.active);

  // IMPORTANT: depend ONLY on agent._id, never on agent.config / agent.bootstrap.
  // The parent polls `/admin/va/ai-agents/:id` every 5s, and Mongoose .lean()
  // returns a brand-new config object on every fetch (same content, new ref).
  // If we watched agent.config here, every poll would stomp the operator's
  // in-progress edits in the Setup form back to whatever the server has —
  // making changes to e.g. `maxConcurrentBrowsers` look like they save but
  // actually got reverted by the next poll before Save was clicked.
  // The effect now only resets local state when the operator opens a
  // different agent. eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (agent?.config) {
      setConfig({ ...DEFAULT_CONFIG, ...agent.config });
    }
    if (agent?.bootstrap) setBootstrap({ ...DEFAULT_BOOTSTRAP, ...agent.bootstrap });
    {
      const next = new Set((agent?.disabledAccountIds || []).map(String));
      for (const acc of agent?.accounts || []) {
        if (isUnselectableAccount(acc)) next.add(String(acc._id));
      }
      setDisabledAccountIds(next);
    }
    setAccountFilter('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?._id]);

  // Re-sync the disabled set when the parent's polling refreshes the
  // agent payload — but only when we aren't mid-edit. Always force-include
  // captcha / suspended / auth_expired from the hydrated accounts list so a
  // light poll (or stale disabledAccountIds) cannot re-check them.
  useEffect(() => {
    if (accountSelectionSaving) return;
    const next = new Set((agent?.disabledAccountIds || []).map(String));
    for (const acc of agent?.accounts || []) {
      if (isUnselectableAccount(acc)) next.add(String(acc._id));
    }
    setDisabledAccountIds((prev) => {
      if (prev.size === next.size && [...prev].every((id) => next.has(id))) {
        return prev;
      }
      return next;
    });
  }, [agent?.disabledAccountIds, agent?.accounts, accountSelectionSaving, isUnselectableAccount]);

  // Persist auto-deselect when the server still has parked profiles enabled.
  const ineligiblePersistRef = useRef('');
  useEffect(() => {
    if (!agent?._id || accountSelectionSaving) return;
    const accounts = agent?.accounts || [];
    if (!accounts.length) return;
    const serverDisabled = new Set((agent?.disabledAccountIds || []).map(String));
    const missing = [];
    for (const acc of accounts) {
      if (!isUnselectableAccount(acc)) continue;
      const id = String(acc._id);
      if (!serverDisabled.has(id)) missing.push(id);
    }
    if (!missing.length) return;
    const key = `${agent._id}:${missing.slice().sort().join(',')}`;
    if (ineligiblePersistRef.current === key) return;
    ineligiblePersistRef.current = key;
    const next = new Set([...serverDisabled, ...missing]);
    setDisabledAccountIds(next);
    persistAccountSelection(next);
  // persistAccountSelection is stable enough via closure; avoid re-firing on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?._id, agent?.accounts, agent?.disabledAccountIds, accountSelectionSaving, isUnselectableAccount]);

  // Initialise the TargetSource form when the operator switches agents.
  // We intentionally do NOT re-sync on every poll: the parent refetches
  // `/admin/va/ai-agents/:id` every 5s and Mongoose .lean() returns a
  // new `targetSource` object reference each time. Any value-comparing
  // guard here still stomps in-progress local edits because the server
  // still reports the OLD value until the operator clicks Save (so the
  // dropdown snaps back to "paste" after ~2 seconds — exactly the bug
  // we just fixed). Re-syncing on `_id` is enough: when the user opens
  // a different agent (or the same one again) we pull the latest
  // server snapshot. Reset paste-loaded flag too so the new agent's
  // saved pastedTargets get pulled on next paste-mode reveal.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const ts = agent?.targetSource || {};
    setTsType(ts.type || 'scrape_v2_pool');
    setTsScrapeSessionId(ts.scrapeSessionId || '');
    setTsScrapeTag(ts.scrapeTag || '');
    setTsBufferDays(ts.bufferDays ?? 1);
    setTsFollowModelId(agent?.followModelId || '');
    const fbf = ts.followBacksFirst || {};
    setFbfEnabled(Boolean(fbf.enabled));
    setFbfSourceModelId(fbf.sourceModelId ? String(fbf.sourceModelId) : '');
    setFbfMinAgeDays(Number(fbf.minAgeDays) || 7);
    setTsPastedText('');
    setTsPastedLoaded(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?._id]);

  // Keep follow model in sync when the server updates (e.g. after Create Automation).
  useEffect(() => {
    if (!agent?.followModelId) return;
    const serverModel = String(agent.followModelId);
    setTsFollowModelId((prev) => (prev === serverModel ? prev : serverModel));
  }, [agent?.followModelId]);

  useEffect(() => {
    if (fbfSourceModelId && tsFollowModelId && String(fbfSourceModelId) === String(tsFollowModelId)) {
      setFbfSourceModelId('');
    }
  }, [tsFollowModelId, fbfSourceModelId]);

  /**
   * Pull the available-targets count, daily cap, and desired buffer for
   * the current target source. Mirrors Create Automation modal params:
   * always pass followModelId + scrapeSessionId/scrapeTag when linked.
   */
  const loadTargetSourceAvailable = useCallback(async () => {
    if (!agent?._id) return;
    const effectiveType = tsType || agent.targetSource?.type || 'paste';
    const modelId = String(tsFollowModelId || agent.followModelId || '').trim();
    if (effectiveType !== 'paste' && !modelId) {
      setTsAvailable(-1);
      setTsStoredInSource(null);
      setTsAlreadyTouched(null);
      setTsClaimedInSource(null);
      setTsUnclaimedInSource(null);
      return;
    }
    setTsStatsLoading(true);
    setTsLoadingAvailable(true);
    setTsReserveHint(null);
    try {
      const params = { workspaceId: tsWorkspaceId || 'legacy' };
      if (modelId) params.followModelId = modelId;
      if ((effectiveType === 'scrape_session' || effectiveType === 'scrape_v2_session')
        && (tsScrapeSessionId || agent.targetSource?.scrapeSessionId)) {
        params.scrapeSessionId = tsScrapeSessionId || agent.targetSource?.scrapeSessionId;
        params.sourceType = effectiveType;
      } else if (effectiveType === 'scrape_v2_pool') {
        params.sourceType = 'scrape_v2_pool';
        // Local draft wins: empty string = all categories (do not fall back to saved tag).
        if (tsScrapeTag) params.scrapeTag = tsScrapeTag;
      } else if (effectiveType === 'scrape_tag' && (tsScrapeTag || agent.targetSource?.scrapeTag)) {
        params.scrapeTag = tsScrapeTag || agent.targetSource?.scrapeTag;
      }

      const applyPartial = (partial) => {
        if (typeof partial.storedInSource === 'number') setTsStoredInSource(partial.storedInSource);
        if (typeof partial.alreadyTouchedInSource === 'number') setTsAlreadyTouched(partial.alreadyTouchedInSource);
        if (typeof partial.claimedInSource === 'number') setTsClaimedInSource(partial.claimedInSource);
        if (typeof partial.unclaimedInSource === 'number') setTsUnclaimedInSource(partial.unclaimedInSource);
        if (typeof partial.available === 'number') {
          setTsAvailable(partial.available);
          setTsLoadingAvailable(false);
        }
        if (typeof partial.estimatedDailyFollowCap === 'number') {
          setTsDailyCap(partial.estimatedDailyFollowCap);
        }
        if (typeof partial.desiredBufferAhead === 'number') {
          setTsDesiredBuffer(partial.desiredBufferAhead);
        }
        setTsReserveHint(formatPoolReserveHint(partial));
      };

      const data = await fetchTargetSourceAvailability(agent._id, params, {
        onProgress: applyPartial,
      });
      if (data) {
        applyPartial(data);
        if (typeof data.available === 'number') {
          setTsAvailable(data.available);
          setTsLoadingAvailable(false);
        }
        setTsDailyCap(data.estimatedDailyFollowCap || 0);
        setTsDesiredBuffer(data.desiredBufferAhead || 0);
      }
    } catch (err) {
      if (err.response?.data?.needsScrapeOwner) {
        toast.error(err.response.data.error || 'Assign a follow model first');
      }
    } finally {
      setTsStatsLoading(false);
    }
  }, [
    agent?._id,
    agent?.followModelId,
    agent?.targetSource?.scrapeSessionId,
    agent?.targetSource?.scrapeTag,
    tsFollowModelId,
    tsType,
    tsScrapeSessionId,
    tsScrapeTag,
    tsWorkspaceId,
  ]);

  const loadFollowModels = useCallback(async () => {
    if (!agent?._id) return;
    try {
      const res = await api.get(`/admin/va/ai-agents/${agent._id}/follow-models`);
      if (res.data?.success) {
        setFollowModels(res.data.models || []);
        if (!tsFollowModelId && res.data.selectedModelId) {
          setTsFollowModelId(res.data.selectedModelId);
        }
      }
    } catch (_) { /* non-fatal */ }
  }, [agent?._id, tsFollowModelId]);

  /**
   * Lazily load scrape sessions and tags for the source dropdowns.
   * Runs once per agent open; the lists are stable for the duration
   * of the dashboard session.
   */
  const loadScrapeSources = useCallback(async () => {
    if (!agent?._id) return;
    try {
      // Admin endpoint scoped to THE AGENT'S OWNER, so an admin viewing
      // an agent that belongs to another user still sees that user's
      // scrape sessions / tags. (The plain /scraper/sessions and
      // /scraper/data are filtered by req.user._id and would otherwise
      // return an empty list here.)
      const res = await api.get(`/admin/va/ai-agents/${agent._id}/target-source/scrape-sources`);
      if (res.data?.success) {
        setScrapeSessions(res.data.sessions || []);
        setScrapeV2Sessions(res.data.v2Sessions || []);
        setScrapeTags(res.data.dataSets || []);
        setTsWorkspaces(res.data.workspaces || []);
      }
    } catch (_) { /* non-fatal */ }
    try {
      const catRes = await api.get('/scraper-v2/categories');
      if (catRes.data?.success) {
        setScrapeV2Categories(catRes.data.categories || []);
      }
    } catch (_) { /* non-fatal — categories optional until Scraper V2 is used */ }
  }, [agent?._id]);

  // Load saved pastedTargets on first reveal of the Setup → paste mode.
  const loadPastedTargets = useCallback(async () => {
    if (!agent?._id || tsPastedLoaded) return;
    try {
      const res = await api.get(`/admin/va/ai-agents/${agent._id}/secrets`);
      const list = Array.isArray(res.data?.pastedTargets) ? res.data.pastedTargets : null;
      if (list) {
        setTsPastedText(list.join('\n'));
        setTsPastedLoaded(true);
      }
    } catch (_) { /* non-fatal */ }
  }, [agent?._id, tsPastedLoaded]);

  // Refresh source stats on panel open and every 30s; load models first so followModelId is known.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadFollowModels();
      if (!cancelled) await loadTargetSourceAvailable();
    })();
    loadScrapeSources();
    const id = window.setInterval(() => loadTargetSourceAvailable(), 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [loadTargetSourceAvailable, loadScrapeSources, loadFollowModels]);

  useEffect(() => {
    loadTargetSourceAvailable();
  }, [
    agent?._id,
    agent?.targetSource?.type,
    agent?.targetSource?.scrapeSessionId,
    agent?.targetSource?.scrapeTag,
    agent?.followModelId,
    tsFollowModelId,
    loadTargetSourceAvailable
  ]);

  /** Merge live job stats only while a job is actively running — idle jobs report followed:0. */
  const sourceStats = useMemo(() => {
    const lr = liveJob?.liveRun;
    const jobActive = liveJob && ['running', 'pausing', 'paused'].includes(liveJob.status);
    const available = tsAvailable >= 0
      ? tsAvailable
      : (jobActive && typeof lr?.sourceAvailable === 'number' ? lr.sourceAvailable : tsAvailable);
    const stored = tsStoredInSource != null
      ? tsStoredInSource
      : (jobActive && typeof lr?.sourceTotal === 'number' ? lr.sourceTotal : null);
    const touched = tsAlreadyTouched != null
      ? tsAlreadyTouched
      : (jobActive && typeof lr?.followed === 'number' && lr.followed > 0 ? lr.followed : null);
    return {
      available,
      stored,
      touched,
      claimed: tsClaimedInSource,
      unclaimed: tsUnclaimedInSource,
      loading: tsStatsLoading,
      loadingAvailable: tsLoadingAvailable,
      reserveHint: tsReserveHint,
    };
  }, [tsAvailable, tsStoredInSource, tsAlreadyTouched, tsClaimedInSource, tsUnclaimedInSource, tsStatsLoading, tsLoadingAvailable, tsReserveHint, liveJob?.liveRun, liveJob?.status]);

  const saveTargetSource = async (patch = {}) => {
    if (!agent?._id) return;
    setTsSaving(true);
    try {
      const body = {
        type: tsType,
        workspaceId: tsWorkspaceId || 'legacy',
        scrapeSessionId: tsScrapeSessionId || null,
        scrapeTag: tsScrapeTag || null,
        bufferDays: Number(tsBufferDays) || 1,
        ...patch
      };
      if (patch.pastedTargets !== undefined) {
        body.pastedTargets = patch.pastedTargets;
      } else if (tsType === 'paste') {
        body.pastedTargets = tsParsedPaste;
      }
      if (tsType === 'scrape_v2_pool' && fbfEnabled) {
        if (!fbfSourceModelId) {
          toast.error('Pick the model whose follow-backs to take, or turn follow-backs-first off');
          setTsSaving(false);
          return;
        }
        if (String(fbfSourceModelId) === String(tsFollowModelId)) {
          toast.error('Follow-backs-first has to use a different model than this agent follows as');
          setTsSaving(false);
          return;
        }
      }
      const minAgeDays = (() => {
        const n = Math.round(Number(fbfMinAgeDays));
        if (!Number.isFinite(n)) return 7;
        return Math.max(1, Math.min(90, n));
      })();
      if (tsType === 'scrape_v2_pool') {
        body.followBacksFirst = {
          enabled: Boolean(fbfEnabled),
          sourceModelId: fbfSourceModelId || null,
          minAgeDays,
        };
      }
      if (tsFollowModelId && tsFollowModelId !== (agent.followModelId || '')) {
        await api.patch(`/admin/va/ai-agents/${agent._id}/follow-model`, {
          followModelId: tsFollowModelId
        });
      }
      const res = await api.patch(`/admin/va/ai-agents/${agent._id}/target-source`, body);
      if (res.data?.success) {
        toast.success('Target source saved');
        if (typeof res.data.available === 'number') setTsAvailable(res.data.available);
        onChanged();
        loadTargetSourceAvailable();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save target source');
    } finally {
      setTsSaving(false);
    }
  };

  const loadJobs = useCallback(async () => {
    if (!agent?._id) return;
    try {
      const res = await api.get(`/admin/va/ai-agents/${agent._id}/jobs`, { params: { limit: 10 } });
      if (res.data.success) setJobs(res.data.jobs || []);
    } catch (_) { /* ignore */ }
  }, [agent?._id]);

  useEffect(() => { loadJobs(); }, [loadJobs, agent?._id]);

  /**
   * Refresh the live job (most recent job for this agent) more aggressively
   * than the broader jobs list. Pollers only run while the Run tab is open
   * and the latest job is still active, so we don't hammer the API once a
   * run finishes.
   */
  const fetchLiveJob = useCallback(async (jobId) => {
    if (!agent?._id || !jobId) return;
    try {
      const res = await api.get(`/admin/va/ai-agents/${agent._id}/jobs/${jobId}`);
      if (res.data.success) setLiveJob(res.data.job || null);
    } catch (_) { /* ignore transient errors during polling */ }
  }, [agent?._id]);

  // Pick which job to render in the Live view: prefer the latest non-cancelled job.
  // Cancelled duplicates (from race-condition guards) should not shadow the real running job.
  const latestJob = jobs.find((j) => j.status !== 'cancelled') || jobs[0] || null;
  const latestJobId = latestJob?._id ? String(latestJob._id) : null;
  const latestJobStatus = latestJob?.status || null;
  useEffect(() => {
    if (!latestJobId) {
      setLiveJob(null);
      return undefined;
    }
    fetchLiveJob(latestJobId);
    const isActive = latestJobStatus === 'running' || latestJobStatus === 'queued' || latestJobStatus === 'preparing' || latestJobStatus === 'pausing';
    const pollMs = isActive ? 2500 : 15000;
    const id = window.setInterval(() => fetchLiveJob(latestJobId), pollMs);
    return () => window.clearInterval(id);
  }, [latestJobId, latestJobStatus, fetchLiveJob]);

  const testableAccounts = useMemo(
    () => (agent?.accounts || []).filter((a) => a.gologinProfileId),
    [agent?.accounts]
  );

  if (!agent) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 text-center text-sm text-gray-500">
        Loading agent…
      </div>
    );
  }

  const copy = (text) => {
    if (!text) return;
    navigator.clipboard?.writeText(text).then(
      () => toast.success('Copied'),
      () => toast.error('Copy failed')
    );
  };

  const saveConfig = async () => {
    try {
      await api.patch(`/admin/va/ai-agents/${agent._id}`, { config, bootstrap });
      toast.success('Configuration saved');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    }
  };

  /** Save a config object passed directly (used by inline edit in the Run tab). */
  const saveConfigDirect = async (newConfig) => {
    try {
      await api.patch(`/admin/va/ai-agents/${agent._id}`, { config: newConfig, bootstrap });
      setConfig(newConfig);
      toast.success('Configuration saved');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    }
  };

  const renameAgent = async () => {
    const next = window.prompt('Rename agent', agent.name);
    if (!next || next === agent.name) return;
    try {
      await api.patch(`/admin/va/ai-agents/${agent._id}`, { name: next.trim() });
      toast.success('Renamed');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to rename');
    }
  };

  const detachAccount = async (id) => {
    if (!window.confirm('Detach this account from the agent?')) return;
    try {
      await api.post(`/admin/va/ai-agents/${agent._id}/detach-accounts`, { accountIds: [id] });
      toast.success('Detached');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to detach');
    }
  };

  /**
   * Persist the local deselect set to the server. Optimistic: the local
   * state has already been updated by the caller, this just flushes the
   * decision to /account-selection. Rolls back to the server's view on
   * failure so the UI doesn't lie about what /start will actually use.
   */
  const persistAccountSelection = async (nextSet) => {
    if (!agent?._id) return;
    setAccountSelectionSaving(true);
    try {
      await api.patch(`/admin/va/ai-agents/${agent._id}/account-selection`, {
        disabledAccountIds: Array.from(nextSet)
      });
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save selection');
      setDisabledAccountIds(new Set((agent?.disabledAccountIds || []).map(String)));
    } finally {
      setAccountSelectionSaving(false);
    }
  };

  const toggleAccountEnabled = (id, nextEnabled) => {
    if (nextEnabled) {
      const acc = (agent?.accounts || []).find((a) => String(a._id) === String(id));
      if (acc && isUnselectableAccount(acc)) {
        toast.error(
          `@${acc.username || id} is ${acc.status} — clear it before enabling`
        );
        return;
      }
    }
    setDisabledAccountIds((prev) => {
      const next = new Set(prev);
      if (nextEnabled) next.delete(String(id));
      else next.add(String(id));
      persistAccountSelection(next);
      return next;
    });
  };

  const selectAllAccounts = () => {
    // Enable every attached account except captcha / suspended / auth_expired.
    setDisabledAccountIds((prev) => {
      const next = new Set();
      for (const acc of agent?.accounts || []) {
        if (isUnselectableAccount(acc)) next.add(String(acc._id));
      }
      const same = next.size === prev.size && [...next].every((id) => prev.has(id));
      if (same) return prev;
      persistAccountSelection(next);
      return next;
    });
  };

  const deselectAllAccounts = () => {
    const allIds = (agent?.accounts || []).map((a) => String(a._id));
    setDisabledAccountIds((prev) => {
      const next = new Set(allIds);
      if (prev.size === next.size && allIds.every((id) => prev.has(id))) return prev;
      persistAccountSelection(next);
      return next;
    });
  };

  const dispatchJob = async () => {
    const srcType = agent?.targetSource?.type || 'paste';
    // In scrape_session / scrape_tag modes the backend pulls targets
    // straight from the linked source; the per-run textarea is
    // intentionally ignored so the operator can't accidentally mix
    // sources. In paste mode we still allow the legacy per-run
    // textarea AS WELL AS the saved pastedTargets list (merged
    // server-side).
    const sendExplicit = srcType === 'paste';
    const explicitTargets = sendExplicit ? parsedCustomTargets : [];
    if (sendExplicit && !useTargetPool && explicitTargets.length === 0
        && !(agent?.targetSource?.pastedTargetsCount > 0)) {
      toast.error('Provide usernames to follow or enable "Top up from target pool".');
      return;
    }
    try {
      // "Stop Automation" disables auto-continuation; re-enable it here so a
      // restart always runs in continuous live-run mode (per-account 24h
      // cycles). A start with it disabled dispatches a legacy one-shot job
      // instead of the continuous automation. Failure aborts the start —
      // the outer catch surfaces the error toast.
      await api.patch(`/admin/va/ai-agents/${agent._id}/auto-continuation`, { enabled: true });
      const res = await api.post(`/admin/va/ai-agents/${agent._id}/start`, {
        useTargetPool: sendExplicit ? useTargetPool : false,
        targets: explicitTargets
      });
      if (res.data.success) {
        const dispatched = res.data?.job?.totals?.totalTargets ?? jobTargetCapacity;
        toast.success(`Job dispatched (${dispatched} targets)`);
        setCustomTargetsText('');
        loadJobs();
        onChanged();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start');
      loadTargetSourceAvailable();
    }
  };

  const stopJob = async () => {
    try {
      await api.post(`/admin/va/ai-agents/${agent._id}/stop`, {});
      toast.success('Stop signal sent');
      loadJobs();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to stop');
    }
  };

  const pauseJob = async () => {
    try {
      await api.post(`/admin/va/ai-agents/${agent._id}/pause`, {});
      toast.success('Pause signal sent — browsers will close shortly');
      loadJobs();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to pause');
    }
  };

  const resumeJob = async () => {
    try {
      const res = await api.post(`/admin/va/ai-agents/${agent._id}/resume`, {});
      if (res.data?.completed) {
        toast.success('Nothing left to resume — marked complete');
      } else {
        const remaining = res.data?.resumed?.remainingTargets ?? 0;
        toast.success(`Resuming — ${remaining.toLocaleString()} targets queued`);
      }
      loadJobs();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to resume');
    }
  };

  /**
   * Re-dispatch a finished job's leftover targets (everything that
   * wasn't followed or already-following) as a NEW job. Used from the
   * Recent jobs row when a run ended with `followed < totalTargets`.
   */
  const continueJob = async (sourceJobId) => {
    try {
      const res = await api.post(`/admin/va/ai-agents/${agent._id}/continue/${sourceJobId}`, {});
      if (res.data?.alreadyDone) {
        toast.success('Nothing left to follow — every target was already handled');
      } else {
        const left = res.data?.leftoverCount ?? 0;
        toast.success(`Continuing — ${left.toLocaleString()} targets queued`);
      }
      loadJobs();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to continue');
    }
  };

  const forceStopJob = async (jobId) => {
    try {
      await api.post(`/admin/va/ai-agents/${agent._id}/jobs/${jobId}/force-cancel`);
      toast.success('Job force-cancelled');
      onChanged();
      loadJobs();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to force-cancel job');
    }
  };

  /**
   * Cancel or disable the auto-continuation schedule. Two modes:
   *   - 'cancel' clears the pending dispatch but leaves the feature on
   *     (next finished-with-leftovers job will re-schedule one).
   *   - 'disable' flips the master switch off; the operator has to
   *     manually click Continue from then on.
   */
  const updateAutoContinuation = async (mode) => {
    try {
      const body = mode === 'disable' ? { enabled: false } : { cancelPending: true };
      await api.patch(`/admin/va/ai-agents/${agent._id}/auto-continuation`, body);
      toast.success(mode === 'disable' ? 'Auto-continuation disabled' : 'Pending auto-continuation cancelled');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update auto-continuation');
    }
  };

  const enableAutoContinuation = async () => {
    try {
      await api.patch(`/admin/va/ai-agents/${agent._id}/auto-continuation`, { enabled: true });
      toast.success('Auto-continuation enabled');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to enable auto-continuation');
    }
  };

  const addAccountsToLiveRun = async () => {
    const jobId = liveJob?._id || jobs[0]?._id;
    if (!jobId) {
      toast.error('No live run job to extend — start a run first');
      return;
    }
    setAddingLiveRunAccounts(true);
    try {
      const res = await api.post(`/admin/va/ai-agents/${agent._id}/live-run/add-accounts`, { jobId });
      if (res.data?.success) {
        const n = res.data.addedAccountIds?.length || 0;
        toast.success(
          res.data.queued
            ? `Queued ${n} account(s) — desktop worker will start them when the current job finishes`
            : `Added ${n} account(s) to the live run`
        );
        onChanged();
        loadJobs();
        if (res.data.job?._id) fetchLiveJob(res.data.job._id);
        else fetchLiveJob(jobId);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add accounts to live run');
    } finally {
      setAddingLiveRunAccounts(false);
    }
  };

  const toggleTestAccount = (accountId) => {
    const id = String(accountId);
    setTestSelectedAccountIds((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
  };

  const handleStartTestSession = async () => {
    const target = testTargetUsername.replace(/^@/, '').trim();
    if (!testSelectedAccountIds.length) {
      toast.error('Select at least one account');
      return;
    }
    if (!target) {
      toast.error('Enter a target username');
      return;
    }
    setTestSessionLoading(true);
    try {
      const res = await api.post(`/admin/va/ai-agents/${agent._id}/test-session/start`, {
        accountIds: testSelectedAccountIds,
        targetUsername: target
      });
      if (res.data?.success) {
        setTestModeStatus((prev) => ({
          ...prev,
          scanner: res.data.scanner,
          testSession: res.data.testSession
        }));
        toast.success('Test session started');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start test session');
    } finally {
      setTestSessionLoading(false);
    }
  };

  const handleStopTestSession = async () => {
    setTestSessionLoading(true);
    try {
      const res = await api.post(`/admin/va/ai-agents/${agent._id}/test-session/stop`);
      if (res.data?.success) {
        setTestModeStatus((prev) => ({
          ...prev,
          scanner: res.data.scanner,
          testSession: res.data.testSession
        }));
        setTestLogs([]);
        toast.success('Test session stopped');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to stop test session');
    } finally {
      setTestSessionLoading(false);
    }
  };

  const handleClearLogs = async () => {
    try {
      await api.delete(`/admin/va/ai-agents/${agent._id}/test-mode-logs`);
      setTestLogs([]);
    } catch (_) {}
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-indigo-300 dark:border-indigo-700 shadow-lg overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-950/40 dark:to-violet-950/30 border-b border-indigo-200 dark:border-indigo-800">
        <div className="flex items-center gap-3">
          <ComputerDesktopIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-300" />
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900 dark:text-white">{agent.name}</h3>
              <button onClick={renameAgent} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <PencilSquareIcon className="w-4 h-4" />
              </button>
            </div>
            {agent.description && <p className="text-xs text-gray-500">{agent.description}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill online={agent.online} />
          <RunStatusPill status={agent.status} />
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Header action row */}
      <div className="flex items-center justify-between gap-2 px-5 pt-2 pb-2 border-b border-indigo-100 dark:border-indigo-900/40">
        <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <BoltIcon className="w-3.5 h-3.5" />
          <span className="font-medium">Automation</span>
        </div>
        <div className="flex items-center gap-2">
          {agent.online && (
            <button
              type="button"
              onClick={() => setTestDrawerOpen(true)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                testSessionActive
                  ? 'bg-violet-100 text-violet-800 ring-1 ring-violet-300 dark:bg-violet-900/40 dark:text-violet-200 dark:ring-violet-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700/50 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
              title="Open follow + follow-back test suite"
            >
              <BeakerIcon className="w-3.5 h-3.5" />
              Test Suite
              {testSessionActive && (
                <span className="inline-flex items-center gap-1 ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-violet-200/80 text-violet-900 dark:bg-violet-800 dark:text-violet-100">
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-600 animate-pulse" />
                  In progress
                </span>
              )}
            </button>
          )}
          <button
            type="button"
            onClick={() => setConfigureOpen((o) => !o)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              configureOpen
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700/50 dark:text-gray-300 dark:hover:bg-gray-700'
            }`}
            title="Configure source, accounts, and job settings"
          >
            <Cog6ToothIcon className="w-3.5 h-3.5" />
            {configureOpen ? 'Hide Configuration' : 'Configure Automation'}
          </button>
        </div>
      </div>

      {/* Test suite drawer — closed by default */}
      {testDrawerOpen && agent.online && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={() => setTestDrawerOpen(false)}
            aria-hidden="true"
          />
          <div className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-white dark:bg-gray-900 shadow-2xl border-l border-violet-200 dark:border-violet-800 flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-violet-200 dark:border-violet-800 bg-violet-50/80 dark:bg-violet-950/40">
              <div className="flex items-center gap-2">
                <BeakerIcon className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Full Test Suite</h4>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">Follow → scan → DM (automated)</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setTestDrawerOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Status */}
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Status</span>
                  {testSessionActive ? (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-200">
                      <span className="w-2 h-2 rounded-full bg-violet-500 animate-pulse" />
                      In progress
                    </span>
                  ) : (
                    <span className="text-[11px] text-gray-500">Ready</span>
                  )}
                </div>
                {testSessionActive && (
                  <div className="mt-2 space-y-1 text-[11px] text-gray-500 dark:text-gray-400">
                    <p>
                      Follows: {testModeStatus?.testSession?.followsCompleted ?? 0}
                      /{testModeStatus?.testSession?.followsTotal ?? testSelectedAccountIds.length}
                    </p>
                    <p>
                      Scanner: {testModeStatus?.scanner?.active ? 'Running (auto)' : 'Stopped'}
                      {testModeStatus?.scanner?.scanning ? ' · scanning now…' : ''}
                    </p>
                    {testModeStatus?.scanner?.lastScanAt && (
                      <p>Last scan: {new Date(testModeStatus.scanner.lastScanAt).toLocaleTimeString()}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Account multi-select */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Profile accounts</label>
                  {!testSessionActive && testableAccounts.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setTestSelectedAccountIds(testableAccounts.map((a) => String(a._id)))}
                      className="text-[10px] text-violet-600 dark:text-violet-400 hover:underline"
                    >
                      Select all
                    </button>
                  )}
                </div>
                <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
                  {testableAccounts.length === 0 ? (
                    <p className="p-3 text-xs text-gray-400">No accounts with GoLogin profiles attached.</p>
                  ) : (
                    testableAccounts.map((a) => {
                      const id = String(a._id);
                      const checked = testSelectedAccountIds.includes(id);
                      return (
                        <label
                          key={id}
                          className={`flex items-center gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 ${testSessionActive ? 'opacity-60 cursor-not-allowed' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={testSessionActive || testSessionLoading}
                            onChange={() => toggleTestAccount(id)}
                            className="rounded border-gray-300"
                          />
                          <span className="text-gray-800 dark:text-gray-200">@{a.username}</span>
                          {!a.linkedAccountId && (
                            <span className="text-amber-500 text-[10px]">no link</span>
                          )}
                        </label>
                      );
                    })
                  )}
                </div>
                <p className="text-[10px] text-gray-400">
                  {testSelectedAccountIds.length} selected · 10–20s random delay between follows
                </p>
              </div>

              {/* Target */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Target username</label>
                <input
                  type="text"
                  value={testTargetUsername}
                  onChange={(e) => setTestTargetUsername(e.target.value)}
                  placeholder="username (no @)"
                  disabled={testSessionActive || testSessionLoading}
                  className="form-input text-sm w-full"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                {!testSessionActive ? (
                  <button
                    type="button"
                    onClick={handleStartTestSession}
                    disabled={
                      testSessionLoading
                      || !testSelectedAccountIds.length
                      || !testTargetUsername.trim()
                    }
                    className="flex-1 inline-flex items-center justify-center gap-1.5 btn-primary !py-2 text-sm disabled:opacity-40"
                  >
                    <PlayIcon className="w-4 h-4" />
                    {testSessionLoading ? 'Starting…' : 'Start Test'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleStopTestSession}
                    disabled={testSessionLoading}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 btn-secondary !py-2 text-sm !border-red-300 !text-red-700 dark:!border-red-800 dark:!text-red-300 disabled:opacity-40"
                  >
                    <StopIcon className="w-4 h-4" />
                    {testSessionLoading ? 'Stopping…' : 'Stop Test'}
                  </button>
                )}
              </div>

              <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-relaxed">
                Starting a test automatically queues follows for each selected account, starts follow-back scanning, and sends DMs when follow-backs are detected.
              </p>

              {/* Live logs */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
                    <ClipboardDocumentListIcon className="w-4 h-4" />
                    Live Logs
                    {testLogs.length > 0 && (
                      <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 font-semibold">
                        {testLogs.length}
                      </span>
                    )}
                  </span>
                  {testLogs.length > 0 && (
                    <button
                      type="button"
                      onClick={handleClearLogs}
                      className="text-[10px] text-gray-400 hover:text-red-500"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <TestModeLogPanel logs={testLogs} />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Outdated-worker banner — the server refuses to dispatch to
          desktop-agent builds older than the runtime fix that prevents
          the round-1 ReferenceError. Push the operator straight to
          Setup so they can redownload + restart. */}
      {agent.desktopAgent?.outdated && (
        <div className="mx-5 mt-3 rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 px-4 py-3">
          <div className="flex items-start gap-2 text-xs text-amber-900 dark:text-amber-100">
            <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-300" />
            <div className="flex-1 space-y-1">
              <p className="font-semibold">Desktop worker is outdated</p>
              <p>
                Running v{agent.desktopAgent.reportedVersion || 'unknown'}, need ≥ v{agent.desktopAgent.minRequiredVersion}.
                Older builds crash every profile after round 1 with a <code>attemptedCount is not defined</code> error
                and the job stalls. Open Setup → Download desktop agent, replace the folder on the Mac,
                then <code>npm install &amp;&amp; npm start</code>.
              </p>
            </div>
            {!configureOpen && (
              <button
                type="button"
                onClick={() => setConfigureOpen(true)}
                className="btn-secondary !py-1 !px-2.5 text-xs inline-flex items-center gap-1 flex-shrink-0"
              >
                Open Configuration
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Configure Automation (collapsible) ── */}
      <div className={configureOpen ? 'block' : 'hidden'}>
      <div className="p-5 grid lg:grid-cols-2 gap-6">
        {/* Pairing + GoLogin secrets */}
        <section className="space-y-5">
          <AgentDesktopSetup
            agent={agent}
            onCopy={copy}
            onAgentUpdated={() => onChanged()}
          />
        </section>

        {/* Config + bootstrap */}
        <section className="space-y-4">
          {config && <ConfigEditor value={config} onChange={setConfig} />}
          <BootstrapEditor value={bootstrap} onChange={setBootstrap} />
          <div className="flex justify-end">
            <button onClick={saveConfig} className="btn-primary text-sm">Save configuration</button>
          </div>
        </section>
      </div>

      {/* Scraper V2 pool — only shown for follow automation agents */}
      {(agent?.automationType !== 'warmup') && (
        <div className="px-5 pb-5">
          <TargetSourcePanel
            tsType={tsType}
            setTsType={setTsType}
            tsWorkspaceId={tsWorkspaceId}
            setTsWorkspaceId={setTsWorkspaceId}
            tsWorkspaces={tsWorkspaces}
            tsScrapeSessionId={tsScrapeSessionId}
            setTsScrapeSessionId={setTsScrapeSessionId}
            tsScrapeTag={tsScrapeTag}
            setTsScrapeTag={setTsScrapeTag}
            jobTargetCapacity={jobTargetCapacity}
            enabledAccountCount={enabledAccountCount}
            config={config || agent?.config || DEFAULT_CONFIG}
            tsBufferDays={tsBufferDays}
            setTsBufferDays={setTsBufferDays}
            tsPastedText={tsPastedText}
            setTsPastedText={setTsPastedText}
            tsParsedPaste={tsParsedPaste}
            tsAvailable={tsAvailable}
            tsStoredInSource={tsStoredInSource}
            tsAlreadyTouched={tsAlreadyTouched}
            tsClaimedInSource={tsClaimedInSource}
            tsDailyCap={tsDailyCap}
            tsDesiredBuffer={tsDesiredBuffer}
            tsStatsLoading={tsStatsLoading}
            tsLoadingAvailable={tsLoadingAvailable}
            tsReserveHint={tsReserveHint}
            tsFollowModelId={tsFollowModelId}
            setTsFollowModelId={setTsFollowModelId}
            followModels={followModels}
            fbfEnabled={fbfEnabled}
            setFbfEnabled={setFbfEnabled}
            fbfSourceModelId={fbfSourceModelId}
            setFbfSourceModelId={setFbfSourceModelId}
            fbfMinAgeDays={fbfMinAgeDays}
            setFbfMinAgeDays={setFbfMinAgeDays}
            tsSaving={tsSaving}
            scrapeSessions={scrapeSessions}
            scrapeV2Sessions={scrapeV2Sessions}
            scrapeTags={scrapeTags}
            scrapeV2Categories={scrapeV2Categories}
            loadPastedTargets={loadPastedTargets}
            onSave={saveTargetSource}
            serverTargetSource={agent.targetSource}
            serverFollowModelId={agent.followModelId}
          />
        </div>
      )}

      <div className="px-5 pb-5">
        <AttachedAccountsPanel
          agentId={agent._id}
          agentOnline={Boolean(agent.online)}
          accounts={agent.accounts || []}
          accountCount={agent.accountCount || 0}
          disabledIds={disabledAccountIds}
          filter={accountFilter}
          onFilterChange={setAccountFilter}
          saving={accountSelectionSaving}
          onToggle={toggleAccountEnabled}
          onSelectAll={selectAllAccounts}
          onDeselectAll={deselectAllAccounts}
          onDetach={detachAccount}
          onAttach={() => setShowAttach(true)}
          onChanged={onChanged}
        />
      </div>
      </div>{/* /configure panel */}

      {/* ── Modules (live edit — no stop required) ── */}
      <div className="px-5 pt-4">
        <AgentModulesPanel agent={agent} onChanged={onChanged} />
      </div>

      {/* ── Daily posts status (Model Auto Posts via GoLogin) ── */}
      <div className="px-5 pt-4">
        <AgentDailyPostsPanel agent={agent} />
      </div>

      {/* ── Automation view (always visible) ── */}
      <div>
      <div className="px-5 py-5 space-y-5">
        {agent?.automationType === 'warmup' ? (
          <WarmupControlPanel
            agent={agent}
            onStartWarmup={() => setShowWarmupStartModal(true)}
            onChanged={onChanged}
          />
        ) : (
        <>
        {/* ── Create / manage job card ── */}
        <CreateJobCard
          agent={agent}
          config={config || agent?.config || DEFAULT_CONFIG}
          onConfigSave={saveConfigDirect}
          tsAvailable={sourceStats.available}
          tsStoredInSource={sourceStats.stored}
          tsAlreadyTouched={sourceStats.touched}
          tsClaimedInSource={sourceStats.claimed}
          tsUnclaimedInSource={sourceStats.unclaimed}
          tsStatsLoading={sourceStats.loading}
          tsLoadingAvailable={sourceStats.loadingAvailable}
          tsReserveHint={sourceStats.reserveHint}
          tsDailyCap={tsDailyCap}
          tsDesiredBuffer={tsDesiredBuffer}
          enabledAccountCount={enabledAccountCount}
          jobTargetCapacity={jobTargetCapacity}
          onDispatch={dispatchJob}
          onPause={pauseJob}
          onStop={stopJob}
          onResume={resumeJob}
          onEditSetup={() => setConfigureOpen(true)}
          customTargetsText={customTargetsText}
          setCustomTargetsText={setCustomTargetsText}
          parsedCustomTargets={parsedCustomTargets}
          useTargetPool={useTargetPool}
          setUseTargetPool={setUseTargetPool}
        />

        {/* Daily-cap auto-continuation countdown — shown whenever the
            scheduler has a future dispatch queued for this agent. */}
        <AutoContinuationCard
          agent={agent}
          job={liveJob || jobs[0] || null}
          tsAvailable={tsAvailable}
          tsStoredInSource={tsStoredInSource}
          tsAlreadyTouched={tsAlreadyTouched}
          onStopAutomation={async () => {
            // 1. Force-cancel the current running job (releases reserved targets)
            const jobId = agent?.currentJobId || liveJob?._id || jobs[0]?._id;
            if (jobId) {
              try {
                await api.post(`/admin/va/ai-agents/${agent._id}/jobs/${jobId}/force-cancel`);
              } catch (_) { /* non-fatal — proceed to disable */ }
            }
            // 2. Disable auto-continuation + clear accountSchedule
            try {
              await api.patch(`/admin/va/ai-agents/${agent._id}/auto-continuation`, { enabled: false });
              toast.success('Automation stopped — reserved targets released. Click "Create Automation" to start again.');
              onChanged();
              loadJobs();
            } catch (err) {
              toast.error(err.response?.data?.error || 'Failed to stop automation');
            }
          }}
          onEnable={enableAutoContinuation}
          onCreateAutomation={() => setShowTypeSelector(true)}
          onForceComplete={async () => {
            if (!agent?.currentJobId) return;
            try {
              const d = await api.post(`/admin/va/ai-agents/${agent._id}/jobs/${agent.currentJobId}/force-complete`);
              if (d.success) {
                toast.success(d.dispatched
                  ? 'Job unstuck — next wave dispatching now.'
                  : 'Job unstuck — next wave dispatches within ~60s.');
              } else {
                toast.error(`Force-complete failed: ${d.error}`);
              }
            } catch (e) {
              toast.error(`Force-complete error: ${e.message}`);
            }
          }}
          onResetCaps={async () => {
            try {
              const res = await api.post(`/admin/va/ai-agents/${agent._id}/reset-caps`);
              const d = res.data || {};
              if (d.success) {
                if (d.capped === 0) {
                  toast.success('No accounts are on daily cap right now.');
                } else if (d.requeued > 0) {
                  toast.success(`Caps reset for ${d.capped} account(s) — ${d.requeued} requeued with fresh budgets, following resumes now.`);
                } else {
                  toast.success(`Caps reset for ${d.capped} account(s) — they're due now and start with the next dispatch (~60s when idle, or after resume if paused).`);
                }
                onChanged();
                loadJobs();
              } else {
                toast.error(`Reset caps failed: ${d.error || 'unknown error'}`);
              }
            } catch (e) {
              toast.error(e.response?.data?.error || `Reset caps error: ${e.message}`);
            }
          }}
        />

        {/* Live per-account view of the most recent job */}
        <LiveJobPanel
          job={liveJob || jobs[0] || null}
          agent={agent}
          onAddAccounts={addAccountsToLiveRun}
          addingAccounts={addingLiveRunAccounts}
        />

        {/* Job history */}
        {(() => {
          const ORPHAN_MSGS = ['Orphaned duplicate', 'Duplicate dispatch'];
          const visibleJobs = jobs.filter(
            (j) => !(j.status === 'cancelled' && j.error && ORPHAN_MSGS.some((m) => j.error.includes(m)))
          );
          const hiddenCount = jobs.length - visibleJobs.length;
          return (
            <section className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <ChartBarIcon className="w-4 h-4" /> Recent jobs
                {visibleJobs.length > 0 && (
                  <span className="text-xs font-normal text-gray-400">({visibleJobs.length})</span>
                )}
                {hiddenCount > 0 && (
                  <span className="text-xs font-normal text-gray-400 italic">· {hiddenCount} duplicate(s) auto-cancelled</span>
                )}
              </h4>
              {visibleJobs.length === 0 ? (
                <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-xl px-3 py-6 text-center text-sm text-gray-500">
                  No jobs yet — click Start above to dispatch the first job.
                </div>
              ) : (
                <div className="space-y-1 max-h-[20rem] overflow-y-auto">
                  {visibleJobs.map((job) => (
                    <RecentJobRow
                      key={job._id}
                      job={job}
                      agent={agent}
                      onContinue={() => continueJob(job._id)}
                      onEdit={() => setEditingJobId(job._id)}
                      onForceStop={() => forceStopJob(job._id)}
                    />
                  ))}
                </div>
              )}
            </section>
          );
        })()}
        </>
        )}
      </div>
      </div>{/* /automation view */}

      {showAttach && (
        <AttachAccountsModal
          agent={agent}
          vaUsers={vaUsers}
          onClose={() => setShowAttach(false)}
          onAttached={async () => {
            setShowAttach(false);
            onChanged();
          }}
        />
      )}
      {editingJobId && (
        <EditJobModal
          agent={agent}
          jobId={editingJobId}
          onClose={() => setEditingJobId(null)}
          onDispatched={() => {
            setEditingJobId(null);
            loadJobs();
            onChanged();
          }}
        />
      )}
      {showCreateModal && (
        <CreateAutomationModal
          agent={agent}
          scrapeSessions={scrapeSessions}
          scrapeV2Sessions={scrapeV2Sessions}
          scrapeV2Categories={scrapeV2Categories}
          enabledAccountCount={enabledAccountCount}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            onChanged();
            loadJobs();
          }}
        />
      )}
      {showTypeSelector && (
        <AutomationTypeSelectorModal
          onFollow={() => { setShowTypeSelector(false); setShowCreateModal(true); }}
          onWarmup={() => { setShowTypeSelector(false); setShowWarmupStartModal(true); }}
          onClose={() => setShowTypeSelector(false)}
        />
      )}
      {showWarmupStartModal && (
        <WarmupStartModal
          agent={agent}
          onClose={() => setShowWarmupStartModal(false)}
          onSuccess={() => {
            setShowWarmupStartModal(false);
            onChanged();
          }}
        />
      )}
    </div>
  );
}

/**
 * Modal for selecting the automation type before creating.
 */
function AutomationTypeSelectorModal({ onFollow, onWarmup, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Create Automation</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">What kind of automation do you want to run?</p>
        <div className="space-y-3">
          <button
            type="button"
            onClick={onFollow}
            className="w-full flex items-start gap-4 p-4 rounded-xl border-2 border-emerald-200 dark:border-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors text-left"
          >
            <div className="flex-shrink-0 mt-0.5 w-9 h-9 rounded-xl bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
              <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <div>
              <div className="font-semibold text-gray-900 dark:text-white">Follow Automation</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Mass-follow target users from the Scraper V2 pool. Best for growing the funnel.</div>
            </div>
          </button>
          <button
            type="button"
            onClick={onWarmup}
            className="w-full flex items-start gap-4 p-4 rounded-xl border-2 border-amber-200 dark:border-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors text-left"
          >
            <div className="flex-shrink-0 mt-0.5 w-9 h-9 rounded-xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
              <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <div className="font-semibold text-gray-900 dark:text-white">Warmup Runner</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Browse the For You feed with humanised delays to warm up accounts before follow campaigns.</div>
            </div>
          </button>
        </div>
        <div className="mt-5 flex justify-end">
          <button type="button" onClick={onClose} className="btn-secondary text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Warmup runner control panel — start/stop + live status for warmup agents.
 */
function WarmupControlPanel({ agent, onStartWarmup, onChanged }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stopping, setStopping] = useState(false);
  const [switchingToFollow, setSwitchingToFollow] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!agent?._id) return;
    try {
      const res = await api.get(`/admin/va/ai-agents/${agent._id}/warmup/status`);
      if (res.data?.success) setStatus(res.data);
    } catch (_) {
      /* tolerate poll failures */
    } finally {
      setLoading(false);
    }
  }, [agent?._id]);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 10_000);
    return () => clearInterval(t);
  }, [fetchStatus]);

  const handleStop = async () => {
    setStopping(true);
    try {
      await api.post(`/admin/va/ai-agents/${agent._id}/warmup/stop`, {});
      toast.success('Warmup stopped — desktop agent will not launch new browsers');
      await fetchStatus();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to stop warmup');
    } finally {
      setStopping(false);
    }
  };

  const handleSwitchToFollow = async () => {
    if (!window.confirm('Switch this agent back to Follow Automation? The warmup progress records are kept — you can restart warmup any time.')) return;
    setSwitchingToFollow(true);
    try {
      await api.patch(`/admin/va/ai-agents/${agent._id}`, { automationType: 'follow' });
      toast.success('Switched to Follow Automation');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to switch automation type');
    } finally {
      setSwitchingToFollow(false);
    }
  };

  const isRunning = status?.isRunning || agent?.status === 'running';
  const prog = status?.progress || {};
  const session = status?.session;
  const cfg = session?.config || {};

  return (
    <section className="rounded-2xl border border-amber-200 dark:border-amber-800/60 bg-amber-50/50 dark:bg-amber-950/20 p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            Warmup Runner
            {isRunning ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> Running
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                Stopped
              </span>
            )}
          </h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-xl">
            Runs once per day per account. The desktop agent dispatches batches when online —
            click <strong>Stop Warmup</strong> to pause; already-open browsers finish their current session.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isRunning ? (
            <button
              type="button"
              onClick={handleStop}
              disabled={stopping}
              className="inline-flex items-center gap-1.5 !py-2 !px-3 text-sm rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40 font-medium disabled:opacity-40"
            >
              <StopIcon className="w-4 h-4" />
              {stopping ? 'Stopping…' : 'Stop Warmup'}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={handleSwitchToFollow}
                disabled={switchingToFollow || isRunning}
                title="Switch this agent back to Follow Automation"
                className="inline-flex items-center gap-1.5 !py-2 !px-3 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium disabled:opacity-40"
              >
                {switchingToFollow ? 'Switching…' : 'Switch to Follow'}
              </button>
              <button
                type="button"
                onClick={onStartWarmup}
                disabled={gologinTokenBlocksStart(agent)}
                className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-40"
              >
                <PlayIcon className="w-4 h-4" />
                {prog.paused > 0 ? 'Resume Warmup' : 'Start Warmup'}
              </button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-xs text-gray-400">Loading status…</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-2.5 text-center">
            <div className="text-lg font-bold text-indigo-600 dark:text-indigo-400 tabular-nums">{prog.active ?? 0}</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">Active</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-2.5 text-center">
            <div className="text-lg font-bold text-amber-600 dark:text-amber-400 tabular-nums">{prog.paused ?? 0}</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">Paused</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-2.5 text-center">
            <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">{prog.completed ?? 0}</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">Completed</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-2.5 text-center">
            <div className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">{cfg.maxConcurrent ?? '—'}</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">Concurrent</div>
          </div>
        </div>
      )}

      {session && (
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          Phase 1: {cfg.phase1Days ?? 7} day{(cfg.phase1Days ?? 7) !== 1 ? 's' : ''}
          {(cfg.phase2Days ?? 0) > 0 && ` · Phase 2: ${cfg.phase2Days} day${cfg.phase2Days !== 1 ? 's' : ''}`}
          {cfg.browseMinMs && cfg.browseMaxMs && (
            <> · Browse {Math.round(cfg.browseMinMs / 60_000)}–{Math.round(cfg.browseMaxMs / 60_000)} min</>
          )}
        </p>
      )}

      {!isRunning && prog.active === 0 && prog.paused === 0 && prog.completed === 0 && (
        <p className="text-xs text-gray-500">No warmup program configured yet — click Start Warmup to begin.</p>
      )}
    </section>
  );
}

/**
 * Modal for starting a warmup session — with per-account selection.
 */
function WarmupStartModal({ agent, onClose, onSuccess }) {
  const [browseMinMin, setBrowseMinMin] = useState(12);
  const [browseMaxMin, setBrowseMaxMin] = useState(15);
  const [maxConcurrent, setMaxConcurrent] = useState(3);
  const [phase1Days, setPhase1Days] = useState(7);
  const [includePhase2, setIncludePhase2] = useState(false);
  const [phase2Days, setPhase2Days] = useState(7);
  const [minLikes, setMinLikes] = useState(1);
  const [maxLikes, setMaxLikes] = useState(2);
  const [loading, setLoading] = useState(false);
  const [accountSearch, setAccountSearch] = useState('');

  // Build list of enabled accounts from the detail-fetched agent.accounts array.
  const disabledSet = useMemo(() =>
    new Set((agent?.disabledAccountIds || []).map(String)),
  [agent?.disabledAccountIds]);

  const enabledAccounts = useMemo(() => {
    const accounts = agent?.accounts || [];
    return accounts.filter((a) => {
      if (disabledSet.has(String(a._id))) return false;
      const st = String(a.status || '').toLowerCase();
      if (st === 'captcha' || st === 'suspended' || st === 'auth_expired') return false;
      return true;
    });
  }, [agent?.accounts, disabledSet]);

  // Default: all enabled accounts selected.
  const [selectedIds, setSelectedIds] = useState(() =>
    new Set(enabledAccounts.map((a) => String(a._id)))
  );

  // Sync when enabledAccounts resolves (agent prop arrives after first render).
  const prevCountRef = React.useRef(0);
  React.useEffect(() => {
    if (enabledAccounts.length !== prevCountRef.current) {
      prevCountRef.current = enabledAccounts.length;
      setSelectedIds(new Set(enabledAccounts.map((a) => String(a._id))));
    }
  }, [enabledAccounts]);

  const filteredAccounts = useMemo(() => {
    const q = accountSearch.trim().toLowerCase().replace(/^@/, '');
    if (!q) return enabledAccounts;
    return enabledAccounts.filter((a) =>
      (a.username || '').toLowerCase().includes(q)
    );
  }, [enabledAccounts, accountSearch]);

  const allFilteredSelected = filteredAccounts.length > 0 &&
    filteredAccounts.every((a) => selectedIds.has(String(a._id)));

  const toggleAccount = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allFilteredSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filteredAccounts.forEach((a) => next.delete(String(a._id)));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filteredAccounts.forEach((a) => next.add(String(a._id)));
        return next;
      });
    }
  };

  const handleStart = async () => {
    if (!selectedIds.size) return;
    setLoading(true);
    try {
      await api.post(`/admin/va/ai-agents/${agent._id}/warmup/start`, {
        browseMinMs: browseMinMin * 60_000,
        browseMaxMs: browseMaxMin * 60_000,
        maxConcurrent: Number(maxConcurrent),
        phase1Days: Number(phase1Days),
        phase2Days: includePhase2 ? Number(phase2Days) : 0,
        minLikes: Number(minLikes),
        maxLikes: Number(maxLikes),
        selectedAccountIds: Array.from(selectedIds),
      });
      toast.success(`Warmup started for ${selectedIds.size} account${selectedIds.size !== 1 ? 's' : ''}`);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start warmup');
    } finally {
      setLoading(false);
    }
  };

  const STATUS_DOT = {
    active:       'bg-emerald-500',
    suspended:    'bg-red-500',
    auth_expired: 'bg-amber-400',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-6 pb-4 flex-shrink-0">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Start Warmup</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Opens GoLogin browsers, browses the For You feed with humanised delays, then closes.
          </p>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-6 space-y-4 pb-2">
          {/* Browse timing + concurrency */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Browse min (min)</label>
              <input type="number" min={1} max={60} value={browseMinMin} onChange={(e) => setBrowseMinMin(Number(e.target.value))} className="form-input text-sm w-full" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Browse max (min)</label>
              <input type="number" min={1} max={60} value={browseMaxMin} onChange={(e) => setBrowseMaxMin(Number(e.target.value))} className="form-input text-sm w-full" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Concurrent</label>
              <input type="number" min={1} max={200} value={maxConcurrent} onChange={(e) => setMaxConcurrent(Number(e.target.value))} className="form-input text-sm w-full" />
            </div>
          </div>

          {/* Phase schedule */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden divide-y divide-gray-200 dark:divide-gray-700">
            {/* Phase 1 */}
            <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">Phase 1 — Browse only</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Opens browser, scrolls the For You feed with humanised timing. Runs once per day per account.</div>
                </div>
                <div className="flex-shrink-0 flex items-center gap-2">
                  <input
                    type="number" min={1} max={365} value={phase1Days}
                    onChange={(e) => setPhase1Days(Math.max(1, Number(e.target.value)))}
                    className="form-input text-sm w-20 text-center"
                  />
                  <span className="text-xs text-gray-500 whitespace-nowrap">days</span>
                </div>
              </div>
            </div>

            {/* Phase 2 toggle */}
            <div className={`p-3 ${includePhase2 ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-gray-50 dark:bg-gray-900/20'}`}>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={includePhase2} onChange={(e) => setIncludePhase2(e.target.checked)} className="rounded" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">Phase 2 — Browse + Like posts</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">After Phase 1 completes, accounts continue scrolling and like 1–2 posts per session. Deeper engagement signal.</div>
                </div>
                {includePhase2 && (
                  <div className="flex-shrink-0 flex items-center gap-2">
                    <input
                      type="number" min={1} max={365} value={phase2Days}
                      onChange={(e) => setPhase2Days(Math.max(1, Number(e.target.value)))}
                      className="form-input text-sm w-20 text-center"
                    />
                    <span className="text-xs text-gray-500 whitespace-nowrap">days</span>
                  </div>
                )}
              </label>
              {includePhase2 && (
                <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-amber-200 dark:border-amber-700">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Min likes / session</label>
                    <input type="number" min={1} max={10} value={minLikes} onChange={(e) => setMinLikes(Number(e.target.value))} className="form-input text-sm w-full" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Max likes / session</label>
                    <input type="number" min={1} max={10} value={maxLikes} onChange={(e) => setMaxLikes(Number(e.target.value))} className="form-input text-sm w-full" />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Account selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                Accounts — <span className="text-indigo-600 dark:text-indigo-400">{selectedIds.size}</span> / {enabledAccounts.length} selected
              </label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setSelectedIds(new Set(enabledAccounts.map((a) => String(a._id))))}
                  className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline">All</button>
                <button type="button" onClick={() => setSelectedIds(new Set())}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:underline">None</button>
              </div>
            </div>
            <input
              type="text"
              placeholder="Filter by @username…"
              value={accountSearch}
              onChange={(e) => setAccountSearch(e.target.value)}
              className="form-input text-sm w-full mb-2"
            />
            {enabledAccounts.length === 0 ? (
              <p className="text-xs text-gray-500 text-center py-3">No enabled accounts on this agent.</p>
            ) : (
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                {/* Select all row for filtered view */}
                {filteredAccounts.length > 1 && (
                  <label className="flex items-center gap-3 px-3 py-2 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700 cursor-pointer text-xs font-medium text-gray-600 dark:text-gray-400">
                    <input type="checkbox" checked={allFilteredSelected} onChange={toggleAll} className="rounded" />
                    Select all {accountSearch ? `${filteredAccounts.length} matching` : 'accounts'}
                  </label>
                )}
                <div className="max-h-48 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
                  {filteredAccounts.length === 0 ? (
                    <p className="text-xs text-gray-500 text-center py-3">No accounts match</p>
                  ) : (
                    filteredAccounts.map((acc) => {
                      const id = String(acc._id);
                      return (
                        <label key={id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors">
                          <input type="checkbox" checked={selectedIds.has(id)} onChange={() => toggleAccount(id)} className="rounded flex-shrink-0" />
                          <span className={`h-2 w-2 rounded-full flex-shrink-0 ${STATUS_DOT[acc.status] || 'bg-gray-400'}`} title={acc.status} />
                          <span className="text-sm text-gray-900 dark:text-white font-medium truncate flex-1">
                            @{acc.username || id.slice(-6)}
                          </span>
                          {acc.status !== 'active' && (
                            <span className="text-[10px] text-gray-400 flex-shrink-0">{acc.status}</span>
                          )}
                          {!acc.gologinProfileId && (
                            <span className="text-[10px] text-amber-500 flex-shrink-0">no GL profile</span>
                          )}
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 pt-4 flex-shrink-0 flex justify-end gap-2 border-t border-gray-100 dark:border-gray-700">
          <button type="button" onClick={onClose} disabled={loading} className="btn-secondary text-sm">Cancel</button>
          <button type="button" onClick={handleStart} disabled={loading || !selectedIds.size} className="btn-primary text-sm">
            {loading ? 'Starting…' : `Start Warmup (${selectedIds.size} account${selectedIds.size !== 1 ? 's' : ''})`}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Setup-tab account picker. Every attached account is rendered with a
 * checkbox so the operator can choose which subset will participate in
 * the next /start (or /resume / /continue) without having to detach.
 *
 * - Defaults to "all selected" — `disabledIds` is the deselect set
 *   (matches the server's `disabledAccountIds` field).
 * - Includes Select all / Deselect all controls and a quick @handle
 *   filter for big rosters.
 * - Detach is still available per-row for permanent removal.
 *
 * Wiring (parent owns the state):
 *   - `disabledIds: Set<string>` — current deselect set
 *   - `onToggle(id, nextEnabled)`
 *   - `onSelectAll()` / `onDeselectAll()` — flip the whole roster
 *   - `saving: boolean` — disables controls while the PATCH is in flight
 */
/**
 * Modal for creating a new automation from scratch.
 * Opens when the user clicks "Create Automation" after stopping.
 * Saves config + source atomically on "Start Automation" — no separate
 * "Save config" / "Save source" buttons needed.
 */
function CreateAutomationModal({
  agent,
  scrapeSessions,
  scrapeV2Sessions,
  scrapeV2Categories = [],
  enabledAccountCount,
  onClose,
  onSuccess,
}) {
  const ts = agent?.targetSource || {};
  const [tsType, setTsType] = useState(
    ts.type && ts.type !== 'paste' ? ts.type : 'scrape_v2_pool'
  );
  const [scrapeSessionId, setScrapeSessionId] = useState(ts.scrapeSessionId || '');
  const [scrapeTag, setScrapeTag] = useState(ts.scrapeTag || '');
  const [followModelId, setFollowModelId] = useState(agent?.followModelId || '');
  const [followModels, setFollowModels] = useState([]);
  const savedFbf = ts.followBacksFirst || {};
  const [fbfEnabled, setFbfEnabled] = useState(Boolean(savedFbf.enabled));
  const [fbfSourceModelId, setFbfSourceModelId] = useState(
    savedFbf.sourceModelId ? String(savedFbf.sourceModelId) : ''
  );
  const [fbfMinAgeDays, setFbfMinAgeDays] = useState(Number(savedFbf.minAgeDays) || 7);
  const [localConfig, setLocalConfig] = useState({ ...DEFAULT_CONFIG, ...(agent?.config || {}) });
  const [saving, setSaving] = useState(false);
  const [previewAvailable, setPreviewAvailable] = useState(null);
  const [previewStored, setPreviewStored] = useState(null);
  const [previewTouched, setPreviewTouched] = useState(null);
  const [previewClaimed, setPreviewClaimed] = useState(null);
  const [previewUnclaimed, setPreviewUnclaimed] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewLoadingAvailable, setPreviewLoadingAvailable] = useState(false);
  const [previewReserveHint, setPreviewReserveHint] = useState(null);

  const accountsInRun = enabledAccountCount
    ?? Math.max(0, (agent?.accountCount || 0) - (agent?.disabledAccountIds?.length || 0));
  const followCapacity = computeJobTargetCapacity(localConfig, accountsInRun);
  const selectedModelName = followModels.find((m) => String(m._id) === String(followModelId))?.name;

  const liveSessions = (scrapeSessions || []).filter(
    (s) => s.status === 'running' || s.status === 'paused'
  );
  const storedSessions = (scrapeSessions || []).filter(
    (s) => s.status === 'completed' || s.status === 'stopped'
  );

  useEffect(() => {
    if (agent?.followModelId) {
      setFollowModelId(String(agent.followModelId));
    }
  }, [agent?.followModelId]);

  useEffect(() => {
    if (!agent?._id) return undefined;
    let cancelled = false;
    api.get(`/admin/va/ai-agents/${agent._id}/follow-models`)
      .then((res) => {
        if (cancelled || !res.data?.success) return;
        setFollowModels(res.data.models || []);
        if (!followModelId && res.data.selectedModelId) {
          setFollowModelId(res.data.selectedModelId);
        }
      })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, [agent?._id]);

  useEffect(() => {
    if (fbfSourceModelId && followModelId && String(fbfSourceModelId) === String(followModelId)) {
      setFbfSourceModelId('');
    }
  }, [followModelId, fbfSourceModelId]);

  const liveV2Sessions = (scrapeV2Sessions || []).filter(
    (s) => s.status === 'running' || s.status === 'paused'
  );
  const storedV2Sessions = (scrapeV2Sessions || []).filter(
    (s) => s.status === 'completed' || s.status === 'stopped'
  );

  useEffect(() => {
    if (!agent?._id || !followModelId) {
      setPreviewAvailable(null);
      setPreviewStored(null);
      setPreviewTouched(null);
      setPreviewClaimed(null);
      setPreviewUnclaimed(null);
      return undefined;
    }
    if (needsScrapeSessionPick(tsType) && !scrapeSessionId) {
      setPreviewAvailable(null);
      setPreviewStored(null);
      setPreviewTouched(null);
      setPreviewClaimed(null);
      setPreviewUnclaimed(null);
      return undefined;
    }
    if (tsType !== 'scrape_v2_pool' && !isLinkedScrapeSessionType(tsType) && tsType !== 'scrape_tag') {
      setPreviewAvailable(null);
      setPreviewStored(null);
      setPreviewTouched(null);
      setPreviewClaimed(null);
      setPreviewUnclaimed(null);
      return undefined;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewLoadingAvailable(true);
    setPreviewReserveHint(null);
    const params = {
      followModelId,
      sourceType: tsType,
      workspaceId: agent?.targetSource?.workspaceId || 'legacy',
    };
    if (needsScrapeSessionPick(tsType)) params.scrapeSessionId = scrapeSessionId;
    if (tsType === 'scrape_v2_pool' && scrapeTag) params.scrapeTag = scrapeTag;

      const applyPartial = (partial) => {
        if (cancelled) return;
        if (typeof partial.storedInSource === 'number') setPreviewStored(partial.storedInSource);
        if (typeof partial.alreadyTouchedInSource === 'number') setPreviewTouched(partial.alreadyTouchedInSource);
        if (typeof partial.claimedInSource === 'number') setPreviewClaimed(partial.claimedInSource);
        if (typeof partial.unclaimedInSource === 'number') setPreviewUnclaimed(partial.unclaimedInSource);
        if (typeof partial.available === 'number') {
          setPreviewAvailable(partial.available);
          setPreviewLoadingAvailable(false);
        }
        setPreviewReserveHint(formatPoolReserveHint(partial));
      };

    fetchTargetSourceAvailability(agent._id, params, { onProgress: applyPartial, blocking: true })
      .then((data) => {
        if (cancelled || !data) return;
        applyPartial(data);
        if (typeof data.available === 'number') {
          setPreviewAvailable(data.available);
          setPreviewLoadingAvailable(false);
        }
      })
      .catch(() => { /* non-fatal */ })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => { cancelled = true; };
  }, [agent?._id, tsType, scrapeSessionId, scrapeTag, followModelId]);

  const handleStart = async () => {
    if (!followModelId) {
      toast.error('Select a follow model first (e.g. Amy)');
      return;
    }
    if (isScrapeV2PoolType(tsType) && fbfEnabled) {
      if (!fbfSourceModelId) {
        toast.error('Pick the model whose follow-backs to take, or turn follow-backs-first off');
        return;
      }
      if (String(fbfSourceModelId) === String(followModelId)) {
        toast.error('Follow-backs-first has to use a different model than this agent follows as');
        return;
      }
    }
    if (needsScrapeSessionPick(tsType) && !scrapeSessionId) {
      toast.error('Select a scrape session first');
      return;
    }
    setSaving(true);
    try {
      // Single round-trip: /start persists config/source/model and prepares targets async.
      const res = await api.post(`/admin/va/ai-agents/${agent._id}/start`, {
        useTargetPool: false,
        targets: [],
        followModelId,
        config: localConfig,
        targetSource: {
          type: tsType,
          scrapeSessionId: isLinkedScrapeSessionType(tsType) ? (scrapeSessionId || null) : null,
          scrapeTag: tsType === 'scrape_v2_pool' ? (scrapeTag || null) : null,
          bufferDays: 1,
          ...(isScrapeV2PoolType(tsType) ? {
            followBacksFirst: {
              enabled: Boolean(fbfEnabled),
              sourceModelId: fbfSourceModelId || null,
              minAgeDays: (() => {
                const n = Math.round(Number(fbfMinAgeDays));
                if (!Number.isFinite(n)) return 7;
                return Math.max(1, Math.min(90, n));
              })(),
            },
          } : {}),
        },
        autoContinuation: { enabled: true }
      });
      if (res.data?.success) {
        toast.success(
          res.data?.preparing
            ? 'Preparing targets — automation will start when the queue is ready'
            : 'Automation started — accounts will follow on individual 24h cycles'
        );
        onSuccess();
      } else {
        toast.error(res.data?.error || 'Failed to start automation');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start automation');
    } finally {
      setSaving(false);
    }
  };


  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={() => !saving && onClose()}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Create Automation</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Configure your follow automation — all settings are saved automatically when you start.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-40"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* ── Follow model ── */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
              Follow model
            </h3>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
              Dedup is per model — Vanessa can follow the same handles Amy already followed.
              Two agents on the same model never overlap targets.
            </p>
            <select
              value={followModelId}
              onChange={(e) => setFollowModelId(e.target.value)}
              className="form-select w-full text-sm"
            >
              <option value="">— select model —</option>
              {followModels.map((m) => (
                <option key={m._id} value={m._id}>{m.name}</option>
              ))}
            </select>
            {!followModelId && (
              <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-2">
                Required — pick the dashboard model these follows represent.
              </p>
            )}
          </section>

          {/* ── Target source ── */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-1.5">
              <BoltIcon className="w-4 h-4 text-indigo-500" />
              Target source
            </h3>
            <div className="flex gap-2 mb-3">
              <button
                type="button"
                onClick={() => { setTsType('scrape_v2_pool'); setScrapeSessionId(''); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  tsType === 'scrape_v2_pool'
                    ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300'
                    : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:border-gray-300'
                }`}
              >
                Scraper V2 pool
              </button>
              <button
                type="button"
                onClick={() => { setTsType('paste'); setScrapeSessionId(''); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  tsType === 'paste'
                    ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300'
                    : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:border-gray-300'
                }`}
              >
                Paste list
              </button>
            </div>

            {isScrapeV2PoolType(tsType) && followModelId && (
              <div className="mb-3 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Data category
                  </label>
                  <select
                    value={scrapeTag}
                    onChange={(e) => setScrapeTag(e.target.value)}
                    className="form-select w-full text-sm"
                  >
                    <option value="">All categories</option>
                    {(scrapeV2Categories || []).map((c) => (
                      <option key={c.id || c.name} value={c.name}>
                        {c.name}
                        {typeof c.filteredCount === 'number' ? ` (${Number(c.filteredCount).toLocaleString()} verified)` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-[11px] text-gray-500">
                    Only pull verified V2 handles tagged with this category. Ledger dedup and follow-backs stay model-wide.
                  </p>
                </div>
                <SourceAvailabilityStats
                  available={previewAvailable}
                  stored={previewStored}
                  followed={previewTouched}
                  claimed={previewClaimed}
                  unclaimed={previewUnclaimed}
                  showClaimed
                  poolMode
                  categoryFilter={scrapeTag || null}
                  loadingAvailable={previewLoadingAvailable}
                  loadingFollowed={previewLoading && previewTouched == null}
                  loadingStored={previewLoading && previewStored == null}
                  loadingClaimed={previewLoading && previewClaimed == null}
                  reserveHint={previewReserveHint}
                />
              </div>
            )}

            {isScrapeV2PoolType(tsType) && (
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
                Global filtered pool — all V2 scrape sessions feed this. Per-model dedup at dispatch.
              </p>
            )}

            {isScrapeV2PoolType(tsType) && (
              <div className="mb-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="form-checkbox h-3.5 w-3.5"
                    checked={fbfEnabled}
                    onChange={(e) => setFbfEnabled(e.target.checked)}
                  />
                  <span className="text-sm text-gray-800 dark:text-gray-100">
                    Follow another model&apos;s follow-backs first
                  </span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="block text-xs space-y-1">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Source model</span>
                    <select
                      value={fbfSourceModelId}
                      disabled={!fbfEnabled}
                      onChange={(e) => setFbfSourceModelId(e.target.value)}
                      className="form-select w-full text-sm"
                    >
                      <option value="">— select model —</option>
                      {followModels
                        .filter((m) => String(m._id) !== String(followModelId))
                        .map((m) => (
                          <option key={m._id} value={m._id}>{m.name}</option>
                        ))}
                    </select>
                  </label>
                  <label className="block text-xs space-y-1">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Minimum age (days)</span>
                    <input
                      type="number"
                      min={1}
                      max={90}
                      disabled={!fbfEnabled}
                      value={fbfMinAgeDays}
                      onChange={(e) => setFbfMinAgeDays(e.target.value)}
                      className="form-input w-full text-sm"
                    />
                  </label>
                </div>
                <p className="text-[11px] text-gray-500">
                  Users who followed the source model back are dispatched first, once they
                  are at least this many days old. Anyone this model already followed is skipped.
                </p>
              </div>
            )}

            {needsScrapeSessionPick(tsType) && (
              <div className="space-y-2">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                  {tsType === 'scrape_v2_session' ? 'Scraper V2 session' : 'Legacy scrape session'}
                </label>
                <select
                  value={scrapeSessionId}
                  onChange={(e) => setScrapeSessionId(e.target.value)}
                  className="form-select w-full text-sm"
                >
                  <option value="">— select a session —</option>
                  {tsType === 'scrape_v2_session' ? (
                    <>
                      {liveV2Sessions.length > 0 && (
                        <optgroup label="Scraper V2 — live">
                          {liveV2Sessions.map((s) => (
                            <option key={s.sessionId} value={s.sessionId}>
                              {scrapeSessionOptionLabel(s)}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {storedV2Sessions.length > 0 && (
                        <optgroup label="Scraper V2 — completed">
                          {storedV2Sessions.map((s) => (
                            <option key={s.sessionId} value={s.sessionId}>
                              {scrapeSessionOptionLabel(s)}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </>
                  ) : (
                    <>
                      {liveSessions.length > 0 && (
                        <optgroup label="Legacy — live">
                          {liveSessions.map((s) => (
                            <option key={s.sessionId} value={s.sessionId}>
                              {scrapeSessionOptionLabel(s)}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {storedSessions.length > 0 && (
                        <optgroup label="Legacy — completed">
                          {storedSessions.map((s) => (
                            <option key={s.sessionId} value={s.sessionId}>
                              {scrapeSessionOptionLabel(s)}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </>
                  )}
                </select>
                {tsType === 'scrape_v2_session' && scrapeV2Sessions.length === 0 && (
                  <p className="text-[11px] text-amber-600 dark:text-amber-400">
                    No Scraper V2 sessions found — create one under Dashboard → Scraper V2 first.
                  </p>
                )}
                {tsType === 'scrape_session' && scrapeSessions.length === 0 && (
                  <p className="text-[11px] text-amber-600 dark:text-amber-400">
                    No legacy scrape sessions found for this agent&apos;s owner.
                  </p>
                )}
                {!scrapeSessionId && (
                  <p className="text-[11px] text-amber-600 dark:text-amber-400">
                    ⚠ Select a scrape session to enable dedup-aware follow tracking
                  </p>
                )}
                {scrapeSessionId && (
                  <div className="mt-3">
                    <SourceAvailabilityStats
                      available={previewAvailable}
                      stored={previewStored}
                      followed={previewTouched}
                      loadingAvailable={previewLoadingAvailable}
                      loadingFollowed={previewLoading && previewTouched == null}
                      loadingStored={previewLoading && previewStored == null}
                      reserveHint={previewReserveHint}
                    />
                  </div>
                )}
                {scrapeSessionId && !followModelId && (
                  <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-2">
                    Select a follow model above to preview availability.
                  </p>
                )}
                {scrapeSessionId && followModelId && !previewLoading && previewAvailable === 0 && (
                  <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-2">
                    All handles in this session were already followed by model
                    {selectedModelName ? ` ${selectedModelName}` : ''} — pick a different session or wait for new scrapes.
                  </p>
                )}
              </div>
            )}

            {tsType === 'paste' && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                You can paste handles in the main automation view after starting.
                Switch to <strong>Scraper V2 session</strong> for automatic dedup tracking.
              </p>
            )}
          </section>

          {/* ── Configuration ── */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1 flex items-center gap-1.5">
              <Cog6ToothIcon className="w-4 h-4 text-gray-400" />
              Configuration
            </h3>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-3">
              Each account gets a randomized daily cap between{' '}
              <strong className="text-gray-700 dark:text-gray-300">
                {localConfig.totalFollowsPerProfileMin}–{localConfig.totalFollowsPerProfileMax}
              </strong>{' '}
              follows and starts its own 24h timer independently.
            </p>
            <RunJobConfigFields config={localConfig} onChange={setLocalConfig} />
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-3">
              <span className="font-semibold text-gray-700 dark:text-gray-300 tabular-nums">
                {followCapacity.toLocaleString()}
              </span>{' '}
              follow capacity ·{' '}
              <span className="font-semibold text-gray-700 dark:text-gray-300">{accountsInRun}</span>{' '}
              account{accountsInRun === 1 ? '' : 's'} ×{' '}
              {localConfig.totalFollowsPerProfileMax ?? 55} max/profile
              {previewAvailable != null && previewAvailable >= 0 && (
                <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                  {' '}· {Math.min(followCapacity, previewAvailable).toLocaleString()} available now
                </span>
              )}
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
          <p className="text-[11px] text-gray-400">
            Config + source are saved automatically when you click Start.
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="btn-secondary !py-2 !px-5 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleStart}
              disabled={
                saving
                || !followModelId
                || (needsScrapeSessionPick(tsType) && !scrapeSessionId)
                || (needsScrapeSessionPick(tsType) && previewAvailable === 0)
              }
              title={
                previewAvailable === 0
                  ? 'No unfollowed handles left in this scrape session for this model'
                  : undefined
              }
              className="btn-primary !py-2 !px-6 text-sm inline-flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Starting…
                </>
              ) : (
                <>
                  <PlayIcon className="w-4 h-4" />
                  Start Automation
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Unified card rendered in the Run tab.
 * – Idle state  → source stats (available / already-followed / total),
 *                 inline-editable config, batch preview, Start button.
 * – Active state → running/paused badge + Pause / Resume / Stop controls.
 *
 * Requirement: daily cap is tracked per-job (each new job gets a fresh
 * dailySent counter in accountResults — it does NOT carry over from
 * previous jobs). This card makes that behaviour explicit in the UI.
 */
function CreateJobCard({
  agent, config, onConfigSave,
  tsAvailable, tsStoredInSource, tsAlreadyTouched, tsClaimedInSource, tsUnclaimedInSource, tsStatsLoading,
  tsLoadingAvailable, tsReserveHint,
  tsDailyCap, tsDesiredBuffer,
  enabledAccountCount, jobTargetCapacity,
  onDispatch, onPause, onStop, onResume,
  onEditSetup,
  customTargetsText, setCustomTargetsText,
  parsedCustomTargets, useTargetPool, setUseTargetPool,
}) {
  const [showConfigEdit, setShowConfigEdit] = useState(false);
  const [localConfig, setLocalConfig] = useState(config || DEFAULT_CONFIG);
  const [configSaving, setConfigSaving] = useState(false);

  useEffect(() => { if (config) setLocalConfig(config); }, [config]);

  const agentStatus = agent.status;
  const isActive = agentStatus === 'running' || agentStatus === 'pausing' || agentStatus === 'paused';

  const ts = agent.targetSource || {};
  const tsType = ts.type || 'paste';
  let sourceLabel = 'Paste list';
  let sourceDetail = ts.pastedTargetsCount
    ? `${ts.pastedTargetsCount.toLocaleString()} handles saved`
    : 'No handles saved';
  if (tsType === 'scrape_v2_pool') {
    sourceLabel = 'Scraper V2 pool';
    sourceDetail = ts.scrapeTag
      ? `Category “${ts.scrapeTag}” · verified V2 handles`
      : 'All verified V2 handles for your account';
  } else if (tsType === 'scrape_v2_session') {
    sourceLabel = 'Scraper V2 session';
    sourceDetail = ts.scrapeSessionId ? `Session ${ts.scrapeSessionId.slice(0, 18)}…` : 'No session selected';
  } else if (tsType === 'scrape_session') {
    sourceLabel = 'Legacy scrape session';
    sourceDetail = ts.scrapeSessionId ? `Session ${ts.scrapeSessionId.slice(0, 14)}…` : 'No session selected';
  } else if (tsType === 'scrape_tag') {
    sourceLabel = 'Stored scrape tag';
    sourceDetail = ts.scrapeTag ? `Tag "${ts.scrapeTag}"` : 'No tag selected';
  }

  const isSourced = tsType !== 'paste';
  const totalAccounts = agent.accountCount || 0;
  const outdated = Boolean(agent.desktopAgent?.outdated);
  const startDisabled = !enabledAccountCount || gologinTokenBlocksStart(agent) || outdated;
  let startTitle = '';
  if (outdated) startTitle = `Desktop worker is outdated (v${agent.desktopAgent?.reportedVersion || 'unknown'} < v${agent.desktopAgent?.minRequiredVersion}). Redownload from Setup.`;
  else if (gologinTokenBlocksStart(agent)) startTitle = 'Set GoLogin API token first';
  else if (!totalAccounts) startTitle = 'Attach at least one account first';
  else if (!enabledAccountCount) startTitle = 'Every attached account is deselected — pick at least one in the Setup tab.';

  const handleConfigSave = async () => {
    setConfigSaving(true);
    try {
      await onConfigSave(localConfig);
      setShowConfigEdit(false);
    } finally {
      setConfigSaving(false);
    }
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-2xl bg-white dark:bg-gray-800 overflow-hidden">

      {/* ── Card header ── */}
      <div className="px-4 pt-3.5 pb-3 flex items-center justify-between border-b border-gray-100 dark:border-gray-700/50">
        <div className="flex items-center gap-2.5">
          {isActive ? (
            <>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">Job active</span>
              {agentStatus === 'running' && (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
                  Running
                </span>
              )}
              {agentStatus === 'pausing' && (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                  Pausing…
                </span>
              )}
              {agentStatus === 'paused' && (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  Paused
                </span>
              )}
            </>
          ) : (
            <span className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
              <PlusIcon className="w-4 h-4 text-gray-400" />
              No automation running
            </span>
          )}
        </div>
        {/* Controls — always visible */}
        <div className="flex items-center gap-2">
          {agentStatus === 'running' && (
            <button
              type="button"
              onClick={onPause}
              className="btn-secondary !py-1 !px-3 text-xs inline-flex items-center gap-1"
            >
              <PauseIcon className="w-3.5 h-3.5" /> Pause
            </button>
          )}
          {agentStatus === 'paused' && (
            <button
              type="button"
              onClick={onResume}
              disabled={gologinTokenBlocksStart(agent)}
              className="btn-primary !py-1 !px-3 text-xs inline-flex items-center gap-1 disabled:opacity-40"
            >
              <PlayIcon className="w-3.5 h-3.5" /> Resume
            </button>
          )}
          {(agentStatus === 'running' || agentStatus === 'pausing' || agentStatus === 'paused' || agent.pendingJobs) && (
            <button
              type="button"
              onClick={onStop}
              className="btn-secondary !py-1 !px-3 text-xs inline-flex items-center gap-1 !text-rose-600 dark:!text-rose-400 hover:!bg-rose-50 dark:hover:!bg-rose-950/30"
            >
              <StopIcon className="w-3.5 h-3.5" /> Stop
            </button>
          )}
        </div>
      </div>

      {/* ── Source stats ── */}
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide flex items-center gap-1.5">
            <BoltIcon className="w-3 h-3" />
            {sourceLabel}
            {sourceDetail && (
              <span className="font-normal normal-case tracking-normal text-gray-400 ml-0.5">{sourceDetail}</span>
            )}
          </span>
          <button
            type="button"
            onClick={onEditSetup}
            className="text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            Configure source ▸
          </button>
        </div>

        {isSourced ? (
          <SourceAvailabilityStats
            available={tsAvailable}
            stored={tsStoredInSource}
            followed={tsAlreadyTouched}
            claimed={tsClaimedInSource}
            unclaimed={tsUnclaimedInSource}
            showClaimed={isScrapeV2PoolType(tsType)}
            poolMode={isScrapeV2PoolType(tsType)}
            categoryFilter={isScrapeV2PoolType(tsType) ? (ts.scrapeTag || null) : null}
            loadingAvailable={tsLoadingAvailable}
            loadingFollowed={tsStatsLoading && tsAlreadyTouched == null}
            loadingStored={tsStatsLoading && tsStoredInSource == null}
            loadingClaimed={tsStatsLoading && tsClaimedInSource == null}
            reserveHint={tsReserveHint}
            size="xl"
          />
        ) : (
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-3 py-2.5 text-xs text-gray-500">
            Paste mode — handles entered manually below.{' '}
            <button type="button" onClick={onEditSetup} className="text-indigo-600 dark:text-indigo-400 hover:underline">
              Switch to a scrape source
            </button>{' '}
            for live dedup-aware counts.
          </div>
        )}
      </div>

      {/* ── Paste textarea (paste mode only) ── */}
      {tsType === 'paste' && (
        <div className="px-4 pb-3 space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
              Add handles <span className="text-gray-400 font-normal">(merged with Setup paste list)</span>
            </label>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500">
                {parsedCustomTargets.length.toLocaleString()} parsed
              </span>
              <label className="inline-flex items-center gap-1 text-[11px] text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 cursor-pointer">
                <input
                  type="file"
                  accept=".txt,.csv,text/plain,text/csv"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (!file) return;
                    if (file.size > 5 * 1024 * 1024) { toast.error('File too large — keep it under 5 MB'); return; }
                    try {
                      const text = await file.text();
                      const parsed = parseUsernameList(text);
                      if (!parsed.length) { toast.error('No valid X handles found in the file'); return; }
                      setCustomTargetsText(parseUsernameList(`${customTargetsText}\n${text}`).join('\n'));
                      toast.success(`Loaded ${parsed.length.toLocaleString()} handles from ${file.name}`);
                    } catch (err) { toast.error(`Failed to read ${file.name}: ${err.message}`); }
                  }}
                />
                <ArrowUpTrayIcon className="w-3.5 h-3.5" />
                <span>Upload .txt</span>
              </label>
              {customTargetsText && (
                <button
                  type="button"
                  onClick={() => setCustomTargetsText('')}
                  className="text-[11px] text-gray-500 hover:text-rose-600 dark:hover:text-rose-400"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          <textarea
            value={customTargetsText}
            onChange={(e) => setCustomTargetsText(e.target.value)}
            placeholder={'@elonmusk\n@jack\nnaval, paulg\n…one per line, comma or space separated — or use Upload .txt'}
            rows={3}
            className="form-input text-xs font-mono w-full leading-snug"
          />
          <label className="inline-flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={useTargetPool}
              onChange={(e) => setUseTargetPool(e.target.checked)}
              className="form-checkbox"
            />
            <span>
              Top up from shared target pool
              {parsedCustomTargets.length > 0 && useTargetPool && (
                <span className="text-gray-500">
                  {' '}(claims {Math.max(0, jobTargetCapacity - parsedCustomTargets.length).toLocaleString()} more)
                </span>
              )}
            </span>
          </label>
        </div>
      )}

      {/* ── Configuration ── */}
      <div className="px-4 pb-3">
        <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
          {/* Config header row */}
          <div className="px-3 py-2.5 flex items-center justify-between bg-gray-50/60 dark:bg-gray-900/40">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Configuration
              </span>
              <span className="text-[11px] text-gray-400">
                {config?.maxConcurrentBrowsers} browsers · {config?.followsPerAccountMin}–{config?.followsPerAccountMax}/round · {config?.totalFollowsPerProfileMin}–{config?.totalFollowsPerProfileMax} total/profile
              </span>
            </div>
            <button
              type="button"
              onClick={() => setShowConfigEdit((v) => !v)}
              className="text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-0.5 flex-shrink-0"
            >
              {showConfigEdit
                ? <><ChevronUpIcon className="w-3 h-3" /> Close</>
                : <><ChevronDownIcon className="w-3 h-3" /> Edit</>}
            </button>
          </div>
          {/* Per-profile total — clarifies each account's individual daily ceiling */}
          <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-700/50 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-400">
              <ClockIcon className="w-3.5 h-3.5" />
              {config?.totalFollowsPerProfileMin ?? '—'}–{config?.totalFollowsPerProfileMax ?? '—'} total/profile (daily ceiling per account)
            </span>
            <span className="text-[11px] text-gray-400">·</span>
            <span className="text-[11px] text-gray-500">
              Resets fresh each new job — daily count is tracked per-job, not globally
            </span>
          </div>
          {/* Inline edit form */}
          {showConfigEdit && (
            <div className="px-3 py-3 border-t border-gray-200 dark:border-gray-700 space-y-3">
              <RunJobConfigFields config={localConfig} onChange={setLocalConfig} />
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleConfigSave}
                  disabled={configSaving}
                  className="btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1 disabled:opacity-50"
                >
                  {configSaving ? 'Saving…' : 'Save for next run'}
                </button>
                <button
                  type="button"
                  onClick={() => { setLocalConfig(config || DEFAULT_CONFIG); setShowConfigEdit(false); }}
                  className="btn-secondary !py-1.5 !px-3 text-xs"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Batch preview + Start button ── */}
      <div className="px-4 pb-4 pt-1 border-t border-gray-100 dark:border-gray-700/50 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-gray-600 dark:text-gray-400">
            <span className="font-semibold text-gray-900 dark:text-white tabular-nums">
              {jobTargetCapacity.toLocaleString()}
            </span>{' '}handles ·{' '}
            <span className="font-semibold text-gray-900 dark:text-white">{enabledAccountCount}</span>{' '}
            account{enabledAccountCount === 1 ? '' : 's'} ×{' '}
            {config?.totalFollowsPerProfileMax ?? agent?.config?.totalFollowsPerProfileMax ?? 55} max/profile
            {isSourced && tsAvailable >= 0 && (
              <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                {' '}· {Math.min(jobTargetCapacity, tsAvailable).toLocaleString()} available now
              </span>
            )}
          </div>
          {totalAccounts > 0 && enabledAccountCount < totalAccounts && (
            <div className="text-[11px] text-gray-500 mt-0.5">
              {enabledAccountCount}/{totalAccounts} accounts selected —{' '}
              <button
                type="button"
                onClick={onEditSetup}
                className="text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                change in Setup
              </button>
            </div>
          )}
        </div>
        {!isActive && (
          <button
            type="button"
            onClick={onDispatch}
            disabled={startDisabled}
            title={startTitle}
            className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-40"
          >
            <PlayIcon className="w-4 h-4" /> Start Automation
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Compact run-tab card that shows the operator which target source
 * will feed the next job, with a live "X recipients ready" preview
 * and an "Edit in Setup" affordance.
 */
function TargetSourceRunSummary({
  source, available, storedInSource, alreadyTouchedInSource, dailyCap, desiredBuffer, onEdit
}) {
  const ts = source || { type: 'paste', pastedTargetsCount: 0 };
  const type = ts.type || 'paste';
  let label = 'Paste list';
  let detail = `${(ts.pastedTargetsCount || 0).toLocaleString()} handle${(ts.pastedTargetsCount || 0) === 1 ? '' : 's'} saved`;
  if (type === 'scrape_v2_pool') {
    label = 'Scraper V2 pool';
    detail = ts.scrapeTag
      ? `Category “${ts.scrapeTag}”`
      : 'Global filtered pool (all V2 sessions)';
  } else if (type === 'scrape_v2_session') {
    label = 'Scraper V2 session';
    detail = ts.scrapeSessionId ? `Session ${ts.scrapeSessionId.slice(0, 18)}…` : 'No session selected';
  } else if (type === 'scrape_session') {
    label = 'Legacy scrape session';
    detail = ts.scrapeSessionId ? `Session ${ts.scrapeSessionId.slice(0, 14)}…` : 'No session selected';
  } else if (type === 'scrape_tag') {
    label = 'Stored scrape tag';
    detail = ts.scrapeTag ? `Tag "${ts.scrapeTag}"` : 'No tag selected';
  }
  const availableLabel = available >= 0
    ? `${available.toLocaleString()} unfollowed recipient${available === 1 ? '' : 's'} ready`
    : '— dedup-aware count unavailable';
  const storedHint = storedInSource != null
    ? `${storedInSource.toLocaleString()} passed filters in source`
    : null;
  const touchedHint = alreadyTouchedInSource != null && alreadyTouchedInSource > 0
    ? `${alreadyTouchedInSource.toLocaleString()} followed from pool by this model`
    : null;
  const bufferHint = (isLinkedScrapeSessionType(type) || isScrapeV2PoolType(type)) && desiredBuffer > 0
    ? `Linker keeps ~${desiredBuffer.toLocaleString()} ahead (≈${dailyCap.toLocaleString()}/day cap × buffer)`
    : null;

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 bg-gray-50/40 dark:bg-gray-900/30">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
            <BoltIcon className="w-3.5 h-3.5" /> Target source
          </h4>
          <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white truncate">
            {label}
            <span className="ml-2 text-xs font-normal text-gray-500 truncate">{detail}</span>
          </p>
          <p className="mt-1 text-xs text-gray-500">
            <span className="font-medium text-gray-700 dark:text-gray-300">{availableLabel}</span>
            {storedHint && (
              <>
                <br />
                <span className="text-[11px]">{storedHint}</span>
              </>
            )}
            {touchedHint && (
              <>
                <br />
                <span className="text-[11px]">{touchedHint}</span>
              </>
            )}
            {bufferHint && (
              <>
                <br />
                <span className="text-[11px]">{bufferHint}</span>
              </>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={onEdit}
          className="btn-secondary !py-1 !px-2.5 text-xs inline-flex items-center gap-1 flex-shrink-0"
          title="Open Setup tab to change source"
        >
          <Cog6ToothIcon className="w-3.5 h-3.5" /> Configure
        </button>
      </div>
    </div>
  );
}

/**
 * Setup tab: picks the target source (paste / live scrape session /
 * stored scrape tag), default per-dispatch count, and the
 * lookahead buffer that controls the scraper auto-pause behaviour.
 */
function TargetSourcePanel({
  tsType, setTsType,
  tsWorkspaceId, setTsWorkspaceId, tsWorkspaces = [],
  tsScrapeSessionId, setTsScrapeSessionId,
  tsScrapeTag, setTsScrapeTag,
  jobTargetCapacity,
  enabledAccountCount,
  config,
  tsBufferDays, setTsBufferDays,
  tsFollowModelId, setTsFollowModelId,
  followModels,
  fbfEnabled, setFbfEnabled,
  fbfSourceModelId, setFbfSourceModelId,
  fbfMinAgeDays, setFbfMinAgeDays,
  tsPastedText, setTsPastedText,
  tsParsedPaste,
  tsAvailable, tsStoredInSource, tsAlreadyTouched, tsClaimedInSource, tsDailyCap, tsDesiredBuffer,
  tsStatsLoading, tsLoadingAvailable, tsReserveHint,
  tsSaving,
  scrapeSessions, scrapeV2Sessions, scrapeTags,
  scrapeV2Categories = [],
  loadPastedTargets,
  onSave,
  serverTargetSource,
  serverFollowModelId
}) {
  const [showAdvancedSources, setShowAdvancedSources] = useState(
    () => ADVANCED_SOURCE_TYPES.has(serverTargetSource?.type || '')
  );
  // Lazily reveal the saved paste list when the operator switches to
  // paste mode (avoids one extra GET per agent open if they never look).
  useEffect(() => {
    if (tsType === 'paste') loadPastedTargets();
  }, [tsType, loadPastedTargets]);

  const dirty = (
    (tsType !== (serverTargetSource?.type || 'paste'))
    || (tsScrapeSessionId !== (serverTargetSource?.scrapeSessionId || ''))
    || (tsScrapeTag !== (serverTargetSource?.scrapeTag || ''))
    || (String(tsFollowModelId || '') !== String(serverFollowModelId || ''))
    || (Boolean(fbfEnabled) !== Boolean(serverTargetSource?.followBacksFirst?.enabled))
    || (String(fbfSourceModelId || '') !== String(serverTargetSource?.followBacksFirst?.sourceModelId || ''))
    || ((Number(fbfMinAgeDays) || 7) !== (Number(serverTargetSource?.followBacksFirst?.minAgeDays) || 7))
    || (Number(tsBufferDays) !== (serverTargetSource?.bufferDays ?? 1))
    || (tsType === 'paste' && tsPastedText
        && tsParsedPaste.length !== (serverTargetSource?.pastedTargetsCount || 0))
  );

  const selectedModelName = followModels.find((m) => String(m._id) === String(tsFollowModelId))?.name;

  return (
    <section className="border border-gray-200 dark:border-gray-700 rounded-2xl bg-white dark:bg-gray-800 p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <BoltIcon className="w-4 h-4" /> Target source
          </h4>
          <p className="text-[12px] text-gray-500 mt-0.5">
            Follow dedup is scoped per dashboard model (e.g. Amy vs Vanessa).
            Multiple AI VA Agents on the same model share targets and never overlap.
          </p>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Database
        </label>
        <select
          value={tsWorkspaceId || 'legacy'}
          onChange={(e) => setTsWorkspaceId(e.target.value)}
          className="form-select text-sm w-full"
        >
          {(tsWorkspaces.length ? tsWorkspaces : [{ workspaceId: 'legacy', name: 'Current data (legacy)', followBacks: 0 }]).map((w) => (
            <option key={w.workspaceId} value={w.workspaceId}>
              {w.name} — {(Number(w.followBacks) || 0).toLocaleString()} follow-backs
            </option>
          ))}
        </select>
        <p className="text-[11px] text-gray-500 mt-1">
          Clean-sheet databases have their own stored pool and follow ledger. Switch back to Current data any time.
        </p>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Follow model
        </label>
        {followModels.length === 0 ? (
          <p className="text-xs text-gray-500 italic">
            No models found — create one under Dashboard → Models first.
          </p>
        ) : (
          <select
            value={tsFollowModelId}
            onChange={(e) => setTsFollowModelId(e.target.value)}
            className="form-input text-xs w-full"
          >
            <option value="">— Select model —</option>
            {followModels.map((m) => (
              <option key={m._id} value={m._id}>{m.name}</option>
            ))}
          </select>
        )}
        {selectedModelName && (
          <p className="mt-1 text-[11px] text-gray-500">
            Availability stats below are for model <strong>{selectedModelName}</strong> only.
          </p>
        )}
      </div>

      <div className="flex items-start justify-between gap-3">
        <div />
        <button
          type="button"
          onClick={() => onSave()}
          disabled={tsSaving || !dirty}
          className="btn-primary text-sm disabled:opacity-40 inline-flex items-center gap-1.5"
        >
          {tsSaving ? 'Saving…' : 'Save source'}
        </button>
      </div>

      {/* Mode picker — pool default; legacy under Advanced */}
      <div className="space-y-2">
        <div className="grid sm:grid-cols-2 gap-2">
          {[
            { id: 'scrape_v2_pool', title: 'Scraper V2 pool', desc: 'Global verified pool — recommended.' },
            { id: 'paste', title: 'Paste list', desc: 'Manual upload for one-off lists.' },
          ].map((opt) => (
            <button
              type="button"
              key={opt.id}
              onClick={() => {
                setTsType(opt.id);
                if (opt.id !== 'scrape_session' && opt.id !== 'scrape_v2_session') {
                  setTsScrapeSessionId('');
                }
              }}
              className={`text-left p-3 rounded-xl border text-xs transition-colors ${
                tsType === opt.id
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-900 dark:text-indigo-100'
                  : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300 text-gray-700 dark:text-gray-300'
              }`}
            >
              <div className="font-semibold text-sm">{opt.title}</div>
              <div className="mt-0.5 text-[11px] opacity-80">{opt.desc}</div>
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setShowAdvancedSources((v) => !v)}
          className="text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          {showAdvancedSources ? 'Hide advanced sources' : 'Advanced sources (legacy session / tag)'}
        </button>
        {showAdvancedSources && (
          <div className="grid sm:grid-cols-3 gap-2 pt-1">
            {[
              { id: 'scrape_v2_session', title: 'Scraper V2 session', desc: 'Single session bind (legacy).' },
              { id: 'scrape_session', title: 'Legacy scrape', desc: 'V1 scrape tasks.' },
              { id: 'scrape_tag', title: 'Stored scrape tag', desc: 'V1 tagged data.' },
            ].map((opt) => (
              <button
                type="button"
                key={opt.id}
                onClick={() => {
                  setTsType(opt.id);
                  if (opt.id !== 'scrape_session' && opt.id !== 'scrape_v2_session') {
                    setTsScrapeSessionId('');
                  } else if (opt.id !== tsType) {
                    setTsScrapeSessionId('');
                  }
                }}
                className={`text-left p-3 rounded-xl border text-xs transition-colors ${
                  tsType === opt.id
                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-900 dark:text-indigo-100'
                    : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300 text-gray-700 dark:text-gray-300'
                }`}
              >
                <div className="font-semibold text-sm">{opt.title}</div>
                <div className="mt-0.5 text-[11px] opacity-80">{opt.desc}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {isScrapeV2PoolType(tsType) && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="form-checkbox h-3.5 w-3.5"
              checked={Boolean(fbfEnabled)}
              onChange={(e) => setFbfEnabled(e.target.checked)}
            />
            <span className="text-sm text-gray-800 dark:text-gray-100">
              Follow another model&apos;s follow-backs first
            </span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block text-xs space-y-1">
              <span className="font-medium text-gray-700 dark:text-gray-300">Source model</span>
              <select
                value={fbfSourceModelId || ''}
                disabled={!fbfEnabled}
                onChange={(e) => setFbfSourceModelId(e.target.value)}
                className="form-input text-xs w-full"
              >
                <option value="">— select model —</option>
                {followModels
                  .filter((m) => String(m._id) !== String(tsFollowModelId))
                  .map((m) => (
                    <option key={m._id} value={m._id}>{m.name}</option>
                  ))}
              </select>
            </label>
            <label className="block text-xs space-y-1">
              <span className="font-medium text-gray-700 dark:text-gray-300">Minimum age (days)</span>
              <input
                type="number"
                min={1}
                max={90}
                disabled={!fbfEnabled}
                value={fbfMinAgeDays}
                onChange={(e) => setFbfMinAgeDays(e.target.value)}
                className="form-input text-xs w-full"
              />
            </label>
          </div>
          <p className="text-[11px] text-gray-500">
            Users who followed the source model back are dispatched first, once they
            are at least this many days old. Anyone this model already followed is skipped.
            The scrape pool fills whatever is left.
          </p>
        </div>
      )}

      {isScrapeV2PoolType(tsType) && tsFollowModelId && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Data category
            </label>
            <select
              value={tsScrapeTag}
              onChange={(e) => setTsScrapeTag(e.target.value)}
              className="form-input text-xs w-full"
            >
              <option value="">All categories</option>
              {(scrapeV2Categories || []).map((c) => (
                <option key={c.id || c.name} value={c.name}>
                  {c.name}
                  {typeof c.filteredCount === 'number' ? ` (${Number(c.filteredCount).toLocaleString()} verified)` : ''}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-gray-500">
              Limit follows to one Scraper V2 category (e.g. Gaming). Leave on All to use the full pool.
              Previously followed usernames stay on the model ledger; follow-back scans always cover every followed handle.
            </p>
          </div>
          <SourceAvailabilityStats
            available={tsAvailable}
            stored={tsStoredInSource}
            followed={tsAlreadyTouched}
            claimed={tsClaimedInSource}
            showClaimed
            loadingAvailable={tsLoadingAvailable}
            loadingFollowed={tsStatsLoading && tsAlreadyTouched == null}
            loadingStored={tsStatsLoading && tsStoredInSource == null}
            loadingClaimed={tsStatsLoading && tsClaimedInSource == null}
            reserveHint={tsReserveHint}
            size="lg"
            poolMode
            categoryFilter={tsScrapeTag || null}
          />
          <div className="rounded-lg border border-indigo-200 dark:border-indigo-800/50 bg-indigo-50/40 dark:bg-indigo-950/20 p-3">
            <p className="text-xs text-gray-700 dark:text-gray-300">
              Pulls from your <strong>Scraper V2 filtered pool</strong>
              {tsScrapeTag ? <> · category <strong>{tsScrapeTag}</strong></> : null}.
              Stats are for model{' '}
              <strong>{selectedModelName || 'selected above'}</strong> — all agents on this model share the same dedup ledger.
            </p>
            <p className="mt-2 text-[11px] text-gray-500">
              <strong>Total</strong> = verified handles{tsScrapeTag ? ' in this category' : ' in the pool'} ·{' '}
              <strong>Followed</strong> = {tsScrapeTag ? 'ledger-touched handles in this category' : 'successful follows by this model (all agents)'} ·{' '}
              <strong>Reserved</strong> = claimed for dispatch · <strong>Available</strong> = untouched pool handles for this model.
            </p>
          </div>
        </div>
      )}

      {isScrapeV2PoolType(tsType) && !tsFollowModelId && (
        <p className="text-[11px] text-amber-600 dark:text-amber-400">
          Select a follow model above to preview pool stats.
        </p>
      )}

      {/* Mode body */}
      {tsType === 'paste' && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
              Persistent paste list
            </label>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500">
                {tsParsedPaste.length.toLocaleString()} valid handle{tsParsedPaste.length === 1 ? '' : 's'} parsed
              </span>
              <label className="inline-flex items-center gap-1 text-[11px] text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 cursor-pointer">
                <input
                  type="file"
                  accept=".txt,.csv,text/plain,text/csv"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (!file) return;
                    if (file.size > 5 * 1024 * 1024) {
                      toast.error('File too large — keep it under 5 MB');
                      return;
                    }
                    try {
                      const text = await file.text();
                      const parsed = parseUsernameList(text);
                      if (!parsed.length) {
                        toast.error('No valid X handles found in the file');
                        return;
                      }
                      const merged = parseUsernameList(`${tsPastedText}\n${text}`);
                      setTsPastedText(merged.join('\n'));
                      toast.success(`Loaded ${parsed.length.toLocaleString()} handles from ${file.name}`);
                    } catch (err) {
                      toast.error(`Failed to read ${file.name}: ${err.message}`);
                    }
                  }}
                />
                <ArrowUpTrayIcon className="w-3.5 h-3.5" />
                <span>Upload .txt</span>
              </label>
              {tsPastedText && (
                <button
                  type="button"
                  onClick={() => setTsPastedText('')}
                  className="text-[11px] text-gray-500 hover:text-rose-600 dark:hover:text-rose-400"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          <textarea
            value={tsPastedText}
            onChange={(e) => setTsPastedText(e.target.value)}
            placeholder={'@elonmusk\n@jack\nnaval, paulg\n…one per line, comma or space separated — or use Upload .txt'}
            rows={6}
            className="form-input text-xs font-mono w-full leading-snug"
          />
          <p className="mt-1 text-[11px] text-gray-500">
            Up to ~100,000 handles. `@` is optional, invalid handles dropped silently.
            Per-model dedup is applied at dispatch time.
          </p>
        </div>
      )}

      {tsType === 'scrape_v2_session' && (() => {
        const scoped = scrapeV2Sessions.filter((s) => (s.workspaceId || 'legacy') === (tsWorkspaceId || 'legacy'));
        const live = scoped.filter((s) => s.status === 'running' || s.status === 'paused');
        const stored = scoped.filter((s) => s.status === 'completed' || s.status === 'stopped');
        const renderOption = (s) => (
          <option key={s.sessionId} value={s.sessionId}>
            {scrapeSessionOptionLabel(s)}
          </option>
        );
        return (
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Scraper V2 session
            </label>
            {scoped.length === 0 ? (
              <p className="text-xs text-gray-500 italic">
                No Scraper V2 sessions in this database — create a Clean sheet session under Dashboard → Scraper V2, or switch Database.
              </p>
            ) : (
              <select
                value={tsScrapeSessionId}
                onChange={(e) => setTsScrapeSessionId(e.target.value)}
                className="form-input text-xs w-full"
              >
                <option value="">— Pick a Scraper V2 session —</option>
                {live.length > 0 && (
                  <optgroup label={`Live (${live.length})`}>
                    {live.map(renderOption)}
                  </optgroup>
                )}
                {stored.length > 0 && (
                  <optgroup label={`Completed (${stored.length})`}>
                    {stored.map(renderOption)}
                  </optgroup>
                )}
              </select>
            )}
            <p className="mt-2 text-[11px] text-gray-500">
              Pulls from the <strong>filtered</strong> inventory (country + activity passed).
              The Source Linker pauses the V2 session when the buffer is full and resumes when it drops.
            </p>
          </div>
        );
      })()}

      {tsType === 'scrape_session' && (() => {
        const live = scrapeSessions.filter((s) => s.status === 'running' || s.status === 'paused');
        const stored = scrapeSessions.filter((s) => s.status === 'completed' || s.status === 'stopped');
        const renderOption = (s) => (
          <option key={s.sessionId} value={s.sessionId}>
            {scrapeSessionOptionLabel(s)}
          </option>
        );
        return (
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Linked scrape session
            </label>
            {scrapeSessions.length === 0 ? (
              <p className="text-xs text-gray-500 italic">
                No scrape sessions found for this agent's owner. Start a scrape session first, then come back.
              </p>
            ) : (
              <select
                value={tsScrapeSessionId}
                onChange={(e) => setTsScrapeSessionId(e.target.value)}
                className="form-input text-xs w-full"
              >
                <option value="">— Pick a scrape session —</option>
                {live.length > 0 && (
                  <optgroup label={`Live (${live.length})`}>
                    {live.map(renderOption)}
                  </optgroup>
                )}
                {stored.length > 0 && (
                  <optgroup label={`Stored (${stored.length})`}>
                    {stored.map(renderOption)}
                  </optgroup>
                )}
              </select>
            )}
            <p className="mt-2 text-[11px] text-gray-500">
              The Source Linker automatically pauses this session when more than
              <span className="font-medium"> {tsDesiredBuffer.toLocaleString() || '~1d'}</span> recipients
              are queued up (= bufferDays × estimated daily cap), and resumes it when the buffer
              falls below 60% of that. Completed/stopped sessions are usable too — they just
              feed from already-stored rows with no live pause/resume.
            </p>
          </div>
        );
      })()}

      {tsType === 'scrape_tag' && (
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Scraped data tag
          </label>
          {scrapeTags.length === 0 ? (
            <p className="text-xs text-gray-500 italic">
              No scraped data tags found yet — finish a scrape, then come back.
            </p>
          ) : (
            <select
              value={tsScrapeTag}
              onChange={(e) => setTsScrapeTag(e.target.value)}
              className="form-input text-xs w-full"
            >
              <option value="">— Pick a scraped data tag —</option>
              {scrapeTags.map((t) => (
                <option key={t.tag} value={t.tag}>
                  {t.tag}
                  {` · ${(t.totalCount || 0).toLocaleString()} rows`}
                  {t.activeCount ? ` · ${t.activeCount.toLocaleString()} active` : ''}
                </option>
              ))}
            </select>
          )}
          <p className="mt-2 text-[11px] text-gray-500">
            Pulls stored followers (`stored_active` + `stored_direct`) from this tag,
            filtering out anything this model has already followed.
          </p>
        </div>
      )}

      {((isLinkedScrapeSessionType(tsType) && tsScrapeSessionId && tsFollowModelId)
        || (tsType === 'scrape_tag' && tsScrapeTag && tsFollowModelId)) && (
        <SourceAvailabilityStats
          available={tsAvailable}
          stored={tsStoredInSource}
          followed={tsAlreadyTouched}
          loadingAvailable={tsLoadingAvailable}
          loadingFollowed={tsStatsLoading && tsAlreadyTouched == null}
          loadingStored={tsStatsLoading && tsStoredInSource == null}
          reserveHint={tsReserveHint}
        />
      )}

      {/* Shared numeric controls */}
      <div className="grid sm:grid-cols-3 gap-3 pt-2 border-t border-gray-100 dark:border-gray-700">
        <div>
          <label className="block text-[11px] text-gray-500 mb-1">Job batch size (auto)</label>
          <div className="rounded-md border border-gray-200 dark:border-gray-700 px-2 py-1.5 text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
            {jobTargetCapacity.toLocaleString()}
          </div>
          <p className="mt-1 text-[10px] text-gray-500">
            {enabledAccountCount} accounts × {config?.totalFollowsPerProfileMax ?? 60} max/profile
          </p>
        </div>
        {isLinkedScrapeSessionType(tsType) && (
          <div>
            <label className="block text-[11px] text-gray-500 mb-1">Lookahead buffer (days)</label>
            <input
              type="number"
              min={0.5}
              max={30}
              step={0.5}
              value={tsBufferDays}
              onChange={(e) => setTsBufferDays(e.target.value)}
              className="form-input text-sm w-full"
            />
          </div>
        )}
        <div className="text-xs sm:col-span-2">
          <div className="text-[11px] text-gray-500 mb-1">Pipeline preview</div>
          <div className="rounded-md border border-gray-200 dark:border-gray-700 px-2 py-1.5 text-gray-700 dark:text-gray-200 space-y-0.5">
            {tsAvailable >= 0 ? (
              <span><span className="font-semibold">{tsAvailable.toLocaleString()}</span> recipients ready</span>
            ) : tsLoadingAvailable || tsStatsLoading ? (
              <span className="italic text-gray-500">Calculating availability…</span>
            ) : tsStoredInSource != null ? (
              <span className="italic text-gray-500">
                Availability pending — {tsStoredInSource.toLocaleString()} in pool
              </span>
            ) : (
              <span className="italic text-gray-500">No live count available for this source</span>
            )}
            {tsStoredInSource != null && tsAvailable >= 0 && (
              <div className="text-[11px] text-gray-500">
                {tsStoredInSource.toLocaleString()} stored (passed country + activity filters)
              </div>
            )}
            {tsAlreadyTouched != null && tsAlreadyTouched > 0 && (
              <div className="text-[11px] text-gray-500">
                {tsAlreadyTouched.toLocaleString()} already followed/skipped by this agent
              </div>
            )}
            {tsDailyCap > 0 && (
              <div className="text-[11px] text-gray-500">
                ≈{tsDailyCap.toLocaleString()}/day cap
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function AttachedAccountsPanel({
  agentId,
  agentOnline,
  accounts,
  accountCount,
  disabledIds,
  filter,
  onFilterChange,
  saving,
  onToggle,
  onSelectAll,
  onDeselectAll,
  onDetach,
  onAttach,
  onChanged
}) {
  const lowerFilter = filter.trim().toLowerCase();
  const filteredAccounts = useMemo(() => {
    if (!lowerFilter) return accounts;
    return accounts.filter((a) =>
      (a.username || '').toLowerCase().includes(lowerFilter)
    );
  }, [accounts, lowerFilter]);

  const unselectableIds = useMemo(() => {
    const set = new Set();
    for (const acc of accounts) {
      const st = String(acc?.status || '').toLowerCase();
      if (st === 'captcha' || st === 'suspended' || st === 'auth_expired') {
        set.add(String(acc._id));
      }
    }
    return set;
  }, [accounts]);
  const totalAttached = accountCount || accounts.length;
  const selectableAccounts = useMemo(
    () => accounts.filter((a) => !unselectableIds.has(String(a._id))),
    [accounts, unselectableIds]
  );
  const selectedCount = selectableAccounts.filter((a) => !disabledIds.has(String(a._id))).length;
  const allSelected = selectableAccounts.length > 0 && selectedCount === selectableAccounts.length;
  const noneSelected = selectedCount === 0;

  const [openingId, setOpeningId] = useState(null);
  const [solvingId, setSolvingId] = useState(null);
  const [openSession, setOpenSession] = useState(null); // { jobId, username }
  const [captchaSession, setCaptchaSession] = useState(null); // { jobId, username }

  const openProfile = async (acc) => {
    if (!agentId || !acc?._id) return;
    if (!agentOnline) {
      toast.error('Desktop agent is offline — start it on the VPS first');
      return;
    }
    if (!acc.gologinProfileId) {
      toast.error(`@${acc.username} has no GoLogin profile`);
      return;
    }
    setOpeningId(String(acc._id));
    try {
      const res = await api.post(`/admin/va/ai-agents/${agentId}/open-profile`, {
        accountId: acc._id,
        durationMinutes: 15
      });
      toast.success(res.data?.message || `Opening @${acc.username} on VPS`);
      setOpenSession({
        jobId: res.data.jobId,
        username: acc.username
      });
    } catch (err) {
      const existingJobId = err.response?.data?.jobId;
      if (existingJobId) {
        toast.error(err.response?.data?.error || 'Session already open');
        setOpenSession({ jobId: existingJobId, username: acc.username });
      } else {
        toast.error(err.response?.data?.error || 'Failed to open profile');
      }
    } finally {
      setOpeningId(null);
    }
  };

  const solveCaptcha = async (acc) => {
    if (!agentId || !acc?._id) return;
    if (!agentOnline) {
      toast.error('Desktop agent is offline — start it on the VPS first');
      return;
    }
    if (!acc.gologinProfileId) {
      toast.error(`@${acc.username} has no GoLogin profile`);
      return;
    }
    setSolvingId(String(acc._id));
    try {
      const res = await api.post(`/admin/va/ai-agents/${agentId}/solve-captcha`, {
        accountId: acc._id
      });
      toast.success(res.data?.message || `Captcha solve queued for @${acc.username}`);
      setCaptchaSession({
        jobId: res.data.jobId,
        username: acc.username
      });
    } catch (err) {
      const existingJobId = err.response?.data?.jobId;
      if (existingJobId) {
        toast.error(err.response?.data?.error || 'Session already running');
        setCaptchaSession({ jobId: existingJobId, username: acc.username });
      } else {
        toast.error(err.response?.data?.error || 'Failed to queue captcha solve');
      }
    } finally {
      setSolvingId(null);
    }
  };

  return (
    <section className="space-y-3">
      {openSession && (
        <OpenProfileTrackModal
          agentId={agentId}
          jobId={openSession.jobId}
          username={openSession.username}
          onClose={() => {
            setOpenSession(null);
            if (typeof onChanged === 'function') onChanged();
          }}
        />
      )}
      {captchaSession && (
        <SolveCaptchaModal
          agentId={agentId}
          jobId={captchaSession.jobId}
          username={captchaSession.username}
          onClose={() => {
            setCaptchaSession(null);
            if (typeof onChanged === 'function') onChanged();
          }}
        />
      )}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <ClipboardDocumentListIcon className="w-4 h-4" />
          Attached accounts
          <span className="text-xs font-normal text-gray-500">
            (<span className="tabular-nums">{selectedCount.toLocaleString()}</span>
            <span className="text-gray-400"> / {totalAttached.toLocaleString()}</span> selected)
          </span>
        </h4>
        <button
          onClick={onAttach}
          className="btn-secondary !py-1.5 !px-3 text-xs inline-flex items-center gap-1"
        >
          <PlusIcon className="w-3.5 h-3.5" /> Attach accounts
        </button>
      </div>

      {accounts.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={onSelectAll}
            disabled={saving || allSelected}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Enable every attached account"
          >
            Select all
          </button>
          <button
            type="button"
            onClick={onDeselectAll}
            disabled={saving || noneSelected}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Deselect every attached account"
          >
            Deselect all
          </button>
          <span className="text-[11px] text-gray-500">
            Only selected accounts will run when you click Start / Resume / Continue.
          </span>
          {saving && (
            <span className="text-[11px] text-indigo-500 dark:text-indigo-300">Saving…</span>
          )}
          {accounts.length > 8 && (
            <input
              type="search"
              placeholder="Filter @handle…"
              value={filter}
              onChange={(e) => onFilterChange(e.target.value)}
              className="form-input text-xs ml-auto w-40 sm:w-48"
            />
          )}
        </div>
      )}

      {accounts.length === 0 ? (
        <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-xl px-3 py-6 text-center text-sm text-gray-500">
          No accounts attached. Attach VA profile accounts (with GoLogin profiles imported) before starting.
        </div>
      ) : (
        <div className="border border-gray-200 dark:border-gray-700 rounded-xl divide-y divide-gray-100 dark:divide-gray-700 max-h-80 overflow-y-auto">
          {/* Sticky header row with a "master" checkbox that flips
              everything when clicked. Renders only when there's at
              least one account so the empty-state isn't crowded. */}
          <label className="sticky top-0 z-10 flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400 bg-gray-50/90 dark:bg-gray-900/80 backdrop-blur cursor-pointer select-none">
            <input
              type="checkbox"
              className="form-checkbox h-3.5 w-3.5"
              checked={allSelected}
              ref={(el) => { if (el) el.indeterminate = !allSelected && !noneSelected; }}
              disabled={saving}
              onChange={() => (allSelected ? onDeselectAll() : onSelectAll())}
            />
            <span>{allSelected ? 'All selectable' : noneSelected ? 'None selected' : `${selectedCount} of ${selectableAccounts.length} selectable`}</span>
            <span className="ml-auto normal-case tracking-normal text-gray-400 font-normal">
              {unselectableIds.size > 0 && `${unselectableIds.size} blocked · `}
              {filteredAccounts.length !== accounts.length && `${filteredAccounts.length} match`}
            </span>
          </label>

          {filteredAccounts.length === 0 ? (
            <div className="px-3 py-4 text-xs text-center text-gray-500">
              No @handles match "{filter}".
            </div>
          ) : filteredAccounts.map((acc) => {
            const id = String(acc._id);
            const unselectable = unselectableIds.has(id);
            // Status wins over the deselect list — parked profiles never look selected.
            const enabled = !unselectable && !disabledIds.has(id);
            const hasIssue = !acc.gologinProfileId || !acc.gologinCookieSet || acc.status !== 'active';
            return (
              <label
                key={id}
                className={`flex items-center gap-2 px-3 py-2 text-sm ${
                  unselectable ? 'cursor-not-allowed' : 'cursor-pointer'
                } ${
                  enabled
                    ? 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                    : 'bg-gray-50/60 dark:bg-gray-900/40 opacity-70 hover:opacity-100'
                }`}
              >
                <input
                  type="checkbox"
                  className="form-checkbox h-4 w-4 flex-shrink-0"
                  checked={enabled}
                  disabled={saving || unselectable}
                  readOnly={unselectable}
                  title={unselectable
                    ? `${acc.status} — cannot select until cleared`
                    : undefined}
                  onChange={(e) => {
                    if (unselectable) return;
                    onToggle(id, e.target.checked);
                  }}
                />
                <div className="flex items-center gap-2 min-w-0 flex-1 flex-wrap">
                  <span className={`font-mono truncate ${enabled ? '' : 'line-through text-gray-500'}`}>
                    @{acc.username}
                  </span>
                  {!acc.gologinProfileId && (
                    <span className="text-[10px] uppercase font-medium text-amber-600 dark:text-amber-400" title="No GoLogin profile imported">
                      no GoLogin
                    </span>
                  )}
                  {!acc.gologinCookieSet && (
                    <span className="text-[10px] uppercase font-medium text-amber-600 dark:text-amber-400" title="GoLogin profile has no cookies">
                      no cookies
                    </span>
                  )}
                  {acc.status !== 'active' && (
                    <span className="text-[10px] uppercase font-medium text-red-500">
                      {acc.status}
                    </span>
                  )}
                  {unselectable && (
                    <span className="text-[10px] uppercase font-medium text-gray-400">
                      auto-skipped
                    </span>
                  )}
                  {!enabled && !hasIssue && !unselectable && (
                    <span className="text-[10px] uppercase font-medium text-gray-400">
                      skipped
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); solveCaptcha(acc); }}
                    disabled={!agentOnline || !acc.gologinProfileId || solvingId === id || openingId === id}
                    className="text-xs text-amber-700 hover:text-amber-800 dark:text-amber-300 dark:hover:text-amber-200 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                    title={agentOnline
                      ? 'Test CapMonster + access-gate solve via GoLogin (same path as automations)'
                      : 'Desktop agent offline'}
                  >
                    <ShieldExclamationIcon className="w-3.5 h-3.5" />
                    {solvingId === id ? 'Solving…' : 'Captcha'}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); openProfile(acc); }}
                    disabled={!agentOnline || !acc.gologinProfileId || openingId === id || solvingId === id}
                    className="text-xs text-indigo-600 hover:text-indigo-700 dark:text-indigo-300 dark:hover:text-indigo-200 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                    title={agentOnline
                      ? 'Open GoLogin on the VPS and track clicks (for manual captcha solve)'
                      : 'Desktop agent offline'}
                  >
                    <ComputerDesktopIcon className="w-3.5 h-3.5" />
                    {openingId === id ? 'Opening…' : 'Open profile'}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDetach(acc._id); }}
                    className="text-xs text-red-600 hover:text-red-700"
                    title="Permanently detach this account from the agent"
                  >
                    Detach
                  </button>
                </div>
              </label>
            );
          })}
        </div>
      )}
    </section>
  );
}

function SolveCaptchaModal({ agentId, jobId, username, onClose }) {
  const [job, setJob] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!agentId || !jobId) return;
    try {
      const res = await api.get(`/admin/va/ai-agents/${agentId}/jobs/${jobId}`);
      if (res.data?.success) {
        setJob(res.data.job);
        setError('');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load job');
    }
  }, [agentId, jobId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [load]);

  const status = job?.status || 'queued';
  const gateCleared = Boolean(job?.progress?.gateCleared);
  const done = ['completed', 'failed', 'cancelled'].includes(status);
  const message = job?.progress?.lastMessage || job?.error || 'Waiting for desktop agent…';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-xl">
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-800">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <ShieldExclamationIcon className="w-4 h-4 text-amber-500" />
              Captcha solve · @{username}
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Same GoLogin + CapMonster path as automations. Browser opens briefly on the VPS.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 py-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className={`px-2 py-0.5 rounded-full font-medium ${jobStatusClass(status)}`}>
              {status}
            </span>
            {gateCleared && (
              <span className="px-2 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                gate cleared
              </span>
            )}
            {done && !gateCleared && status === 'failed' && (
              <span className="px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200">
                not cleared
              </span>
            )}
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-200 break-words">{message}</p>
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-800 flex justify-end">
          <button type="button" onClick={onClose} className="btn-secondary !py-1.5 !px-3 text-xs">
            {done ? 'Done' : 'Hide'}
          </button>
        </div>
      </div>
    </div>
  );
}

function OpenProfileTrackModal({ agentId, jobId, username, onClose }) {
  const [job, setJob] = useState(null);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState('');
  const listRef = useRef(null);

  const load = useCallback(async () => {
    if (!agentId || !jobId) return;
    try {
      const res = await api.get(`/admin/va/ai-agents/${agentId}/jobs/${jobId}`);
      if (res.data?.success) {
        setJob(res.data.job);
        setError('');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load session');
    }
  }, [agentId, jobId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [load]);

  const events = Array.isArray(job?.clickTrack) ? job.clickTrack : [];
  const status = job?.status || 'queued';
  const gateCleared = Boolean(job?.progress?.gateCleared);
  const done = ['completed', 'failed', 'cancelled'].includes(status);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [events.length]);

  const closeBrowser = async () => {
    setClosing(true);
    try {
      await api.post(`/admin/va/ai-agents/${agentId}/open-profile/${jobId}/close`);
      toast.success('Close signal sent');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to close');
    } finally {
      setClosing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="w-full max-w-lg rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-xl flex flex-col max-h-[85vh]">
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-800">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <ComputerDesktopIcon className="w-4 h-4 text-indigo-500" />
              Open profile · @{username}
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Browser opens on the VPS — use Remote Desktop to solve. Main-frame clicks are tracked live.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            title="Close panel (browser keeps running until you Close browser)"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 py-2 flex items-center gap-2 flex-wrap text-xs">
          <span className={`px-2 py-0.5 rounded-full font-medium ${jobStatusClass(status)}`}>
            {status}
          </span>
          {gateCleared && (
            <span className="px-2 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
              gate cleared
            </span>
          )}
          <span className="text-gray-500 truncate flex-1 min-w-0">
            {job?.progress?.lastMessage || 'Waiting for desktop agent…'}
          </span>
        </div>

        {error && (
          <div className="px-4 text-xs text-red-600">{error}</div>
        )}

        <div
          ref={listRef}
          className="flex-1 overflow-y-auto px-4 py-2 space-y-1.5 min-h-[200px] border-t border-gray-100 dark:border-gray-800"
        >
          {events.length === 0 ? (
            <p className="text-xs text-gray-500 py-6 text-center">
              No clicks yet — solve on the VPS when the browser appears.
            </p>
          ) : events.map((ev, idx) => (
            <div
              key={`${ev.t || idx}-${ev.kind}-${idx}`}
              className="text-[11px] rounded-md border border-gray-100 dark:border-gray-800 px-2 py-1.5"
            >
              <div className="flex items-center gap-2">
                <span className="uppercase font-semibold text-gray-400 w-12 flex-shrink-0">{ev.kind}</span>
                <span className="font-medium text-gray-800 dark:text-gray-100 truncate">
                  {ev.label || '(no label)'}
                </span>
                {(ev.x != null && ev.y != null) && (
                  <span className="text-gray-400 ml-auto flex-shrink-0">{ev.x},{ev.y}</span>
                )}
              </div>
              {(ev.selector || ev.detail || ev.url) && (
                <div className="mt-0.5 text-gray-500 font-mono truncate">
                  {ev.selector || ev.detail || ev.url}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary !py-1.5 !px-3 text-xs"
          >
            {done ? 'Done' : 'Hide panel'}
          </button>
          {!done && (
            <button
              type="button"
              onClick={closeBrowser}
              disabled={closing}
              className="btn-primary !py-1.5 !px-3 text-xs disabled:opacity-50"
            >
              {closing ? 'Closing…' : 'Close browser'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function jobStatusClass(status) {
  switch (status) {
    case 'preparing': return 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200';
    case 'running': return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200';
    case 'pausing': return 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200';
    case 'paused': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
    case 'completed': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200';
    case 'failed': return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200';
    case 'cancelled': return 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
    default: return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  }
}

/** Human-readable job status label (cancelled → stopped). */
function formatJobStatus(status) {
  if (status === 'cancelled') return 'stopped';
  if (status === 'preparing') return 'preparing…';
  return status || '—';
}

function formatDuration(ms) {
  if (!ms || ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function formatRelative(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  if (Number.isNaN(diff) || diff < 0) return '';
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}

/** Forward-looking sibling of formatRelative ("in 23h 45m", "in 2m"). */
function formatRelativeFuture(ts) {
  if (!ts) return '';
  const diff = new Date(ts).getTime() - Date.now();
  if (Number.isNaN(diff)) return '';
  if (diff <= 0) return 'shortly';
  const s = Math.floor(diff / 1000);
  if (s < 60) return `in ${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `in ${m}m`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  if (h < 24) return remM ? `in ${h}h ${remM}m` : `in ${h}h`;
  const d = Math.floor(h / 24);
  return `in ${d}d`;
}

/**
 * Format ms remaining as "23h 45m" / "12m 09s". Falls back to "ready"
 * when the scheduled time is already past (the scheduler should pick
 * it up on the next tick, ≤60s later).
 */
function formatCountdown(ms) {
  if (ms == null || Number.isNaN(ms)) return '—';
  if (ms <= 0) return 'ready — dispatching shortly';
  const totalS = Math.floor(ms / 1000);
  const h = Math.floor(totalS / 3600);
  const m = Math.floor((totalS % 3600) / 60);
  const s = totalS % 60;
  if (h >= 1) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m >= 1) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

/**
 * Auto-continuation status card — surfaces the daily-cap countdown when
 * the backend has a continuation queued, and lets the operator either
 * cancel just the pending dispatch (keeps feature on) or fully disable
 * the feature for this agent. Re-renders every second so the countdown
 * stays live without needing parent polling.
 */
function AutoContinuationCard({
  agent, job, tsAvailable, tsStoredInSource, tsAlreadyTouched,
  onStopAutomation, onEnable, onCreateAutomation, onForceComplete, onResetCaps
}) {
  const ac = agent?.autoContinuation;
  const dispatchAt = ac?.nextAccountRoundAt || ac?.nextDispatchAt;
  const [now, setNow] = useState(() => Date.now());
  const [showStopModal, setShowStopModal] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [resettingCaps, setResettingCaps] = useState(false);

  useEffect(() => {
    if (!dispatchAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [dispatchAt]);

  const hasSchedule = Boolean(dispatchAt);
  const disabled = ac?.enabled === false;
  // Only "awaiting schedule" after a real live run — not after a failed prepare
  // that left sourceJobId pointing at a dead job.
  const sourceJobOk = job
    && !['failed', 'cancelled'].includes(job.status);
  const awaitingSchedule = !hasSchedule && !disabled
    && ac?.sourceJobId
    && agent?.status === 'idle'
    && !agent?.currentJobId
    && sourceJobOk;

  const dispatchMsRemaining = dispatchAt ? new Date(dispatchAt).getTime() - Date.now() : null;
  const isStaleDispatch = hasSchedule
    && agent?.status === 'idle'
    && !agent?.currentJobId
    && dispatchMsRemaining !== null
    && dispatchMsRemaining < -(5 * 60 * 1000);

  /** Config saved + auto-continuation on, but no job/schedule (e.g. failed Start). */
  const idleArmed = !disabled
    && agent?.status === 'idle'
    && !agent?.currentJobId
    && !hasSchedule
    && !awaitingSchedule;

  if (idleArmed) {
    return (
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-4 py-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 min-w-0">
            <ClockIcon className="w-4 h-4 shrink-0 text-gray-400" />
            <span>
              {job?.status === 'failed'
                ? 'Last start failed — fix the source or try again with Start Automation above.'
                : (
                  <>
                    Automation configured but not running — use <strong>Start Automation</strong> above,
                    or create a fresh run.
                  </>
                )}
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={onCreateAutomation}
              className="btn-primary !py-1 !px-2.5 text-xs"
            >
              + Create Automation
            </button>
            <button
              type="button"
              onClick={() => setShowStopModal(true)}
              className="!py-1 !px-2.5 text-xs rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40 font-medium transition-colors"
              title="Release reserved handles and clear schedule state"
            >
              Stop Automation
            </button>
          </div>
        </div>
        {job?.status === 'failed' && job?.error && (
          <p className="text-xs text-red-600 dark:text-red-400 break-words">{job.error}</p>
        )}
        {showStopModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => !stopping && setShowStopModal(false)}>
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">Stop this automation?</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    This releases reserved handles back to the pool and clears account timers.
                    You can create a fresh automation afterward.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowStopModal(false)}
                  disabled={stopping}
                  className="btn-secondary !py-1.5 !px-4 text-sm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={stopping}
                  onClick={async () => {
                    setStopping(true);
                    try {
                      await onStopAutomation();
                    } finally {
                      setStopping(false);
                      setShowStopModal(false);
                    }
                  }}
                  className="!py-1.5 !px-4 text-sm rounded-lg border border-red-400 dark:border-red-600 bg-red-600 hover:bg-red-700 text-white font-semibold transition-colors disabled:opacity-60"
                >
                  {stopping ? 'Stopping…' : 'Yes, stop automation'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!hasSchedule && !disabled && !awaitingSchedule) return null;
  if (isStaleDispatch && !disabled) return null;

  if (awaitingSchedule) {
    return (
      <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-950/30 px-4 py-3">
        <div className="flex items-center gap-2 text-sm text-amber-900 dark:text-amber-100">
          <ClockIcon className="w-4 h-4 shrink-0" />
          <span>
            Batch complete — building per-profile 24h restart schedule.
            Countdown appears within a minute (or refresh this page).
          </span>
        </div>
      </div>
    );
  }

  if (disabled) {
    return (
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-4 py-4 space-y-3">
        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
          <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" /></svg>
          <span>
            Automation stopped — all account timers cleared and reserved handles released.
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCreateAutomation}
            className="btn-primary !py-1.5 !px-4 text-sm"
          >
            + Create Automation
          </button>
          <button type="button" onClick={onEnable} className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline underline-offset-2">
            Re-enable existing schedule
          </button>
        </div>
      </div>
    );
  }

  const target = new Date(dispatchAt).getTime();
  const remaining = target - now;
  const ready = remaining <= 0;
  const isAgentRunning = agent?.status === 'running' || agent?.status === 'queued';
  const isContinuousLiveJob = Boolean(
    job?.continuousLiveRun
    && agent?.autoContinuation?.liveRunRootJobId
    && String(agent.autoContinuation.liveRunRootJobId) === String(job?._id)
  );
  const dueCount = ac?.dueAccountCount || 0;
  const dueLabel = dueCount > 0 ? `${dueCount.toLocaleString()} accounts` : 'Accounts';

  const titleText = ready
    ? isContinuousLiveJob && isAgentRunning
      ? `${dueLabel} due — refreshing in current job…`
      : `${dueLabel} ready — scheduling next batch…`
    : isAgentRunning
      ? `Automation running · next accounts ready in ${formatCountdown(remaining)}`
      : `Next accounts ready in ${formatCountdown(remaining)}`;

  // ── Per-account status breakdown from current job ──────────────────────
  const accountResults = Array.isArray(job?.accountResults) ? job.accountResults : [];
  const statusCounts = accountResults.reduce((acc, a) => {
    const k = a.status || 'queued';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  // Determine if all entries are still "queued" (seeded but not yet started)
  // or if accountResults is completely empty (legacy worker — no per-account events yet).
  const hasLiveAccountData = accountResults.some(
    (a) => a.status && a.status !== 'queued'
  );
  const jobIsRunning = job?.status === 'running' || job?.status === 'queued' || job?.status === 'preparing';

  // When the VPS hasn't sent per-account events yet, fall back to config-based estimates.
  const maxConcurrent = Number(agent?.config?.maxConcurrentBrowsers || agent?.config?.maxBrowsers) || 0;
  const activeCount = hasLiveAccountData
    ? (statusCounts.running || 0) + (statusCounts.warmup || 0)
    : (jobIsRunning ? maxConcurrent : 0);
  const awaitingRoundCount = statusCounts.round_done || 0;
  const dailyCapCount = (statusCounts.daily_limit || 0) + (statusCounts.completed || 0);
  const failedCount = statusCounts.failed || 0;
  const issueCount = (statusCounts.failed || 0) + (statusCounts.cancelled || 0) + (statusCounts.paused || 0);
  // Total accounts: job totals is the authoritative count; accountResults.length only as fallback
  const totalAccountsInJob = Number(job?.totals?.totalAccounts) || accountResults.length || 0;

  // Total follows across the full automation chain (liveRun aggregates continuation jobs).
  // Fall back to current-job progress if liveRun hasn't been computed yet.
  const totalFollowed = Number(job?.liveRun?.followed ?? job?.progress?.followed)
    || accountResults.reduce((s, a) => s + (Number(a.followed) || 0), 0);
  const totalFailed = Number(job?.liveRun?.failed ?? job?.progress?.failed)
    || accountResults.reduce((s, a) => s + (Number(a.failed) || 0), 0);

  // Scrape session from agent targetSource
  const ts = agent?.targetSource;
  const isSourced = ts?.type === 'scrape_session' || ts?.type === 'scrape_v2_session' || ts?.type === 'scrape_v2_pool' || ts?.type === 'scrape_tag';
  const sessionLabel = ts?.type === 'scrape_v2_pool'
    ? (ts.scrapeTag ? `V2 pool · ${ts.scrapeTag}` : 'V2 global pool')
    : ts?.type === 'scrape_v2_session'
    ? (ts.scrapeSessionId ? `V2: ${ts.scrapeSessionId.slice(0, 18)}…` : 'Scrape V2 session')
    : ts?.type === 'scrape_session'
    ? (ts.scrapeSessionId ? `scraper_${String(ts.scrapeSessionId).slice(-8)}…` : 'Scrape session')
    : ts?.type === 'scrape_tag' ? `Tag: ${ts.scrapeTag}` : null;

  const statBadge = (label, value, color, estimated = false) => (
    <div className={`flex flex-col items-center px-3 py-1.5 rounded-lg border ${color} min-w-[72px]`}>
      <span className="text-base font-bold tabular-nums leading-none">
        {estimated && <span className="text-[11px] font-normal opacity-60 mr-0.5">~</span>}
        {value.toLocaleString()}
      </span>
      <span className="text-[10px] font-medium mt-0.5 opacity-80">{label}</span>
    </div>
  );

  return (
    <div
      className={`rounded-xl border space-y-3 ${
        ready
          ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/30'
          : 'border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-950/30'
      }`}
    >
      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-3 flex-wrap px-4 pt-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
          <ClockIcon className={`w-4 h-4 shrink-0 ${ready ? 'text-emerald-600 dark:text-emerald-300' : 'text-indigo-600 dark:text-indigo-300'}`} />
          <span>{titleText}</span>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {ready && isAgentRunning && onForceComplete && (
            <button
              type="button"
              onClick={onForceComplete}
              className="btn-primary !py-1 !px-2.5 text-xs bg-orange-500 hover:bg-orange-600 border-orange-500"
              title="Force-complete the stuck job so the next wave can dispatch immediately"
            >
              Unstick job
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowStopModal(true)}
            className="!py-1 !px-2.5 text-xs rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40 font-medium transition-colors"
            title="Stop the automation, release reserved handles, and clear all account timers"
          >
            Stop Automation
          </button>
        </div>

        {/* ── Stop confirmation modal ── */}
        {showStopModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => !stopping && setShowStopModal(false)}>
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">Stop this automation?</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    This will cancel the running job, release all reserved handles back to the pool, and clear all per-account 24h timers.
                    You can start a fresh automation at any time.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowStopModal(false)}
                  disabled={stopping}
                  className="btn-secondary !py-1.5 !px-4 text-sm"
                >
                  Keep running
                </button>
                <button
                  type="button"
                  disabled={stopping}
                  onClick={async () => {
                    setStopping(true);
                    try {
                      await onStopAutomation();
                    } finally {
                      setStopping(false);
                      setShowStopModal(false);
                    }
                  }}
                  className="!py-1.5 !px-4 text-sm rounded-lg border border-red-400 dark:border-red-600 bg-red-600 hover:bg-red-700 text-white font-semibold transition-colors disabled:opacity-60"
                >
                  {stopping ? 'Stopping…' : 'Yes, stop automation'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Account cycle summary chips ── */}
      {(ac.scheduledAccountCount > 0 || ac.dueAccountCount > 0) && (
        <div className="flex flex-wrap gap-2 px-4">
          {ac.scheduledAccountCount > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-indigo-400" />
              {ac.scheduledAccountCount.toLocaleString()} accounts on individual 24h cycles
            </span>
          )}
          {ac.dueAccountCount > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              {ac.dueAccountCount.toLocaleString()} due for next daily follow
            </span>
          )}
        </div>
      )}

      {/* ── Per-account status grid ── */}
      {(totalAccountsInJob > 0 || isAgentRunning) && (
        <div className="px-4">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400">
              Account status
            </div>
            <div className="flex items-center gap-2">
              {!hasLiveAccountData && jobIsRunning && (
                <span className="text-[10px] text-gray-400 dark:text-gray-500 italic">
                  Live per-account data arriving as rounds complete
                </span>
              )}
              {onResetCaps && (statusCounts.daily_limit || 0) > 0 && (
                <button
                  type="button"
                  disabled={resettingCaps}
                  onClick={async () => {
                    const n = statusCounts.daily_limit || 0;
                    if (!window.confirm(
                      `Reset the daily cap for ${n.toLocaleString()} capped account(s)? `
                      + 'They get a fresh follow budget and start following again immediately '
                      + 'instead of waiting for their 24h timer.'
                    )) return;
                    setResettingCaps(true);
                    try {
                      await onResetCaps();
                    } finally {
                      setResettingCaps(false);
                    }
                  }}
                  className="inline-flex items-center gap-1 rounded-md border border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-950/30 px-2 py-1 text-[10px] font-semibold text-orange-700 dark:text-orange-300 hover:bg-orange-100 dark:hover:bg-orange-900/40 disabled:opacity-50 disabled:cursor-wait"
                  title="Clear 'Cap reached' for all capped accounts — they roll a fresh daily budget and resume following right away"
                >
                  <ArrowPathIcon className={`w-3 h-3 ${resettingCaps ? 'animate-spin' : ''}`} />
                  {resettingCaps ? 'Resetting…' : 'Reset caps'}
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {statBadge('Active', activeCount,
              'border-indigo-200 dark:border-indigo-700 bg-white dark:bg-indigo-950/20 text-indigo-700 dark:text-indigo-300',
              !hasLiveAccountData && jobIsRunning)}
            {statBadge('Awaiting round', awaitingRoundCount,
              'border-sky-200 dark:border-sky-700 bg-white dark:bg-sky-950/20 text-sky-700 dark:text-sky-300')}
            {statBadge('Cap reached', dailyCapCount,
              'border-orange-200 dark:border-orange-700 bg-white dark:bg-orange-950/20 text-orange-600 dark:text-orange-300')}
            {statBadge('Failed', failedCount,
              'border-red-200 dark:border-red-700 bg-white dark:bg-red-950/20 text-red-600 dark:text-red-300')}
            {issueCount !== failedCount && statBadge('Issues', issueCount,
              'border-amber-200 dark:border-amber-700 bg-white dark:bg-amber-950/20 text-amber-600 dark:text-amber-300')}
            {totalAccountsInJob > 0 && (
              <div className="flex flex-col items-center px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/30 text-gray-600 dark:text-gray-300 min-w-[72px]">
                <span className="text-base font-bold tabular-nums leading-none">{totalAccountsInJob.toLocaleString()}</span>
                <span className="text-[10px] font-medium mt-0.5 opacity-80">Total</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Total follows summary ── */}
      {(totalFollowed > 0 || isAgentRunning) && (
        <div className="px-4">
          <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400 mb-1.5">Follows this batch</div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="text-2xl font-bold tabular-nums text-gray-900 dark:text-white">
              {totalFollowed.toLocaleString()}
            </span>
            <span className="text-sm text-gray-500">follows sent</span>
            {totalFailed > 0 && (
              <span className="text-sm text-red-500 tabular-nums">{totalFailed.toLocaleString()} failed</span>
            )}
            {totalAccountsInJob > 0 && (
              <span className="text-xs text-gray-400 tabular-nums">
                across {totalAccountsInJob.toLocaleString()} accounts
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── Live scrape session ── */}
      {isSourced && (
        <div className="mx-4 mb-0 rounded-lg border border-sky-200 dark:border-sky-800 bg-sky-50/60 dark:bg-sky-950/20 px-3 py-2.5">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 text-[11px] text-sky-700 dark:text-sky-300">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-sky-500 animate-pulse flex-shrink-0" />
              <span className="font-medium">Live scrape session</span>
              {sessionLabel && <span className="font-mono opacity-70">{sessionLabel}</span>}
            </div>
            <a
              href="/dashboard/scraper"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-sky-600 dark:text-sky-400 hover:underline"
            >
              View in Scraper →
            </a>
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
            {tsAvailable != null && tsAvailable >= 0 && (
              <span title={tsAvailable === 0 ? 'All handles in source have been dispatched — scraper is adding more' : undefined}>
                <span className={`font-semibold tabular-nums ${tsAvailable === 0 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-700 dark:text-emerald-300'}`}>
                  {tsAvailable.toLocaleString()}
                </span>
                <span className="text-gray-500 ml-1">
                  {tsAvailable === 0 ? 'new handles (scraper refilling)' : 'available to follow'}
                </span>
              </span>
            )}
            {tsAlreadyTouched != null && (
              <span title="Pool rows this model has already followed (all agents, all accounts)">
                <span className="font-semibold tabular-nums text-gray-700 dark:text-gray-200">{tsAlreadyTouched.toLocaleString()}</span>
                <span className="text-gray-500 ml-1">successfully followed</span>
              </span>
            )}
            {tsStoredInSource != null && (
              <span>
                <span className="font-semibold tabular-nums text-gray-700 dark:text-gray-200">{tsStoredInSource.toLocaleString()}</span>
                <span className="text-gray-500 ml-1">total in source</span>
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── Footer detail ── */}
      <div className="px-4 pb-3">
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          Each account has its own 24h window starting from its <strong className="text-gray-700 dark:text-gray-300">first follow</strong> — accounts do not wait for each other.
          {!ready && dispatchAt && (
            <> Earliest due: <strong className="text-gray-700 dark:text-gray-300">{new Date(target).toLocaleString()}</strong>.</>
          )}
        </p>
        {ac.lastError && (
          <p className="text-[11px] text-red-600 dark:text-red-300 mt-1">⚠ Last dispatch failed: {ac.lastError}</p>
        )}
      </div>
    </div>
  );
}

/**
 * Card representation of a single VA agent job. Shows progress, stats,
 * timing, and continuation context all at a glance. Replaces the prior
 * dense one-line list rows so operators can see job health without
 * having to drill into each.
 */
function RecentJobRow({ job, agent, onContinue, onEdit, onForceStop }) {
  const totalTargets = Number(job.totals?.totalTargets) || 0;
  const totalAccounts = Number(job.totals?.totalAccounts) || 0;
  const followed = Number(job.progress?.followed) || 0;
  const failed = Number(job.progress?.failed) || 0;
  const targetsLeft = Math.max(0, totalTargets - followed - (Number(job.progress?.skipped) || 0));
  const isTerminal = ['completed', 'failed', 'cancelled'].includes(job.status);
  const isActive = ['running', 'pausing', 'paused', 'queued', 'preparing'].includes(job.status);
  const canContinue = isTerminal && targetsLeft > 0;
  const otherJobInFlight = agent.status === 'running' || agent.status === 'pausing' || agent.status === 'paused';

  // Use account budget as the denominator — more meaningful than raw handles count.
  // budget = accounts × profileMax (from configSnapshot or fallback).
  const profileMax = Number(job.configSnapshot?.totalFollowsPerProfileMax
    || job.configSnapshot?.totalFollowsPerProfileMin
    || 55);
  const accountBudget = totalAccounts > 0 ? totalAccounts * profileMax : totalTargets;
  const total = accountBudget || totalTargets; // used for progress bar + display

  const startedAt = job.startedAt || job.createdAt;
  const finishedAt = job.finishedAt || (isTerminal ? job.updatedAt : null);
  const duration = startedAt
    ? formatDuration((finishedAt ? new Date(finishedAt).getTime() : Date.now()) - new Date(startedAt).getTime())
    : null;
  const progressPct = total ? Math.min(100, Math.round((followed / total) * 100)) : 0;

  return (
    <div className={`flex items-center gap-3 px-3 py-2 rounded-lg border text-[11px] ${
      isActive
        ? 'border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-950/20'
        : 'border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900/20 hover:bg-gray-50 dark:hover:bg-gray-800/40'
    }`}>
      {/* Status */}
      <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${jobStatusClass(job.status)}`}>
        {formatJobStatus(job.status)}
      </span>

      {/* ID + continuation */}
      <div className="shrink-0 font-mono text-gray-400 dark:text-gray-500 flex items-center gap-0.5">
        <span title={job._id}>…{String(job._id).slice(-6)}</span>
        {job.continuationOf && (
          <span title={`Continues …${String(job.continuationOf).slice(-6)}`}>
            {' '}↩ …{String(job.continuationOf).slice(-6)}
          </span>
        )}
      </div>

      {/* Time */}
      <span className="shrink-0 text-gray-400 tabular-nums">
        {formatRelative(startedAt)}
        {duration && <span className="ml-1">· {duration}</span>}
      </span>

      {/* Progress bar + numbers */}
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <div className="flex-1 h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progressPct}%` }} />
        </div>
        <span className="shrink-0 tabular-nums text-gray-700 dark:text-gray-200 font-medium">
          {followed.toLocaleString()}
          <span className="text-gray-400 font-normal">
            /{total.toLocaleString()}
            {totalAccounts > 0 && (
              <span className="ml-1">· {totalAccounts.toLocaleString()} accts</span>
            )}
          </span>
        </span>
        {failed > 0 && (
          <span className="shrink-0 tabular-nums text-red-500">{failed.toLocaleString()} failed</span>
        )}
      </div>

      {/* Actions */}
      <div className="shrink-0 flex items-center gap-1">
        {isActive && onForceStop && (
          <button
            type="button"
            onClick={onForceStop}
            className="px-2 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300"
            title="Force-cancel this job"
          >
            Stop
          </button>
        )}
        {canContinue && (
          <button
            type="button"
            onClick={onContinue}
            disabled={otherJobInFlight || gologinTokenBlocksStart(agent)}
            className="px-2 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-700 hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 disabled:opacity-40"
            title={`Continue — ${targetsLeft.toLocaleString()} targets left`}
          >
            Continue
          </button>
        )}
        {canContinue && (
          <button
            type="button"
            onClick={onEdit}
            disabled={otherJobInFlight || gologinTokenBlocksStart(agent)}
            className="px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 disabled:opacity-40"
            title="Edit and continue"
          >
            Edit
          </button>
        )}
      </div>
    </div>
  );
}

function RecentJobCard({ job, agent, onContinue, onEdit, onForceStop }) {
  const total = Number(job.totals?.totalTargets) || 0;
  const totalAccounts = Number(job.totals?.totalAccounts) || 0;
  const followed = Number(job.progress?.followed) || 0;
  const failed = Number(job.progress?.failed) || 0;
  const skipped = Number(job.progress?.skipped) || 0;
  const targetsLeft = Math.max(0, total - followed - skipped);
  const isTerminal = ['completed', 'failed', 'cancelled'].includes(job.status);
  const isActive = ['preparing', 'queued', 'running', 'pausing', 'paused'].includes(job.status);
  const canContinue = isTerminal && targetsLeft > 0;
  const otherJobInFlight = agent.status === 'running' || agent.status === 'pausing' || agent.status === 'paused';

  const progressPct = total ? Math.min(100, Math.round((followed / total) * 100)) : 0;
  const failedPct = total ? Math.min(100 - progressPct, Math.round((failed / total) * 100)) : 0;
  const skippedPct = total ? Math.min(100 - progressPct - failedPct, Math.round((skipped / total) * 100)) : 0;

  const startedAt = job.startedAt || job.createdAt;
  const finishedAt = job.finishedAt || (isTerminal ? job.updatedAt : null);
  const duration = startedAt
    ? formatDuration((finishedAt ? new Date(finishedAt).getTime() : Date.now()) - new Date(startedAt).getTime())
    : null;
  const rate = startedAt && followed > 0
    ? (() => {
        const minutes = ((finishedAt ? new Date(finishedAt).getTime() : Date.now()) - new Date(startedAt).getTime()) / 60000;
        if (minutes <= 0) return null;
        const perMin = followed / minutes;
        if (perMin >= 1) return `${perMin.toFixed(1)}/min`;
        return `${(perMin * 60).toFixed(1)}/hr`;
      })()
    : null;

  return (
    <div className={`relative rounded-xl border bg-white dark:bg-gray-900/40 p-3 space-y-2.5 transition-shadow ${
      isActive
        ? 'border-indigo-300 dark:border-indigo-700 shadow-md shadow-indigo-100/40 dark:shadow-indigo-950/30'
        : 'border-gray-200 dark:border-gray-700 hover:shadow-sm'
    }`}>
      {isActive && (
        <span className="absolute top-2 right-2 inline-flex h-2 w-2 rounded-full bg-indigo-500 animate-pulse" title="Active job" />
      )}

      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${jobStatusClass(job.status)}`}>
              {formatJobStatus(job.status)}
            </span>
            <code className="font-mono text-[10px] text-gray-400 dark:text-gray-500" title={job._id}>
              …{String(job._id).slice(-6)}
            </code>
            {job.continuationOf && (
              <span
                className="font-mono text-[10px] text-gray-400 inline-flex items-center gap-0.5"
                title={`Continues from job …${String(job.continuationOf).slice(-6)}`}
              >
                ↩ …{String(job.continuationOf).slice(-6)}
              </span>
            )}
          </div>
          {startedAt && (
            <div className="mt-1 text-[11px] text-gray-500">
              {formatRelative(startedAt)}
              {duration && <span> · {duration}</span>}
              {rate && <span> · {rate}</span>}
            </div>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex items-baseline justify-between text-[11px] mb-1">
          <span className="text-gray-500">Followed</span>
          <span className="tabular-nums text-gray-900 dark:text-white font-semibold">
            {followed.toLocaleString()} / {total.toLocaleString()}
            <span className="ml-1 text-gray-400 font-normal">({progressPct}%)</span>
          </span>
        </div>
        <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden flex">
          <div
            className="h-full bg-emerald-500 transition-all"
            style={{ width: `${progressPct}%` }}
            title={`${followed.toLocaleString()} followed`}
          />
          <div
            className="h-full bg-red-400 transition-all"
            style={{ width: `${failedPct}%` }}
            title={`${failed.toLocaleString()} failed`}
          />
          <div
            className="h-full bg-amber-300 transition-all"
            style={{ width: `${skippedPct}%` }}
            title={`${skipped.toLocaleString()} skipped`}
          />
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-1.5 text-[11px]">
        <div className="rounded-md bg-emerald-50 dark:bg-emerald-950/30 px-2 py-1">
          <div className="text-emerald-700 dark:text-emerald-300 font-semibold tabular-nums">{followed.toLocaleString()}</div>
          <div className="text-[10px] text-emerald-700/70 dark:text-emerald-400/70">followed</div>
        </div>
        <div className="rounded-md bg-red-50 dark:bg-red-950/30 px-2 py-1">
          <div className="text-red-700 dark:text-red-300 font-semibold tabular-nums">{failed.toLocaleString()}</div>
          <div className="text-[10px] text-red-700/70 dark:text-red-400/70">failed</div>
        </div>
        <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 px-2 py-1">
          <div className="text-amber-700 dark:text-amber-300 font-semibold tabular-nums">{skipped.toLocaleString()}</div>
          <div className="text-[10px] text-amber-700/70 dark:text-amber-400/70">skipped</div>
        </div>
        <div className="rounded-md bg-gray-100 dark:bg-gray-800 px-2 py-1">
          <div className="text-gray-700 dark:text-gray-200 font-semibold tabular-nums">{totalAccounts.toLocaleString()}</div>
          <div className="text-[10px] text-gray-500">accounts</div>
        </div>
      </div>

      {/* Active job context */}
      {isActive && (job.progress?.currentAccountUsername || job.progress?.currentTargetUsername) && (
        <div className="text-[11px] text-gray-600 dark:text-gray-300 truncate">
          {job.progress?.currentAccountUsername && (
            <span className="font-mono">@{job.progress.currentAccountUsername}</span>
          )}
          {job.progress?.currentTargetUsername && (
            <>
              <span className="text-gray-400 mx-1">→</span>
              <span className="font-mono">@{job.progress.currentTargetUsername}</span>
            </>
          )}
        </div>
      )}

      {/* Terminal-with-leftovers helper line — when this exact job is
          the one queued for an auto-continuation, surface the schedule
          inline so the operator can correlate the countdown card above
          with the source row that triggered it. */}
      {isTerminal && targetsLeft > 0 && (
        <div className="text-[11px] flex items-center gap-1 flex-wrap">
          <span className="text-indigo-600 dark:text-indigo-300">
            {targetsLeft.toLocaleString()} target{targetsLeft === 1 ? '' : 's'} left unfollowed
          </span>
          {agent.autoContinuation?.enabled !== false
            && agent.autoContinuation?.sourceJobId === String(job._id)
            && (agent.autoContinuation?.nextAccountRoundAt || agent.autoContinuation?.nextDispatchAt) && (
              <span className="text-gray-500">
                · auto-continues {formatRelativeFuture(
                  agent.autoContinuation.nextAccountRoundAt || agent.autoContinuation.nextDispatchAt
                )}
              </span>
          )}
        </div>
      )}

      {job.error && (
        <div className="text-[11px] text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-950/30 rounded-md px-2 py-1 line-clamp-2" title={job.error}>
          ⚠ {job.error}
        </div>
      )}

      {/* Action row — only renders when there's something to do */}
      {canContinue && (
        <div className="flex items-center gap-2 pt-1 border-t border-gray-100 dark:border-gray-800">
          <button
            type="button"
            onClick={onContinue}
            disabled={otherJobInFlight || gologinTokenBlocksStart(agent)}
            className="flex-1 inline-flex items-center justify-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
            title={
              otherJobInFlight
                ? 'Stop or finish the current job first'
                : `Re-dispatch the ${targetsLeft.toLocaleString()} unfollowed target(s) as a new job`
            }
          >
            <PlayIcon className="w-3.5 h-3.5" />
            Continue ({targetsLeft.toLocaleString()})
          </button>
          <button
            type="button"
            onClick={onEdit}
            disabled={otherJobInFlight || gologinTokenBlocksStart(agent)}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Tweak the targets list or follow config, then dispatch as a continuation"
          >
            <PencilSquareIcon className="w-3.5 h-3.5" />
            Edit
          </button>
        </div>
      )}
      {isActive && onForceStop && (
        <div className="flex items-center gap-2 pt-1 border-t border-gray-100 dark:border-gray-800">
          <button
            type="button"
            onClick={onForceStop}
            className="flex-1 inline-flex items-center justify-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium bg-red-600 text-white hover:bg-red-700"
            title="Force-cancel this job immediately (use if the job is stuck)"
          >
            <StopIcon className="w-3.5 h-3.5" />
            Force Stop
          </button>
        </div>
      )}
    </div>
  );
}

function formatAccountStatus(status) {
  switch (status) {
    case 'round_done': return 'awaiting next round';
    case 'daily_limit': return 'daily limit';
    case 'warmup': return 'warmup';
    case 'running': return 'running';
    case 'completed': return 'completed';
    case 'failed': return 'failed';
    case 'cancelled': return 'stopped';
    case 'paused': return 'paused';
    default: return status || 'queued';
  }
}

function accountStatusClass(status) {
  switch (status) {
    case 'running': return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200';
    case 'warmup': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200';
    case 'round_done': return 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200';
    case 'daily_limit': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-200';
    case 'paused': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
    case 'completed': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200';
    case 'failed': return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200';
    case 'cancelled': return 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
    default: return 'bg-gray-100 text-gray-600 dark:bg-gray-700/60 dark:text-gray-300';
  }
}

// ────────────────────────────────────────────────────────────────────
// Live per-account view of the most recent job
// ────────────────────────────────────────────────────────────────────

function LiveJobPanel({ job, agent, onAddAccounts, addingAccounts }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerFilter, setDrawerFilter] = useState('all');
  const [drawerSearch, setDrawerSearch] = useState('');

  if (!job) {
    return (
      <section className="border border-dashed border-gray-300 dark:border-gray-700 rounded-xl p-6 text-center text-sm text-gray-500">
        No automation running yet — click <span className="font-medium">Start Automation</span> above to begin.
      </section>
    );
  }

  // Terminal jobs (stopped/completed/failed) should not occupy the live panel —
  // show a minimal "nothing running" state instead.
  if (['cancelled', 'completed', 'failed'].includes(job.status)) {
    return (
      <section className="border border-dashed border-gray-300 dark:border-gray-700 rounded-xl px-5 py-6 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Nothing running right now</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Last job{' '}
            <span className={`inline px-1.5 py-0.5 rounded text-[10px] font-medium ${jobStatusClass(job.status)}`}>
              {formatJobStatus(job.status)}
            </span>
            {job.finishedAt && <span className="ml-1">{formatRelative(job.finishedAt)}</span>}
            {' '}· Click <strong>Start Automation</strong> above to resume.
          </p>
          {job.status === 'failed' && job.error && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1.5 break-words">
              {job.error}
            </p>
          )}
        </div>
        <BoltIcon className="w-8 h-8 text-gray-300 dark:text-gray-600 flex-shrink-0" />
      </section>
    );
  }
  const accountResults = Array.isArray(job.accountResults) ? job.accountResults : [];
  const liveRun = job.liveRun || null;
  const followed = liveRun?.followed ?? job.progress?.followed ?? 0;
  const failed = liveRun?.failed ?? job.progress?.failed ?? 0;
  const skipped = liveRun?.skipped ?? job.progress?.skipped ?? 0;
  const batchTargets = job.totals?.totalTargets || 0;
  const totalTargets = liveRun?.progressDenominator || batchTargets;
  const sourceAvailable = liveRun?.sourceAvailable;
  const totalAccounts = job.totals?.totalAccounts || accountResults.length;
  const statusCounts = accountResults.reduce((acc, a) => {
    const k = a.status || 'queued';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const running = (statusCounts.running || 0) + (statusCounts.warmup || 0);
  const awaitingRound = statusCounts.round_done || 0;
  const completed = statusCounts.completed || 0;
  const failedAccounts = statusCounts.failed || 0;
  const pausedAccounts = statusCounts.paused || 0;
  const cancelledAccounts = statusCounts.cancelled || 0;
  const dailyLimitAccounts = statusCounts.daily_limit || 0;
  const queued = Math.max(0, totalAccounts - accountResults.length);
  const progressPct = totalTargets ? Math.min(100, Math.round((followed / totalTargets) * 100)) : 0;
  const failedPct = totalTargets ? Math.min(100 - progressPct, Math.round((failed / totalTargets) * 100)) : 0;
  const skippedPct = totalTargets ? Math.min(100 - progressPct - failedPct, Math.round((skipped / totalTargets) * 100)) : 0;
  const schedule = job.accountSchedule || null;
  const canAddAccounts = typeof onAddAccounts === 'function'
    && ['completed', 'running', 'queued', 'pausing', 'paused'].includes(job.status);

  // Wave context: how many accounts in THIS batch vs. total scheduled across all waves
  const ac = agent?.autoContinuation;
  const scheduledTotal = ac?.scheduledAccountCount || 0;
  const onTimerOutsideBatch = scheduledTotal > totalAccounts ? scheduledTotal - totalAccounts : 0;
  const workerLastSeen = agent?.runtime?.lastSeenAt;
  const workerSeenAgoMs = workerLastSeen ? Date.now() - new Date(workerLastSeen).getTime() : null;

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <BoltIcon className="w-4 h-4" />
          Automation
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${jobStatusClass(job.status)}`}>{formatJobStatus(job.status)}</span>
          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
            Live · continuous
          </span>
          {workerSeenAgoMs !== null && (
            <span className={`text-[10px] font-normal tabular-nums ${workerSeenAgoMs < 90_000 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
              · worker {workerSeenAgoMs < 90_000 ? `online · last seen ${Math.round(workerSeenAgoMs / 1000)}s ago` : `last seen ${Math.round(workerSeenAgoMs / 60000)}m ago`}
            </span>
          )}
        </h4>
        <div className="flex items-center gap-2 flex-wrap">
          {canAddAccounts && (
            <button
              type="button"
              onClick={onAddAccounts}
              disabled={addingAccounts}
              className="btn-secondary !py-1.5 !px-3 text-xs disabled:opacity-50"
              title="Add accounts — they join on their own 24h cycle immediately"
            >
              {addingAccounts ? 'Adding…' : 'Add accounts'}
            </button>
          )}
          <span className="text-[11px] text-gray-500 font-mono">{String(job._id).slice(-6)}</span>
        </div>
      </div>

      {/* Automation summary banner — total accounts across ALL active cycles, not just this batch */}
      {(scheduledTotal > 0 || totalAccounts > 0) && (
        <div className="rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-600 dark:text-gray-300">
          {scheduledTotal > 0 ? (
            <span>
              <span className="font-semibold text-gray-900 dark:text-white">{scheduledTotal.toLocaleString()}</span> accounts in automation
            </span>
          ) : (
            <span>
              <span className="font-semibold text-gray-900 dark:text-white">{totalAccounts.toLocaleString()}</span> accounts active
            </span>
          )}
          {scheduledTotal > totalAccounts && (
            <span className="text-indigo-600 dark:text-indigo-300">
              <span className="font-semibold">{totalAccounts.toLocaleString()}</span> following now
              {' · '}<span className="font-semibold">{(scheduledTotal - totalAccounts).toLocaleString()}</span> on 24h timer
            </span>
          )}
          {totalAccounts > 0 && (() => {
            const cfg = job.configSnapshot || agent?.config || {};
            const profileMax = Number(cfg.totalFollowsPerProfileMax) || Number(cfg.totalFollowsPerProfileMin) || 55;
            const accountBudget = totalAccounts * profileMax;
            return (
              <span>
                <span className="font-semibold">{accountBudget.toLocaleString()}</span> follow capacity
                <span className="text-gray-400"> · {totalAccounts.toLocaleString()} accounts × {profileMax} max/account</span>
              </span>
            );
          })()}
          <span className="text-[10px] text-gray-400 italic">Each account restarts 24h after its first follow</span>
        </div>
      )}

      {/* Scraper session link */}
      {agent.targetSource?.type === 'scrape_v2_session' && agent.targetSource?.scrapeSessionId && (
        <a
          href="/dashboard/scraper-v2"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/30 text-[11px] text-sky-700 dark:text-sky-300 hover:bg-sky-100 dark:hover:bg-sky-900/40 transition-colors"
        >
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-sky-500 animate-pulse" />
          Scraper V2 session linked — {(sourceAvailable ?? 0).toLocaleString()} handles ready → View in Scraper V2
        </a>
      )}
      {agent.targetSource?.type === 'scrape_session' && agent.targetSource?.scrapeSessionId && (
        <a
          href="/dashboard/scraper"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/30 text-[11px] text-sky-700 dark:text-sky-300 hover:bg-sky-100 dark:hover:bg-sky-900/40 transition-colors"
        >
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-sky-500 animate-pulse" />
          Live scrape session linked — {(sourceAvailable ?? 0).toLocaleString()} handles ready → View in Scraper
        </a>
      )}
      {agent.targetSource?.type === 'scrape_tag' && (
        <a
          href="/dashboard/scraper"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/30 text-[11px] text-sky-700 dark:text-sky-300 hover:bg-sky-100 dark:hover:bg-sky-900/40 transition-colors"
        >
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-sky-500 animate-pulse" />
          Scrape tag &quot;{agent.targetSource.scrapeTag}&quot; linked — {(sourceAvailable ?? 0).toLocaleString()} handles ready → View in Scraper
        </a>
      )}

      {/* Overall progress bar — replaces the 500-row scroll list as the
          default display so a 500+ account run no longer grows endlessly. */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-4 space-y-3">
        {/* Daily follows progress */}
        {totalAccounts > 0 && (() => {
          const profileMax = agent.config?.totalFollowsPerProfileMax || agent.config?.totalFollowsPerProfileMin || 60;
          const profileMin = agent.config?.totalFollowsPerProfileMin || 45;
          const accountedCap = accountResults.reduce((sum, a) => {
            const budget = Number(a.profileBudgetTotal) || 0;
            return sum + (budget > 0 ? budget : profileMax);
          }, 0);
          const unstartedCount = Math.max(0, totalAccounts - accountResults.length);
          const dailyCapTotal = accountedCap + unstartedCount * profileMax;
          const dailyFollowedTotal = accountResults.reduce((sum, a) => sum + (Number(a.followed) || 0), 0);
          const dailyPct = dailyCapTotal ? Math.min(100, Math.round((dailyFollowedTotal / dailyCapTotal) * 100)) : 0;
          const atDailyLimit = dailyLimitAccounts;
          const atCompleted = completed;
          const donePct = totalAccounts ? Math.min(100, Math.round(((atDailyLimit + atCompleted) / totalAccounts) * 100)) : 0;
          return (
            <div className="space-y-1.5 pb-2 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-between text-[11px]">
                <span className="font-medium text-gray-700 dark:text-gray-200">Daily follows today</span>
                <span className="tabular-nums text-gray-900 dark:text-white font-semibold">
                  {dailyFollowedTotal.toLocaleString()} / {dailyCapTotal.toLocaleString()}
                  <span className="ml-1 text-gray-400 font-normal">({dailyPct}%)</span>
                </span>
              </div>
              {/* Stacked bar: green = follows sent, orange = remaining cap of daily-limit accounts */}
              <div className="h-2.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden flex">
                <div
                  className="h-full bg-emerald-500 transition-all"
                  style={{ width: `${dailyPct}%` }}
                  title={`${dailyFollowedTotal.toLocaleString()} follows sent`}
                />
              </div>
              <div className="flex items-center justify-between text-[10px]">
                <div className="flex items-center gap-2 text-gray-500">
                  <span>~{profileMin}–{profileMax} per account</span>
                </div>
                <div className="flex items-center gap-2">
                  {running > 0 && (
                    <span className="text-indigo-600 dark:text-indigo-300 font-medium">{running} active</span>
                  )}
                  {awaitingRound > 0 && (
                    <span className="text-sky-500">{awaitingRound} awaiting round</span>
                  )}
                  {atDailyLimit > 0 && (
                    <span className="text-orange-500 font-medium">
                      {atDailyLimit.toLocaleString()} hit daily cap
                      {donePct > 0 ? ` · ${donePct}% done` : ''}
                    </span>
                  )}
                  {atCompleted > 0 && !atDailyLimit && (
                    <span className="text-emerald-600 font-medium">{atCompleted.toLocaleString()} completed</span>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400">
              Overall progress
            </div>
            <div className="mt-0.5 flex items-baseline gap-2">
              <span className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
                {followed.toLocaleString()}
              </span>
              <span className="text-sm text-gray-500">
                / {totalTargets.toLocaleString()} targets · <span className="font-semibold tabular-nums">{progressPct}%</span>
              </span>
            </div>
            {sourceAvailable != null && (
              <p className="text-[11px] text-gray-500 mt-1">
                {sourceAvailable.toLocaleString()} recipients ready in linked scrape
              </p>
            )}
            {schedule?.nextRoundAt && (
              <p className="text-[11px] text-sky-600 dark:text-sky-400 mt-0.5">
                Next account round: {new Date(schedule.nextRoundAt).toLocaleString()}
                {schedule.dueCount > 0 ? ` · ${schedule.dueCount} due now` : ''}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="btn-secondary !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5"
            title="Per-account progress — each account runs its own 24h follow cycle"
          >
            <ChartBarIcon className="w-4 h-4" /> Per-account ({totalAccounts.toLocaleString()})
          </button>
        </div>
        <div className="h-3 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden flex">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all"
            style={{ width: `${progressPct}%` }}
            title={`${followed.toLocaleString()} followed`}
          />
          <div
            className="h-full bg-gradient-to-r from-red-400 to-red-500 transition-all"
            style={{ width: `${failedPct}%` }}
            title={`${failed.toLocaleString()} failed`}
          />
          <div
            className="h-full bg-gradient-to-r from-amber-300 to-amber-400 transition-all"
            style={{ width: `${skippedPct}%` }}
            title={`${skipped.toLocaleString()} skipped`}
          />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <LiveStat label="Followed" value={followed} hint={`of ${totalTargets.toLocaleString()} targets`} variant="emerald" />
          <LiveStat label="Failed" value={failed} variant="red" />
          <LiveStat label="Skipped" value={skipped} hint="already following" variant="amber" />
          <LiveStat
            label="Accounts"
            value={totalAccounts}
            hint={`${running} active${awaitingRound ? ` · ${awaitingRound} waiting round` : ''}`}
            variant="indigo"
          />
          <LiveStat
            label="Cap reached"
            value={accountResults.length === 0 && (job.status === 'queued' || job.status === 'running' || job.status === 'preparing')
              ? (job.status === 'preparing' ? 'Preparing…' : 'Starting…')
              : `${completed + dailyLimitAccounts}/${totalAccounts}`}
            hint={accountResults.length === 0
              ? (job.status === 'preparing'
                ? 'Claiming targets…'
                : `${totalAccounts.toLocaleString()} accounts queued`)
              : failedAccounts
                ? `${failedAccounts} failed`
                : queued
                  ? `${queued} queued`
                  : dailyLimitAccounts
                    ? `${dailyLimitAccounts} hit daily cap · restart on 24h timer`
                    : 'all dispatched'}
            variant="slate"
          />
        </div>
      </div>

      {/* Account status chips — quick visual without the full per-account list */}
      {accountResults.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          <StatusChip count={running} label="active" variant="indigo" pulse />
          <StatusChip count={awaitingRound} label="awaiting round" variant="sky" />
          <StatusChip count={dailyLimitAccounts} label="daily limit" variant="amber" />
          <StatusChip count={completed} label="completed" variant="emerald" />
          <StatusChip count={failedAccounts} label="failed" variant="red" />
          <StatusChip count={pausedAccounts} label="paused" variant="amber" />
          <StatusChip count={cancelledAccounts} label="cancelled" variant="slate" />
          <StatusChip count={queued} label="queued" variant="slate" />
        </div>
      )}

      {accountResults.length === 0 && (() => {
        const ageMs = job.createdAt ? Date.now() - new Date(job.createdAt).getTime() : Infinity;
        const isFresh = ageMs < 3 * 60 * 1000; // < 3 min
        const workerLastSeen = agent.runtime?.lastSeenAt;
        const workerSeenAgoMs = workerLastSeen ? Date.now() - new Date(workerLastSeen).getTime() : null;
        const workerOnline = workerSeenAgoMs !== null && workerSeenAgoMs < 90_000;

        if (job.status === 'preparing') {
          return (
            <div className="rounded-xl border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/20 px-4 py-3 flex items-start gap-3">
              <span className="mt-0.5 inline-block h-2 w-2 rounded-full bg-sky-500 animate-pulse shrink-0" />
              <div className="text-xs text-sky-800 dark:text-sky-200 space-y-0.5">
                <p className="font-medium">Preparing targets — claiming recipients from the pool…</p>
                <p className="text-sky-600 dark:text-sky-300">
                  The desktop agent will start once the queue is ready.
                </p>
              </div>
            </div>
          );
        }

        if (job.status === 'running' || (job.status === 'queued' && isFresh && workerOnline)) {
          return (
            <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950/20 px-4 py-3 flex items-start gap-3">
              <span className="mt-0.5 inline-block h-2 w-2 rounded-full bg-indigo-500 animate-pulse shrink-0" />
              <div className="text-xs text-indigo-800 dark:text-indigo-200 space-y-0.5">
                <p className="font-medium">Desktop agent is running — first account progress appears within ~30 seconds.</p>
                {workerLastSeen && (
                  <p className="text-indigo-600 dark:text-indigo-300">
                    Worker last seen {Math.round(workerSeenAgoMs / 1000)}s ago · {totalAccounts.toLocaleString()} accounts queued
                  </p>
                )}
              </div>
            </div>
          );
        }

        if (job.status === 'queued' && isFresh) {
          return (
            <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20 px-4 py-3 flex items-start gap-3">
              <span className="mt-0.5 inline-block h-2 w-2 rounded-full bg-amber-400 animate-pulse shrink-0" />
              <p className="text-xs text-amber-900 dark:text-amber-200">
                Job dispatched — desktop agent will pick it up momentarily.
                {workerLastSeen && !workerOnline && (
                  <span className="ml-1 text-amber-700 dark:text-amber-300">
                    (Worker last seen {Math.round(workerSeenAgoMs / 60000)}m ago — may be restarting)
                  </span>
                )}
              </p>
            </div>
          );
        }

        return (
          <div className="border border-dashed border-amber-300 dark:border-amber-700 rounded-xl px-4 py-4 text-center space-y-1">
            <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
              Job queued — desktop agent has not picked it up yet
            </p>
            <p className="text-[11px] text-gray-500">
              {workerLastSeen
                ? `Worker last seen ${Math.round(workerSeenAgoMs / 60000)}m ago — check the VPS is online and the agent process is running.`
                : 'No heartbeat received — verify the desktop agent VPS is running and connected.'}
            </p>
          </div>
        );
      })()}

      {drawerOpen && (
        <AccountDrawer
          accountResults={accountResults}
          jobId={String(job._id)}
          filter={drawerFilter}
          onFilterChange={setDrawerFilter}
          search={drawerSearch}
          onSearchChange={setDrawerSearch}
          onClose={() => setDrawerOpen(false)}
        />
      )}
    </section>
  );
}

function StatusChip({ count, label, variant = 'slate', pulse = false }) {
  if (!count) return null;
  const colors = {
    indigo: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200',
    sky: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200',
    emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
    red: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
    amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200',
    slate: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
  }[variant];
  const dotColors = {
    indigo: 'bg-indigo-500',
    sky: 'bg-sky-400',
    emerald: 'bg-emerald-500',
    red: 'bg-red-500',
    amber: 'bg-amber-500',
    slate: 'bg-gray-400'
  }[variant];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${colors}`}>
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${dotColors} ${pulse ? 'animate-pulse' : ''}`} />
      <span className="tabular-nums">{count.toLocaleString()}</span> {label}
    </span>
  );
}

/**
 * Slide-over drawer that lists per-account live progress. Opens on demand
 * so 500+ account runs don't render hundreds of cards in the main panel.
 * Includes a status filter and search box so operators can drill into a
 * specific subset without scrolling through the whole roster.
 */
function AccountDrawer({ accountResults, jobId, filter, onFilterChange, search, onSearchChange, onClose }) {
  const lowerSearch = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    return accountResults.filter((a) => {
      if (filter !== 'all') {
        if (filter === 'active') {
          if (a.status !== 'running' && a.status !== 'warmup') return false;
        } else if (filter === 'awaiting') {
          if (a.status !== 'round_done') return false;
        } else if (filter === 'daily_limit') {
          if (a.status !== 'daily_limit') return false;
        } else if (filter === 'completed') {
          if (a.status !== 'completed') return false;
        } else if (filter === 'failed') {
          if (a.status !== 'failed') return false;
        } else if (filter === 'issues') {
          if (a.status !== 'failed' && a.status !== 'cancelled' && a.status !== 'paused') return false;
        }
      }
      if (lowerSearch && !(a.username || '').toLowerCase().includes(lowerSearch)) return false;
      return true;
    });
  }, [accountResults, filter, lowerSearch]);

  const filters = [
    { id: 'all', label: 'All', count: accountResults.length },
    { id: 'active', label: 'Active', count: accountResults.filter((a) => a.status === 'running' || a.status === 'warmup').length },
    { id: 'awaiting', label: 'Awaiting round', count: accountResults.filter((a) => a.status === 'round_done').length },
    { id: 'daily_limit', label: 'Cap reached · on timer', count: accountResults.filter((a) => a.status === 'daily_limit').length },
    { id: 'completed', label: 'Completed', count: accountResults.filter((a) => a.status === 'completed').length },
    { id: 'failed', label: 'Failed', count: accountResults.filter((a) => a.status === 'failed').length },
    { id: 'issues', label: 'Issues', count: accountResults.filter((a) => a.status === 'failed' || a.status === 'cancelled' || a.status === 'paused').length }
  ];

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/50" onClick={onClose} aria-hidden="true" />
      <aside className="w-full sm:w-[28rem] md:w-[34rem] bg-white dark:bg-gray-900 shadow-2xl flex flex-col border-l border-gray-200 dark:border-gray-700">
        <header className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-950/40 dark:to-violet-950/30">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Per-account progress</h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              {filtered.length.toLocaleString()} of {accountResults.length.toLocaleString()} accounts · each runs its own 24h follow cycle
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-white/60 dark:hover:bg-gray-800/60 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            title="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </header>

        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 space-y-2">
          <input
            type="search"
            placeholder="Search by @handle…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="form-input text-xs w-full"
          />
          <div className="flex flex-wrap gap-1">
            {filters.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => onFilterChange(f.id)}
                className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                  filter === f.id
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {f.label}
                <span className={`ml-1 tabular-nums ${filter === f.id ? 'text-indigo-100' : 'text-gray-400'}`}>
                  {f.count.toLocaleString()}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {filtered.length === 0 ? (
            <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-xl px-3 py-6 text-center text-xs text-gray-500">
              No accounts match this filter.
            </div>
          ) : filtered.map((acc) => (
            <AccountLiveCard key={`${acc.accountId || acc.username}`} acc={acc} jobId={jobId} />
          ))}
        </div>
      </aside>
    </div>
  );
}

function LiveStat({ label, value, hint, variant = 'slate' }) {
  const colors = {
    emerald: 'border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300',
    red: 'border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300',
    amber: 'border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300',
    indigo: 'border-indigo-200 dark:border-indigo-900 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300',
    slate: 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 text-gray-700 dark:text-gray-200'
  }[variant];
  return (
    <div className={`rounded-xl border p-2 ${colors}`}>
      <div className="text-[10px] font-medium uppercase tracking-wide opacity-70">{label}</div>
      <div className="mt-0.5 text-lg font-bold tabular-nums">{typeof value === 'number' ? value.toLocaleString() : value}</div>
      {hint && <div className="text-[10px] opacity-70 mt-0.5">{hint}</div>}
    </div>
  );
}

function AccountLiveCard({ acc, jobId }) {
  const [expanded, setExpanded] = useState(false);
  const [events, setEvents] = useState(null);
  const [eventsLoading, setEventsLoading] = useState(false);

  const handleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && events === null && !eventsLoading && jobId && acc.accountId) {
      setEventsLoading(true);
      api.get(`/admin/va/jobs/${jobId}/accounts/${acc.accountId}/events`)
        .then(r => setEvents(r.data))
        .catch(() => setEvents({ followed: [], skipped: [] }))
        .finally(() => setEventsLoading(false));
    }
  };

  const followedList = events?.followed || [];
  const skippedList = events?.skipped || [];
  const failuresList = acc.failures || [];
  const budget = acc.profileBudgetTotal || acc.plannedTargets || 0;
  const followed = acc.followed || 0;
  const failed = acc.failed || 0;
  const skipped = acc.skipped || 0;
  const attempted = followed + failed + skipped;
  const total = budget || (followedList.length + failed + skippedList.length);
  const followPct = total ? Math.min(100, Math.round((followed / total) * 100)) : 0;
  const failPct = total ? Math.min(100 - followPct, Math.round((failed / total) * 100)) : 0;
  const isActive = acc.status === 'running' || acc.status === 'warmup';
  const isWaitingRound = acc.status === 'round_done';
  const isDailyLimit = acc.status === 'daily_limit';
  const dotClass = isActive ? 'bg-indigo-500 animate-pulse'
    : isWaitingRound ? 'bg-sky-400'
    : isDailyLimit ? 'bg-orange-400'
    : acc.status === 'completed' ? 'bg-emerald-500'
    : acc.status === 'failed' ? 'bg-red-500' : 'bg-gray-400';
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={handleExpand}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-900/40"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className={`inline-block h-2 w-2 rounded-full flex-shrink-0 ${dotClass}`} />
          <span className="font-mono text-sm font-medium truncate">@{acc.username || '—'}</span>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${accountStatusClass(acc.status)}`}>
            {formatAccountStatus(acc.status)}
          </span>
          {acc.currentTargetUsername && isActive && (
            <span className="text-xs text-gray-500 truncate">→ @{acc.currentTargetUsername}</span>
          )}
          {isDailyLimit && (
            <span className="text-[10px] text-orange-500 dark:text-orange-400 font-medium">↺ 24h timer</span>
          )}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 text-xs">
          {/* Follows sent / daily cap */}
          <span className="text-emerald-600 dark:text-emerald-400 font-medium tabular-nums">
            <CheckCircleIcon className="w-3.5 h-3.5 inline-block mr-0.5 -mt-0.5" />
            {followed}
          </span>
          {failed > 0 && (
            <span className="text-red-600 dark:text-red-400 tabular-nums">
              <ExclamationCircleIcon className="w-3.5 h-3.5 inline-block mr-0.5 -mt-0.5" />
              {failed}
            </span>
          )}
          {skipped > 0 && (
            <span className="text-amber-600 dark:text-amber-400 tabular-nums">skip {skipped}</span>
          )}
          {/* Budget fraction — e.g. "48 / 55 cap" */}
          {budget > 0 && (
            <span className={`tabular-nums font-medium ${followed >= budget ? 'text-orange-500 dark:text-orange-400' : 'text-gray-500 dark:text-gray-400'}`}>
              {followed}/{budget} cap
            </span>
          )}
          <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </button>
      {total > 0 && (
        <div className="h-1.5 bg-gray-100 dark:bg-gray-800 flex">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${followPct}%` }} />
          <div className="h-full bg-red-400 transition-all" style={{ width: `${failPct}%` }} />
        </div>
      )}
      {expanded && (
        <div className="px-3 py-3 bg-gray-50/60 dark:bg-gray-900/30 border-t border-gray-200 dark:border-gray-700 space-y-3">
          {eventsLoading && (
            <p className="text-[11px] text-gray-400 animate-pulse">Loading follow history…</p>
          )}
          {!eventsLoading && followedList.length > 0 && (
            <UsernameList
              label={`Followed (${followedList.length})`}
              variant="emerald"
              items={followedList.map((u) => ({ username: u }))}
            />
          )}
          {!eventsLoading && failuresList.length > 0 && (
            <UsernameList
              label={`Failed (${failuresList.length})`}
              variant="red"
              items={failuresList}
            />
          )}
          {!eventsLoading && skippedList.length > 0 && (
            <UsernameList
              label={`Skipped — already following (${skippedList.length})`}
              variant="amber"
              items={skippedList.map((u) => ({ username: u }))}
            />
          )}
          {(acc.errors || []).length > 0 && (
            <div className="text-[11px] text-red-600 dark:text-red-300 space-y-0.5">
              {(acc.errors || []).map((e, i) => (
                <div key={i} className="truncate">⚠ {e}</div>
              ))}
            </div>
          )}
          {acc.startedAt && (
            <div className="text-[10px] text-gray-500">
              started {new Date(acc.startedAt).toLocaleTimeString()}
              {acc.finishedAt && ` · finished ${new Date(acc.finishedAt).toLocaleTimeString()}`}
            </div>
          )}
          {isDailyLimit && acc.firstFollowAt && (
            <div className="text-[10px] text-orange-500 dark:text-orange-400">
              ↺ Daily cap reached · next follow after{' '}
              {new Date(new Date(acc.firstFollowAt).getTime() + 24 * 60 * 60 * 1000).toLocaleString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Collapsed-by-default username list used inside the per-account live
 * card. Always shows the most recent N entries (newest first); the
 * rest are tucked behind a "Show more" toggle and revealed inline
 * with a "Show less" button at the bottom. Keeps the live panel
 * compact even when an account has followed thousands of handles.
 */
const USERNAME_PREVIEW_COUNT = 8;
function UsernameList({ label, items, variant = 'slate' }) {
  const [expanded, setExpanded] = useState(false);
  const colors = {
    emerald: 'text-emerald-700 dark:text-emerald-300',
    red: 'text-red-700 dark:text-red-300',
    amber: 'text-amber-700 dark:text-amber-300',
    slate: 'text-gray-700 dark:text-gray-300'
  }[variant];
  const accentBg = {
    emerald: 'hover:bg-emerald-50 dark:hover:bg-emerald-950/30',
    red: 'hover:bg-red-50 dark:hover:bg-red-950/30',
    amber: 'hover:bg-amber-50 dark:hover:bg-amber-950/30',
    slate: 'hover:bg-gray-100 dark:hover:bg-gray-800'
  }[variant];

  // Newest entries first — backend appends to the array, so reversing
  // surfaces the most recent (current) follow at the top of the list.
  const ordered = useMemo(() => {
    const list = Array.isArray(items) ? items.slice().reverse() : [];
    return list;
  }, [items]);
  const total = ordered.length;
  const visible = expanded ? ordered : ordered.slice(0, USERNAME_PREVIEW_COUNT);
  const hiddenCount = Math.max(0, total - visible.length);

  return (
    <div>
      <div className={`text-[11px] font-semibold uppercase tracking-wide mb-1 ${colors}`}>{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {visible.map((it, i) => (
          <span
            key={`${it.username}-${i}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-[11px] font-mono"
            title={it.reason || ''}
          >
            @{it.username}
            {it.reason && <span className="text-[9px] text-red-500 truncate max-w-[140px]">— {it.reason}</span>}
          </span>
        ))}
      </div>
      {(hiddenCount > 0 || expanded) && (
        <div className="mt-1.5">
          {!expanded && hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-md border border-gray-200 dark:border-gray-700 ${colors} ${accentBg}`}
            >
              <ChevronDownIcon className="w-3 h-3" />
              Show {hiddenCount.toLocaleString()} more
            </button>
          )}
          {expanded && (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-md border border-gray-200 dark:border-gray-700 ${colors} ${accentBg}`}
            >
              <ChevronUpIcon className="w-3 h-3" />
              Show less
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Edit-and-continue modal — opens from a finished job's row in
// Recent jobs. Loads that job's leftover handles (the same list
// /continue would dispatch) into an editable textarea + lets the
// operator tweak the most-used follow-config knobs before dispatching
// a continuation job. The edits live ONLY on the new job's
// configSnapshot; the agent's persisted config is untouched.
// ────────────────────────────────────────────────────────────────────

function EditJobModal({ agent, jobId, onClose, onDispatched }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [targetsText, setTargetsText] = useState('');
  const [originalLeftover, setOriginalLeftover] = useState(0);
  const [followedAlready, setFollowedAlready] = useState(0);
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  const parsedTargets = useMemo(
    () => parseUsernameList(targetsText),
    [targetsText]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/admin/va/ai-agents/${agent._id}/jobs/${jobId}/leftover`);
        if (cancelled) return;
        const data = res.data || {};
        if (data.legacy) {
          setError('This job was dispatched before per-target persistence was added — start a fresh run instead.');
          return;
        }
        const handles = Array.isArray(data.targets) ? data.targets : [];
        setTargetsText(handles.join('\n'));
        setOriginalLeftover(handles.length);
        setFollowedAlready(Number(data.followedAlready) || 0);
        // Seed config from the job's snapshot when available so the
        // operator edits start from "what this job ran with", not
        // necessarily the agent's current config.
        setConfig({
          ...DEFAULT_CONFIG,
          ...(agent.config || {}),
          ...(data.configSnapshot || {})
        });
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load job details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [agent._id, jobId, agent.config]);

  const dispatch = async () => {
    if (!parsedTargets.length) {
      toast.error('No valid handles in the target list');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post(`/admin/va/ai-agents/${agent._id}/continue/${jobId}`, {
        targets: parsedTargets,
        config
      });
      if (res.data?.alreadyDone) {
        toast.success('Edited list ended up empty after cleanup — nothing to dispatch');
      } else {
        toast.success(`Continuation dispatched — ${parsedTargets.length.toLocaleString()} targets queued`);
      }
      onDispatched();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to dispatch continuation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Edit & continue job</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-mono">
              source: …{String(jobId).slice(-6)} · agent: {agent.name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {loading && (
            <div className="text-center text-sm text-gray-500 py-8">Loading…</div>
          )}
          {error && !loading && (
            <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg p-3">
              {error}
            </div>
          )}
          {!loading && !error && (
            <>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300">already done</div>
                  <div className="text-base font-bold text-emerald-700 dark:text-emerald-200 tabular-nums">{followedAlready.toLocaleString()}</div>
                </div>
                <div className="rounded-lg border border-indigo-200 dark:border-indigo-900 bg-indigo-50 dark:bg-indigo-950/30 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-indigo-700 dark:text-indigo-300">leftover loaded</div>
                  <div className="text-base font-bold text-indigo-700 dark:text-indigo-200 tabular-nums">{originalLeftover.toLocaleString()}</div>
                </div>
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-gray-600 dark:text-gray-300">after your edits</div>
                  <div className="text-base font-bold text-gray-700 dark:text-gray-200 tabular-nums">{parsedTargets.length.toLocaleString()}</div>
                </div>
              </div>

              {/* Targets editor */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Targets to follow
                  </label>
                  <div className="flex items-center gap-3 text-[11px]">
                    <span className="text-gray-500">
                      {parsedTargets.length.toLocaleString()} valid handle{parsedTargets.length === 1 ? '' : 's'}
                    </span>
                    <label className="inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 cursor-pointer">
                      <input
                        type="file"
                        accept=".txt,.csv,text/plain,text/csv"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (!file) return;
                          if (file.size > 5 * 1024 * 1024) {
                            toast.error('File too large — keep it under 5 MB');
                            return;
                          }
                          try {
                            const text = await file.text();
                            const parsed = parseUsernameList(text);
                            if (!parsed.length) {
                              toast.error('No valid X handles found in the file');
                              return;
                            }
                            const merged = parseUsernameList(`${targetsText}\n${text}`);
                            setTargetsText(merged.join('\n'));
                            toast.success(`Loaded ${parsed.length.toLocaleString()} handles from ${file.name}`);
                          } catch (err2) {
                            toast.error(`Failed to read ${file.name}: ${err2.message}`);
                          }
                        }}
                      />
                      <ArrowUpTrayIcon className="w-3.5 h-3.5" />
                      Upload .txt
                    </label>
                    {targetsText && (
                      <button
                        type="button"
                        onClick={() => setTargetsText('')}
                        className="text-gray-500 hover:text-rose-600"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
                <textarea
                  value={targetsText}
                  onChange={(e) => setTargetsText(e.target.value)}
                  rows={10}
                  className="form-input text-xs font-mono w-full leading-snug"
                  placeholder="one handle per line, comma or space separated"
                />
                <p className="mt-1 text-[11px] text-gray-500">
                  Pre-filled with the {originalLeftover.toLocaleString()} handle(s) the source job didn't follow.
                  Drop / add / replace anything; invalid handles are silently dropped on save.
                </p>
              </div>

              {/* Compact config tweaks — the ones operators most often
                  retune between attempts. The full Setup tab can still
                  edit everything; this is just a fast path. */}
              {config && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Run config (this job only)</h4>
                  <RunJobConfigFields config={config} onChange={setConfig} />
                  <p className="mt-2 text-[11px] text-gray-500">
                    These edits apply <strong>only to the continuation job</strong>.
                    The agent&apos;s Setup config stays exactly as it was.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="btn-secondary text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={dispatch}
            disabled={saving || loading || !parsedTargets.length || gologinTokenBlocksStart(agent)}
            className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-40"
            title={gologinTokenBlocksStart(agent) ? 'Set the GoLogin API token first' : ''}
          >
            <PlayIcon className="w-4 h-4" />
            {saving ? 'Dispatching…' : `Start with edits (${parsedTargets.length.toLocaleString()})`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Attach accounts modal
// ────────────────────────────────────────────────────────────────────

function AttachAccountsModal({ agent, vaUsers, onClose, onAttached }) {
  const [vaFilter, setVaFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const attachedSet = useMemo(
    () => new Set((agent.accountIds || []).map(String)),
    [agent.accountIds]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: 1,
        limit: 200,
        status: 'all'
      };
      if (vaFilter !== 'all') params.vaId = vaFilter;
      if (search.trim()) params.search = search.trim();
      const res = await api.get('/admin/va/profile-accounts', { params });
      const list = res.data.items || res.data.accounts || [];
      setItems(list);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, [vaFilter, search]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    if (attachedSet.has(String(id))) return;
    setSelected((prev) => {
      const next = new Set(prev);
      const sid = String(id);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  };

  const attach = async () => {
    if (!selected.size) return;
    setSaving(true);
    try {
      const res = await api.post(`/admin/va/ai-agents/${agent._id}/attach-accounts`, {
        accountIds: Array.from(selected)
      });
      if (res.data.success) {
        const n = res.data.attached || selected.size;
        const lr = res.data.liveRunAdd;
        if (lr?.ok) {
          const added = lr.addedAccountIds?.length || n;
          toast.success(
            lr.queued
              ? `Attached ${n} — ${added} queued for GoLogin when the current job finishes`
              : `Attached ${n} — ${added} added to the live run`
          );
        } else {
          toast.success(`Attached ${n} account(s)`);
        }
        await onAttached();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to attach');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl my-8">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white">Attach accounts to {agent.name}</h3>
          <button type="button" onClick={onClose}><XMarkIcon className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={vaFilter}
              onChange={(e) => setVaFilter(e.target.value)}
              className="form-select text-sm"
            >
              <option value="all">All VAs</option>
              {vaUsers.map((va) => (
                <option key={va._id} value={va._id}>{va.vaProfile?.displayName || va.username}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Search username…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="form-input text-sm flex-1"
            />
            <button onClick={load} className="btn-secondary text-sm">Refresh</button>
          </div>
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl divide-y divide-gray-100 dark:divide-gray-700 max-h-96 overflow-y-auto">
            {loading && <div className="p-4 text-sm text-gray-500 text-center">Loading…</div>}
            {!loading && items.length === 0 && (
              <div className="p-4 text-sm text-gray-500 text-center">No accounts match</div>
            )}
            {items.map((acc) => {
              const sid = String(acc._id);
              const already = attachedSet.has(sid);
              const sel = selected.has(sid);
              return (
                <label
                  key={acc._id}
                  className={`flex items-center gap-3 px-3 py-2 text-sm ${already ? 'opacity-60' : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/30'}`}
                >
                  <input
                    type="checkbox"
                    checked={already || sel}
                    disabled={already}
                    onChange={() => toggle(acc._id)}
                    className="rounded"
                  />
                  <span className="font-mono flex-1">@{acc.username}</span>
                  {!acc.gologinProfileId && <span className="text-[10px] uppercase text-amber-600">no GoLogin</span>}
                  {acc.status !== 'active' && <span className="text-[10px] uppercase text-red-500">{acc.status}</span>}
                  {already && <span className="text-[10px] uppercase text-emerald-600">attached</span>}
                </label>
              );
            })}
          </div>
        </div>
        <div className="flex justify-between items-center px-5 py-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500">{selected.size} selected</p>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-secondary">Close</button>
            <button type="button" onClick={attach} disabled={!selected.size || saving} className="btn-primary disabled:opacity-40">
              {saving ? 'Attaching…' : `Attach ${selected.size}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
